import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { buildBoorprofielSvg } from '../utils/boorprofiel.js';
import { profileBzb } from '../utils/boorprofiel-data.js';
import { buildBoorprofielenDocFromData } from '../utils/boorprofiel-pdf.js';
import { getCompanyTextures, TEXTURES_EVENT } from '../utils/company-textures.js';
import './boring-photos.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { Edit2, Check, X, Plus, FileText, Loader2, MessageSquare, AlertTriangle, Trash2, RefreshCw, Info, Eye, ChevronLeft, ChevronRight, ChevronDown, Download, TestTube, Droplets, FlaskRound, ScanLine } from 'lucide';
import { lucideIcon } from '../icon.js';
import { CONFIG } from '../config.js';
import { auth } from '../auth.js';
import { classifyGrainSize, grainClassificationName, hasGrainSizeInput,
         extractText, tiCodelists, mediaanApplies, nextJarDepth, ProjectModel, displayName, PEILBUIS_MATERIAAL, PEILBUIS_DOORLOOP, PEILBUIS_REFERENTIEVLAK, WATERMONSTER_OPBRENGST, WATERMONSTER_HELDERHEID, WATERMONSTER_POMPTYPE, WATERMONSTER_POMPTYPE_ALIASES, WATERMONSTER_MATRIX, WATERBOTTLE_FILTERTYPE, WATERBOTTLE_TYPE, convertFromWgs84, convertToWgs84, COORDINATE_SYSTEMS, HEIGHT_DATUMS, DEFAULT_COORDINATE_SYSTEM, DEFAULT_HEIGHT_DATUM, formatHeight, REFERENCE_PLANES, DEFAULT_REFERENCE_PLANE, BORING_TYPE, normalizeBoringType, getBoringTypeLabel, hasPitFields, massFractions, nen6693, SOIL_STANDARDS, SELECTABLE_SOIL_STANDARDS, DEFAULT_SOIL_STANDARD, normalizeSoilStandard, iso14688, MONSTER_VERPAKKING, DEFAULT_MONSTER_VERPAKKING, PLANNED_POINT_KINDS, distanceMeters } from '@mv/models';
import { api } from '../api.js';
import { confirmDialog, alertDialog } from '../utils/confirm-dialog.js';
import './searchable-select.js';
import './camera-venster.js';
import styles from '../styles/recording-row-detail.css?inline';
import { parseTimestamp } from '../utils/datetime.js';
import { preventieVoorGebruiker } from '../utils/fout-uitleg.js';
import { loadSublocations, SUBLOCATIONS_EVENT } from '../utils/sublocations.js';
import { loadPlannedPoints, PLANNED_POINTS_EVENT } from '../utils/planned-points.js';

// Verpakkingsopties met groepskoppen erin, zodat de lange lijst leesbaar blijft.
// De groep is puur visueel: je kiest direct een verpakking, niet eerst een groep.
const VERPAKKING_OPTIONS = (() => {
  const out = [];
  let group = null;
  for (const v of MONSTER_VERPAKKING) {
    if (v.group !== group) { group = v.group; out.push({ header: group }); }
    out.push({ value: v.code, label: v.label });
  }
  return out;
})();

// Domeinen voor een extra opname (backoffice) — spiegelt de field-recorder-dropdown.
const RRD_EXTRA_DOMAINS = [
  { value: 'lagen', label: 'Lagen beschrijven' },
  { value: 'peilbuis', label: 'Peilbuis beschrijven' },
  { value: 'watermonster', label: 'Watermonster beschrijven' },
  { value: 'afwerking', label: 'Afwerking beschrijven' },
];

/**
 * Expanded row detail panel for a single recording.
 * Shows audio player, transcription (with inline editing), and extracted data.
 *
 * Props:
 *   .recording  — the recording object (mutated in place for editing state)
 *   .projectId  — used to build audio file URLs
 *
 * Events dispatched:
 *   transcription-save  { detail: { recording, text } }
 *   transcription-cancel { detail: { recording } }
 */
export class RecordingRowDetail extends LitElement {
  static styles = css`${unsafeCSS(styles)}`;

  static properties = {
    recording:  { type: Object },
    extraRecordings: { type: Array }, // child opnamen of this meting (audio + transcript)
    projectId:  { type: String },
    _sublocations: { state: true },   // deellocaties van het project; het veld staat er alleen als die er zijn
    _dlBezig: { state: true },
    _plannedPoints: { state: true },  // geplande boringen (monsterplan); het veld staat er alleen als die er zijn
    _ppBezig: { state: true },
    coordinateSystem: { type: String }, // project's coordinate system — display coords in it
    heightDatum: { type: String },      // project's height datum (NAP) — for the Z suffix
    standard:   { type: String },       // effectieve classificatiestandaard (nen5104/nen6693)
    // Vult dit project de korrelverdeling in bij een laag? Zie het blok boven Grondsoort.
    grainSizeInput: { type: Boolean },
    // Kortstondige melding bij een percentage dat tegen zijn grens liep.
    _gsizeMelding: { state: true },
    // Korrelverdeling ingeklapt? Blijft staan terwijl je van laag wisselt: wie hem dichtklapt
    // wil hem meestal niet bij de volgende laag weer opengeklapt zien.
    _korrelDicht: { state: true },
    // Uitleg waarom deze velden niet reageren; verschijnt zodra iemand het probeert.
    _korrelUitleg: { state: true },
    templateDefaults: { type: Object },  // sjabloon-defaults van het (deel)project (o.a. velden verbergen)
    borings:    { type: Array },   // flat Boring[] with nested layers — from parent
    boringOnly:   { type: Boolean }, // hide transcription panel, show only result panel
    autoEdit:     { type: Boolean }, // open de eerste boring direct in bewerk-modus (potlood in de lijst)
    initialTab:   { type: Object },  // { boringId, tab } — open die boring meteen op dat tabblad
    _leftCollapsed: { type: Boolean, state: true }, // klap het audio/transcript-paneel in
    _extraMenuOpen: { type: Boolean, state: true }, // domeinkeuze-menu bij 'Extra opname'
    _extraDomain: { type: String, state: true },    // gekozen domein voor de volgende extra opname
    readonly:     { type: Boolean }, // view-only: hide all edit affordances (e.g. validation compare)
    _roLayer:     { state: true },   // uitgeklapte laag in read-only weergave
    crud:         { type: Object },  // optional CRUD overrides (e.g. route edits to the validation DB)
    highlight:    { type: Object },  // optional diff highlight: { borings: { [name]: {...} } } (validation)
    onlyTabs:     { type: Array },   // beperk de zichtbare boring-tabbladen (validatie per model)
    showMeta:     { type: Boolean }, // show the "Kopgegevens" eye-popup — validation only
    _editText: { type: String, state: true },
    _editingId: { type: String, state: true }, // id of the recording (primary/extra) being edited
    _inlineEdit: { type: Object, state: true },  // { boringId, layerId }
    _inlineVal:  { type: Object, state: true },  // working edit values
    _layerSaveState: { type: Object, state: true }, // autosave-status per laag-id ('saving'/'saved'/'error')
    _jarSaveState:   { type: Object, state: true }, // autosave-status per boring-id (grondmonsters)
    _mpEdit:     { type: String, state: true },   // boring ID being edited
    _mpVal:      { type: Object, state: true },   // boring working values
    _mpSaveState: { type: Object, state: true },  // autosave-status van de boring-kopgegevens per boring-id
    _jarEdit:     { type: String, state: true },   // sleutel (_jarSleutel) van het potje in bewerking
    _jarScan:     { type: Boolean, state: true },  // camera open om de barcode van dat potje te scannen
    _jarVal:      { type: Object, state: true },   // jar working values
    _jarBoringId: { type: String, state: true },   // boring ID for the jar being edited
    _invalidShortcode:      { type: Boolean, state: true },
    _invalidColorShortcode: { type: Boolean, state: true },
    _invalidBzbRows:        { type: Object,  state: true }, // Set of invalid BZB row indices
    _metaView:   { type: String, state: true },  // boring guid whose kopgegevens-popup is open
    _afwVal:     { type: Object, state: true },  // { [boringGuid]: afwerking } — werkkopie tijdens bewerken
    _afwDirty:   { type: Object, state: true },  // { [boringGuid]: true } — onopgeslagen afwerking-wijzigingen
    _afwSaveState: { type: Object, state: true }, // autosave-status per boring
    _afwUI:      { type: Object, state: true },  // UI-toggles (bv. waarschuwingen tonen) per boring
    _boringTab:  { type: Object, state: true },  // { [boringGuid]: 'lagen' | 'potjes' | 'peilbuizen' | 'afwerking' }
    _pbFormVals: { type: Object, state: true },  // peilbuis autosave-werkkopie (draft) per pb-id
    _pbSaveState: { type: Object, state: true }, // autosave-status per boring
    _wmFormVals: { type: Object, state: true },   // autosave-werkkopie (draft) per watermonster-id
    _wmSel: { type: Object, state: true },        // geselecteerd watermonster per boring
    _wmSection: { type: Object, state: true },    // actieve sectie per boring
    _wmSaveState: { type: Object, state: true },  // autosave-status per boring ('saving'/'saved'/'error')
    _memberNames:     { type: Array, state: true },  // boormeester dropdown options (org members by name)
    _currentUserName: { type: String, state: true }, // default boormeester for new edits
  };

  constructor() {
    super();
    this.recording = null;
    this.extraRecordings = [];
    this.projectId = '';
    this._sublocations = [];
    this._dlBezig = false;
    this._dlFout = '';
    this.borings   = [];
    this.readonly  = false;
    this._roLayer  = null;
    this.crud      = null;
    this.highlight = null;
    this.showMeta  = false;
    this.autoEdit  = false;
    this._editingId = null;
    this._editText = '';
    this._inlineEdit = null;
    this._inlineVal  = null;
    this._layerSaveState = {};
    this._layerSaveTimers = {}; // niet-reactief: debounce-timer laag-autosave
    this._jarSaveState = {};
    this._jarSaveTimers = {};   // niet-reactief: debounce-timer grondmonster-autosave
    this._mpEdit     = null;
    this._mpVal      = null;
    this._mpSaveState = {};
    this._mpSaveTimers = {}; // niet-reactief: debounce-timer kopgegevens-autosave
    this._jarEdit    = null;
    this._jarScan    = false;
    this._jarVal     = null;
    this._invalidShortcode      = false;
    this._invalidColorShortcode = false;
    this._invalidBzbRows        = new Set();
    this._boringTab  = {};
    this._leftCollapsed = false;
    this._afwVal     = {};
    this._afwDirty   = {};
    this._afwSaveState = {};
    this._afwUI      = {};
    this._afwSaveTimers = {};
    this._pbFormVals = {};
    this._pbSaveState = {};
    this._pbSaveTimers = {};
    this._wmFormVals = {};
    this._wmSel = {};
    this._wmSection = {};
    this._wmSaveState = {};
    this._wmSaveTimers = {}; // niet-reactief: debounce-timers per boring
    this._wmMintedIds = {};  // niet-reactief: verzonnen id per (boring, positie) — zie _wmList
    this._memberNames     = [];
    this._currentUserName = '';
  }

  connectedCallback() {
    super.connectedCallback();
    // Org members feed the boormeester dropdown; the current user is the default
    // for a freshly opened boring without a spoken boormeester. Read-only views
    // (e.g. validation compare) never edit, so they skip the fetch.
    if (!this.readonly && !this._memberNames.length) this._loadMembers();
    // Het boorprofiel opnieuw tekenen zodra de texturen van het bedrijf binnen zijn of wijzigen.
    this._onTextures = () => this.requestUpdate();
    window.addEventListener(TEXTURES_EVENT, this._onTextures);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(TEXTURES_EVENT, this._onTextures);
    window.removeEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
    window.removeEventListener(PLANNED_POINTS_EVENT, this._onPlannedPoints);
  }

  /**
   * Geplande boringen van het project (gedeelde cache). Zonder GeoDesk of zonder monsterplan is
   * de lijst leeg en staat het veld 'Afkomstig van geplande boring' er niet, net als in de veldapp.
   */
  _laadGeplandePunten(changed) {
    if (changed.has('projectId') && this.projectId && !this.readonly) {
      const id = this.projectId;
      loadPlannedPoints(id).then((list) => { if (this.projectId === id) this._plannedPoints = list; });
      if (!this._onPlannedPoints) {
        this._onPlannedPoints = (e) => { if (e.detail?.projectId === this.projectId) this._plannedPoints = e.detail.points || []; };
        window.addEventListener(PLANNED_POINTS_EVENT, this._onPlannedPoints);
      }
    }
  }

  /** Het geplande punt dat deze boring uitvoert: expliciet gekoppeld, of op naam. */
  _plannedPointOf(boringId) {
    const raw = (this.borings || []).find((b) => b.id === boringId);
    const pts = this._plannedPoints || [];
    return pts.find((p) => p.id === raw?.plannedPointId) || pts.find((p) => p.boringId === boringId) || null;
  }

  async _setPlannedPoint(boringId, pointId) {
    this._ppBezig = true;
    this._ppFout = '';
    try {
      await this._crud.updateBoring(boringId, { plannedPointId: pointId || null });
      await loadPlannedPoints(this.projectId, { refresh: true });
      this.dispatchEvent(new CustomEvent('borings-changed', { bubbles: true, composed: true }));
    } catch (e) {
      this._ppFout = e.message || 'Opslaan mislukt.';
    } finally {
      this._ppBezig = false;
    }
  }

  /**
   * 'Afkomstig van geplande boring': welk punt van het monsterplan deze boring uitvoert. Zoals
   * in de veldapp: de punten die nog geen boring hebben plus het huidige, dichtstbijzijnde eerst.
   */
  _renderPlannedPointField(boringId) {
    const pts = this._plannedPoints || [];
    if (!pts.length) return '';
    const raw = (this.borings || []).find((b) => b.id === boringId);
    const huidig = this._plannedPointOf(boringId);
    const lon = Number(raw?.x), lat = Number(raw?.y);
    const heeftPlek = raw?.x != null && raw?.y != null && Number.isFinite(lon) && Number.isFinite(lat);
    const opties = pts
      .filter((p) => !p.boringId || p.boringId === boringId || p.id === huidig?.id)
      .map((p) => ({ p, m: heeftPlek && p.x != null ? distanceMeters(lon, lat, p.x, p.y) : null }))
      .sort((a, z) => (a.m != null && z.m != null ? a.m - z.m : a.p.name.localeCompare(z.p.name, 'nl', { numeric: true })));
    const soort = (k) => PLANNED_POINT_KINDS.find((d) => d.id === k)?.label || 'Boring';
    const afstand = (m) => (m == null ? '' : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1).replace('.', ',')} km`);
    const opNaam = huidig && raw?.plannedPointId !== huidig.id;
    return html`
      <div class="expand-field">
        <label>Afkomstig van geplande boring</label>
        <select ?disabled=${this._ppBezig} @change=${(e) => this._setPlannedPoint(boringId, e.target.value)}>
          <option value="" ?selected=${!huidig}>— geen —</option>
          ${opties.map(({ p, m }) => html`<option value=${p.id} ?selected=${p.id === huidig?.id}>${[p.name, soort(p.kind), afstand(m)].filter(Boolean).join(' · ')}</option>`)}
        </select>
        ${opNaam ? html`<span style="font-size:0.76rem; color:var(--mv-text-muted);">Gekoppeld op naam: de boring heet zoals het geplande punt.</span>` : ''}
        ${this._ppFout ? html`<span style="font-size:0.76rem; color:var(--mv-danger,#c0392b);">${this._ppFout}</span>` : ''}
      </div>`;
  }

  /** Deellocaties van het project: gedeelde cache, dus één ophaalactie voor de hele pagina. */
  _laadDeellocaties(changed) {
    if (changed.has('projectId') && this.projectId && !this.readonly) {
      const id = this.projectId;
      loadSublocations(id).then((list) => { if (this.projectId === id) this._sublocations = list; }).catch(() => {});
      if (!this._onSublocations) {
        this._onSublocations = (e) => { if (e.detail?.projectId === this.projectId) this._sublocations = e.detail.sublocations || []; };
        window.addEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
      }
    }
  }

  /**
   * Een deellocatie van een boring aan- of uitzetten. Een boring kan in meer deellocaties
   * liggen (overlappende vlakken), dus tegels om aan te vinken en geen keuzelijst. Direct
   * opslaan, net als de rest van het formulier (autosave).
   */
  async _toggleBoringSublocation(boringId, sublocationId) {
    const raw = (this.borings || []).find((b) => b.id === boringId);
    const nu = this._dlLokaal?.[boringId] ?? raw?.sublocationIds ?? [];
    const next = nu.includes(sublocationId) ? nu.filter((x) => x !== sublocationId) : [...nu, sublocationId];
    this._dlLokaal = { ...(this._dlLokaal || {}), [boringId]: next };
    this._dlBezig = true;
    this._dlFout = '';
    try {
      await api.setBoringSublocations(boringId, next);
      this.dispatchEvent(new CustomEvent('borings-changed', { bubbles: true, composed: true }));
    } catch (e) {
      this._dlLokaal = { ...this._dlLokaal, [boringId]: nu };
      this._dlFout = e.message || 'Opslaan mislukt.';
    } finally {
      this._dlBezig = false;
    }
  }

  _renderSublocationField(boringId) {
    if (!this._sublocations.length) return '';
    const raw = (this.borings || []).find((b) => b.id === boringId);
    const ids = this._dlLokaal?.[boringId] ?? raw?.sublocationIds ?? [];
    return html`
      <div class="expand-field">
        <label>Deellocatie</label>
        <div style="display:flex; flex-wrap:wrap; gap:6px;">
          ${this._sublocations.map((s) => {
            const on = ids.includes(s.id);
            return html`<button type="button" ?disabled=${this._dlBezig} title=${s.description || s.name}
              @click=${() => this._toggleBoringSublocation(boringId, s.id)}
              style="padding:5px 12px; border-radius:5px; border:1px solid ${on ? '#c2410c' : 'var(--mv-border)'}; background:${on ? '#c2410c' : 'var(--mv-surface)'}; color:${on ? '#fff' : 'var(--mv-text)'}; font-weight:700; font-size:0.82rem; font-family:inherit; cursor:pointer;">
              ${s.name}</button>`;
          })}
          ${ids.length ? '' : html`<span style="align-self:center; font-size:0.78rem; color:var(--mv-text-muted);">Nog geen deellocatie</span>`}
        </div>
        ${this._dlFout ? html`<span style="font-size:0.76rem; color:var(--mv-danger,#c0392b);">${this._dlFout}</span>` : ''}
      </div>`;
  }

  async _loadMembers() {
    try {
      const data = await api.getTeamMembers();
      const members = data?.members || [];
      this._memberNames = members.map(m => displayName(m)).filter(Boolean);
      const me = members.find(m => m.id === data?.myUserId);
      this._currentUserName = me ? displayName(me) : '';
    } catch (_) {
      // No members (e.g. permissions) → dropdown falls back to free-text only.
    }
  }

  /** CRUD layer — defaults to the global API; `crud` lets callers (e.g. the
   *  validation page) route edits to a different backend (the validation DB). */
  get _crud() {
    const c = this.crud || {};

    /*
     * Een mislukte opslag hoort ook een venster op te leveren, niet alleen het chipje.
     *
     * Deze velden slaan automatisch op. Gaat dat mis, dan verschijnt er naast het veld een
     * klein rood "Niet opgeslagen" (zie _saveMark) -- maar de ingetypte waarde BLIJFT staan,
     * want een autosave draait niets terug. Wie dat chipje niet ziet, denkt dus dat het bewaard
     * is. Dat viel op in een meekijksessie, waar élke schrijfactie een 403 geeft: het scherm
     * veranderde, de server deed niets, en pas na een verversing bleek er niets te zijn
     * opgeslagen.
     *
     * Hier en niet bij de elf aanroepplekken: alles wat aan een boring, laag of peilbuis
     * schrijft komt door deze getter. Eén plek dekt ze allemaal, ook wat er later bij komt.
     *
     * De fout gaat daarna gewoon door: het chipje, de terugdraaiing in _optimistic en de
     * bestaande .catch-afhandeling blijven dus werken. Dit komt er alleen bij.
     */
    const report = (err) => {
      // Ontdubbelen: bij het sluiten van een rij kunnen laag, boring én peilbuis tegelijk
      // wegschrijven. Drie vensters over hetzelfde is erger dan geen.
      if (this._saveErrorOpen) return;
      this._saveErrorOpen = true;
      alertDialog({ title: 'Niet opgeslagen', message: err?.message || 'Opslaan is niet gelukt.' })
        .finally(() => { this._saveErrorOpen = false; });
    };
    const guard = (fn) => (...args) => Promise.resolve(fn(...args)).catch((err) => { report(err); throw err; });

    return {
      updateBoring:         guard((id, d) => (c.updateBoring         || api.updateBoring.bind(api))(id, d)),
      createLayer:          guard((id, d) => (c.createLayer          || api.createLayer.bind(api))(id, d)),
      updateLayer:          guard((id, d) => (c.updateLayer          || api.updateLayer.bind(api))(id, d)),
      deleteLayer:          guard((id)    => (c.deleteLayer          || api.deleteLayer.bind(api))(id)),
      createBoringPeilbuis: guard((id, d) => (c.createBoringPeilbuis || api.createBoringPeilbuis.bind(api))(id, d)),
      updatePeilbuis:       guard((id, d) => (c.updatePeilbuis       || api.updatePeilbuis.bind(api))(id, d)),
      deletePeilbuis:       guard((id)    => (c.deletePeilbuis       || api.deletePeilbuis.bind(api))(id)),
    };
  }

  /** Diff highlight for a boring (validation testset), keyed by lowercased name. */
  _hlBoring(mp) {
    const name = String(mp?.name ?? '').trim().toLowerCase();
    return this.highlight?.borings?.[name] || null;
  }

  /** Per-user, per-boring memory of whether the audio/transcript panel is hidden. */
  _readCollapsePrefs() {
    try { return JSON.parse(localStorage.getItem('mv:rrd:audioCollapsed') || '{}'); }
    catch (_) { return {}; }
  }
  _toggleLeftCollapsed() {
    this._leftCollapsed = !this._leftCollapsed;
    const id = this.recording?.id;
    if (!id) return;
    const prefs = this._readCollapsePrefs();
    if (this._leftCollapsed) prefs[id] = true; else delete prefs[id];
    try { localStorage.setItem('mv:rrd:audioCollapsed', JSON.stringify(prefs)); } catch (_) {}
  }

  willUpdate(changedProperties) {
    // Only clear draft state when switching to a different recording entirely.
    // Borings-only refreshes (e.g. after save) must never clear state.
    if (changedProperties.has('recording')) {
      const prevRecording = changedProperties.get('recording');
      if (prevRecording?.id !== this.recording?.id) {
        this._inlineEdit = null;
        this._inlineVal = null;
        this._mpEdit = null;
        this._mpVal = null;
        this._invalidShortcode      = false;
        this._invalidColorShortcode = false;
        this._invalidBzbRows        = new Set();
        // Restore the per-boring audio-panel collapse preference (per user, via
        // localStorage) so re-opening a boring keeps the audio hidden if the user
        // hid it before.
        if (this.recording?.id) this._leftCollapsed = !!this._readCollapsePrefs()[this.recording.id];
      }
    }
    // When borings refresh (e.g. after AI reprocessing replaces boring UUIDs),
    // clear any edit state that references IDs that no longer exist.
    // This prevents 404 errors from stale UUIDs being sent to the backend.
    if (changedProperties.has('borings') && (this._mpEdit || this._inlineEdit)) {
      const boringIds = new Set((this.borings || []).map(b => b.id));
      if (this._mpEdit && !boringIds.has(this._mpEdit)) {
        this._mpEdit = null;
        this._mpVal  = null;
      }
      if (this._inlineEdit && !boringIds.has(this._inlineEdit.boringId)) {
        this._inlineEdit = null;
        this._inlineVal  = null;
      }
    }
    // Potlood in de boringenlijst: open de eerste boring direct in bewerk-modus.
    // Edge-getriggerd (false→true) zodat een gewone rij-klik dit niet opnieuw opent.
    if (changedProperties.has('autoEdit') && this.autoEdit && !this.readonly && !this._mpEdit && (this.borings || []).length) {
      this._startMpEdit({ stopPropagation() {} }, this.borings[0].id);
    }
    // Vanuit de activiteitentegel: spring naar het onderdeel dat is gewijzigd, dus naar de
    // juiste subtab van de juiste boring. Ook edge-getriggerd; daarna bepaalt de gebruiker.
    if (changedProperties.has('initialTab') && this.initialTab?.tab) {
      const { boringId, tab } = this.initialTab;
      if ((this.borings || []).some(b => b.id === boringId)) {
        this._boringTab = { ...this._boringTab, [boringId]: tab };
      }
    }
  }

  updated(changedProperties) {
    this._laadDeellocaties(changedProperties);
    this._laadGeplandePunten(changedProperties);
    // Autosave laag/grondmonster: een *veld*-wijziging verandert alleen de werkkopie
    // (_inlineVal/_jarVal), niet welke rij open staat (_inlineEdit/_jarEdit). Bij openen,
    // wisselen of sluiten wijzigen beide → dan niet plannen (open/close regelt de flush zelf).
    if (changedProperties.has('_inlineVal') && !changedProperties.has('_inlineEdit')
        && this._inlineEdit && this._inlineVal && !this.readonly) {
      this._layerSaveSchedule();
    }
    if (changedProperties.has('_jarVal') && !changedProperties.has('_jarEdit')
        && this._jarEdit && this._jarVal && !this.readonly) {
      this._jarSaveSchedule();
    }
    if (changedProperties.has('_mpVal') && !changedProperties.has('_mpEdit')
        && this._mpEdit && this._mpVal && !this.readonly) {
      this._mpSaveSchedule();
    }
    // Bewerkmodus net geopend → cursor in het naamveld met de tekst geselecteerd, zodat
    // hernoemen één klik is. Pas ná de render, anders bestaat het veld nog niet.
    if (changedProperties.has('_mpEdit') && this._mpEdit && !this.readonly) {
      const input = this.renderRoot?.querySelector('.card-header input[type="text"]');
      if (input) { input.focus(); input.select(); }
    }
  }

  // ─── Audio helpers ──────────────────────────────────────────────────────

  _getFileUrl(filename) {
    const base = `${CONFIG.API_BASE}/projects/${this.projectId}/files/${filename}`;
    const token = auth.getToken() || localStorage.getItem('mv_auth_token');
    return token ? `${base}?token=${token}` : base;
  }

  _getMimeType(filename) {
    const ext = filename?.toLowerCase().split('.').pop();
    return { mp3: 'audio/mpeg', m4a: 'audio/mp4', mp4: 'audio/mp4', wav: 'audio/wav', webm: 'audio/webm', ogg: 'audio/ogg', opus: 'audio/ogg' }[ext] || 'audio/mpeg';
  }

  // ─── Transcription editing ──────────────────────────────────────────────

  _startEdit(e, rec) {
    if (this.readonly) return;
    e?.stopPropagation();
    const target = rec || this.recording;
    this._editingId = target?.id || null;
    this._editText = this._transcript(target);
  }

  _cancelEdit(e) {
    e?.stopPropagation();
    this._editingId = null;
    this._editText = '';
  }

  _save(e, rec) {
    e?.stopPropagation();
    const target = rec || this.recording;
    this.dispatchEvent(new CustomEvent('transcription-save', {
      bubbles: true, composed: true,
      detail: { recording: target, text: this._editText }
    }));
    this._editingId = null;
  }

  // Render one transcript (primary or extra) with its own Bewerken / inline edit.
  // Doel van een opname als leesbare omschrijving (voor de transcript-titel).
  _domainDesc(domain) {
    return ({
      lagen: 'Lagen beschrijving',
      peilbuis: 'Peilbuis beschrijving',
      watermonster: 'Watermonster beschrijving',
      afwerking: 'Afwerking beschrijving',
    })[domain] || 'Lagen beschrijving';
  }

  _renderEditableTranscript(rec, label) {
    if (!rec) return '';
    const editing = this._editingId === rec.id;
    return html`
      <div style="display:flex; justify-content:space-between; align-items:center; margin:${label ? '0.6rem 0 0.2rem' : '0 0 0.2rem'};">
        ${label
          ? html`<span style="font-size:0.7rem; font-weight:700; letter-spacing:0.04em; color:var(--mv-primary);">${label}</span>`
          : html`<span></span>`}
        ${!editing ? html`
          <button class="edit-link" @click=${(e) => this._startEdit(e, rec)}>
            ${unsafeSVG(lucideIcon(Edit2, 13))} Bewerken
          </button>
        ` : ''}
      </div>
      ${editing ? html`
        <textarea
          .value=${this._editText}
          @input=${(e) => this._editText = e.target.value}
          @click=${(e) => e.stopPropagation()}
        ></textarea>
        <div class="edit-actions">
          <button class="save-btn" @click=${(e) => this._save(e, rec)}>
            ${unsafeSVG(lucideIcon(Check, 13))} Opslaan
          </button>
          <button class="cancel-btn" @click=${this._cancelEdit}>
            ${unsafeSVG(lucideIcon(X, 13))} Annuleren
          </button>
        </div>
      ` : this._renderTranscriptieTekst(rec)}
    `;
  }

  /**
   * Re-run AI extraction on the existing transcript (without re-transcribing the
   * audio). Reuses the transcription-save flow with the unchanged text, so the
   * parent shows the loader, warns if borings would be replaced, and the result
   * panel refreshes over the WebSocket.
   */
  _reprocessTranscript(e) {
    e?.stopPropagation();
    if (!this._transcript(this.recording)) return;
    this.dispatchEvent(new CustomEvent('transcription-save', {
      bubbles: true, composed: true,
      detail: { recording: this.recording, text: this._transcript(this.recording) }
    }));
  }

  /**
   * Tegel naast 'Opnieuw verwerken': toont de classificatiestandaard van deze meting en laat de
   * gebruiker 'm per meting wisselen. Een wissel zet de standaard op de opname en verwerkt
   * opnieuw (dat rebuildt de lagen), dus we vragen eerst een bevestiging.
   */
  _renderStandardTile() {
    const cur = normalizeSoilStandard(this.standard);
    const busy = ['transcribing', 'processing', 'syncing'].includes(this.recording?.status);
    return html`
      <label title="Classificatiestandaard van deze meting — wissel + verwerk opnieuw"
        style="display:inline-flex; flex-direction:column; gap:1px; padding:4px 10px; border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface-alt, #f5f6f8); line-height:1.1; cursor:${busy ? 'default' : 'pointer'};">
        <span style="font-size:0.6rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-muted);">Standaard</span>
        <select ?disabled=${busy} @change=${e => this._onStandardSwitch(e)}
          style="border:none; background:transparent; font-size:0.85rem; font-weight:700; color:var(--mv-text); padding:0; margin:0; cursor:${busy ? 'default' : 'pointer'}; outline:none;">
          ${SELECTABLE_SOIL_STANDARDS.map(s => html`<option value=${s.id} ?selected=${s.id === cur}>${s.short}</option>`)}
        </select>
      </label>`;
  }

  _onStandardSwitch(e) {
    const next = e.target.value;
    if (next === normalizeSoilStandard(this.standard)) return;
    // Reset de dropdown naar de huidige waarde; de parent bevestigt + verwerkt en stuurt de
    // nieuwe standaard terug via de props (.standard) na het herladen.
    e.target.value = normalizeSoilStandard(this.standard);
    this.dispatchEvent(new CustomEvent('reprocess-standard', {
      bubbles: true, composed: true,
      detail: { recording: this.recording, standard: next },
    }));
  }

  // Extra opname (backoffice): kies eerst het DOMEIN, upload dan een audiobijlage als child.
  // Het domein bepaalt welk deel van de boring (her)opgebouwd wordt (lagen/peilbuis/
  // watermonster/afwerking) — zo blijven de andere tabbladen ongemoeid.
  _addExtraRecording(e) {
    e?.stopPropagation();
    this._extraMenuOpen = !this._extraMenuOpen;
  }

  _pickExtraDomain(domain, e) {
    e?.stopPropagation();
    this._extraDomain = domain;
    this._extraMenuOpen = false;
    this.shadowRoot?.getElementById('extra-file-input')?.click();
  }

  async _onExtraFileSelected(e) {
    e?.stopPropagation();
    const input = e.target;
    const file = input?.files?.[0];
    if (input) input.value = ''; // allow re-selecting the same file later
    if (!file || !this.recording?.id) return;
    try {
      await api.uploadAdditionalFiles(this.projectId, [file], this.recording.id, this._extraDomain || null);
      const label = RRD_EXTRA_DOMAINS.find(d => d.value === this._extraDomain)?.label || 'Extra opname';
      alertDialog({ title: `${label} toegevoegd`, message: 'De opname wordt verwerkt zodra de transcriptie klaar is.' });
    } catch (err) {
      alertDialog({ title: 'Fout', message: `Uploaden mislukt: ${err.message || err}` });
    }
  }

  // ─── Notify parent to reload borings ────────────────────────────────────

  _notifyChanged() {
    this.dispatchEvent(new CustomEvent('borings-changed', { bubbles: true, composed: true }));
  }

  /** Eén autosave-status voor de boring, afhankelijk van welk tabblad actief is —
   *  zodat de status altijd rechtsboven in de boringkop zichtbaar is (alle tabs). */
  _boringSaveStatus(boringId, activeTab) {
    // Kopgegevens-formulier (potlood) staat boven de tabs; toon dán zijn autosave-status.
    if (this._mpEdit === boringId) return this._mpSaveState?.[boringId];
    switch (activeTab) {
      case 'lagen':        return this._inlineEdit?.boringId === boringId ? this._layerSaveState?.[this._inlineEdit.layerId] : undefined;
      case 'potjes':       return this._jarSaveState?.[boringId];
      case 'peilbuizen':   return this._pbSaveState?.[boringId];
      case 'watermonster': return this._wmSaveState?.[boringId];
      case 'afwerking':    return this._afwSaveState?.[boringId];
      default:             return undefined; // boorprofiel: niets te bewaren
    }
  }

  /** Autosave-statusregel (zelfde stijl als watermonster/afwerking/peilbuis). */
  _autosaveStatus(st) {
    if (st === 'saving') return html`<span style="display:inline-flex; align-items:center; gap:5px; font-size:0.72rem; color:var(--mv-text-muted);"><span style="display:inline-flex; animation:rotate 1s linear infinite;">${unsafeSVG(lucideIcon(Loader2, 12))}</span> Opslaan…</span>`;
    if (st === 'saved') return html`<span style="display:inline-flex; align-items:center; gap:5px; font-size:0.72rem; color:var(--mv-text-muted);">${unsafeSVG(lucideIcon(Check, 12))} Opgeslagen</span>`;
    if (st === 'error') return html`<span style="display:inline-flex; align-items:center; gap:5px; font-size:0.72rem; color:var(--mv-danger,#f1416c);">${unsafeSVG(lucideIcon(AlertTriangle, 12))} Niet opgeslagen</span>`;
    return '';
  }

  // ─── Laag-autosave ───────────────────────────────────────────────────────
  /** Bouw het update-payload uit de huidige werkkopie (standaard-afhankelijk). */
  _layerPayload() {
    return this._is5104() ? this._layerPayload5104(this._inlineVal) : this._layerPayload6693(this._inlineVal);
  }
  _layerSaveSchedule() {
    const layerId = this._inlineEdit?.layerId;
    if (!layerId) return;
    this._layerSaveState = { ...this._layerSaveState, [layerId]: 'saving' };
    clearTimeout(this._layerSaveTimers.t);
    this._layerSaveTimers.t = setTimeout(() => this._layerSaveNow(), 700);
  }
  /** Persisteer de laag zonder de rij te sluiten of de ouder te laten herladen. */
  _layerSaveNow() {
    if (!this._inlineEdit || !this._inlineVal) return;
    const { boringId, layerId } = this._inlineEdit;
    const v = this._inlineVal;
    // Diepte is verplicht; sla niet op zolang die (tijdens typen) leeg is.
    if (v.from === '' || v.from == null || v.to === '' || v.to == null) {
      const { [layerId]: _drop, ...rest } = this._layerSaveState; this._layerSaveState = rest;
      return;
    }
    const payload = this._layerPayload();
    this.borings = (this.borings || []).map(b => b.id !== boringId ? b
      : { ...b, layers: (b.layers || []).map(l => l.id === layerId ? { ...l, ...payload } : l) });
    Promise.resolve(this._crud.updateLayer(layerId, payload))
      .then(() => { this._layerSaveState = { ...this._layerSaveState, [layerId]: 'saved' }; })
      .catch(() => { this._layerSaveState = { ...this._layerSaveState, [layerId]: 'error' }; });
  }
  /** Flush een lopende laag-autosave meteen (bij wisselen/sluiten van een rij).
   *  Tijdens het bewerken wordt niet ge-notify'd (voorkomt ouder-herlaad mid-edit);
   *  bij afronden één keer wel, mits er daadwerkelijk iets gewijzigd is. */
  _layerFlush() {
    const layerId = this._inlineEdit?.layerId;
    if (this._layerSaveTimers.t) {
      clearTimeout(this._layerSaveTimers.t);
      this._layerSaveTimers.t = null;
      this._layerSaveNow();
    }
    if (layerId && this._layerSaveState?.[layerId]) this._notifyChanged();
  }

  // ─── Grondmonster-autosave ───────────────────────────────────────────────
  _jarSaveSchedule() {
    const boringId = this._jarBoringId;
    if (!boringId) return;
    this._jarSaveState = { ...this._jarSaveState, [boringId]: 'saving' };
    clearTimeout(this._jarSaveTimers.t);
    this._jarSaveTimers.t = setTimeout(() => this._jarSaveNow(), 700);
  }
  _jarSaveNow() {
    if (!this._jarEdit || !this._jarVal || !this._jarBoringId) return;
    const boringId = this._jarBoringId;
    const rawBoring = (this.borings || []).find(b => b.id === boringId);
    if (!rawBoring) return;
    // Een potje zonder barcode mag: in de backoffice vul je hem pas in (of scan je hem) als het
    // potje er is. Daarom herkennen we het potje niet aan zijn barcode maar aan _jarSleutel.
    const scanData = this._getScanData(rawBoring);
    const idx = scanData.findIndex(j => this._jarSleutel(j) === this._jarEdit);
    if (idx < 0) return;
    const updated = scanData.map((j, i) => i === idx ? {
      ...j,
      name: this._jarVal.name,
      barcode: (this._jarVal.barcode || '').trim(),
      layerDepthFrom: this._jarVal.from !== '' && this._jarVal.from != null ? Number(this._jarVal.from) : null,
      layerDepthTo: this._jarVal.to !== '' && this._jarVal.to != null ? Number(this._jarVal.to) : null,
      geroerd: this._jarVal.geroerd,
      packingType: this._jarVal.packingType || null,
    } : j);
    this.dispatchEvent(new CustomEvent('update-scan-jar', {
      bubbles: true, composed: true, detail: { boringId, jars: updated },
    }));
    // Volg de nieuwe waarden zodat de volgende autosave hetzelfde potje vindt.
    this._jarEdit = this._jarSleutel(updated[idx]);
    this._jarSaveState = { ...this._jarSaveState, [boringId]: 'saved' };
  }
  _jarFlush() {
    if (this._jarSaveTimers.t) {
      clearTimeout(this._jarSaveTimers.t);
      this._jarSaveTimers.t = null;
      this._jarSaveNow();
    }
  }

  /**
   * Optimistic update helper: applies `applyFn` to `this.borings` immediately,
   * fires `apiFn()` in the background, reverts + alerts on error.
   */
  _optimistic(applyFn, apiFn, errorMsg = 'Opslaan mislukt') {
    const prev = this.borings;
    this.borings = applyFn(this.borings || []);
    apiFn()
      .then(() => this._notifyChanged())
      .catch(err => {
        this.borings = prev;
        alertDialog({ title: 'Fout', message: `${errorMsg}: ${err.message}` });
        this._notifyChanged();
      });
  }

  // ─── Flat boring/layer lookup ────────────────────────────────────────────

  _findFlatBoring(boringId) {
    return (this.borings || []).find(b => b.id === boringId) ?? null;
  }

  _findFlatLayer(boringId, layerId) {
    return this._findFlatBoring(boringId)?.layers?.find(l => l.id === layerId) ?? null;
  }

  // ─── Inline editing helpers ──────────────────────────────────────────────

  /** Reverse-lookup a code in a Map to get the human-readable name. */
  _codeToName(code, map) {
    if (!code) return '';
    for (const [name, id] of map) {
      if (id === code) return name;
    }
    return code;
  }

  // ─── NEN 6693 helpers (laag-formulier) ────────────────────────────────────
  /** Term → NEN 6693-code, over één of meer codelijsten (bv. grondsoort + speciale laagtypen). */
  _nenTermToCode(term, ...maps) {
    if (!term) return null;
    for (const m of maps) { if (m.has(term)) return m.get(term); }
    return null;
  }
  /**
   * NEN 6693-code → term; valt terug op een oude codelijst voor nog-niet-gemigreerde data.
   * (Overlappende codes zoals 'S' = silt (NEN) vs slib (oud) worden pas 100% correct na de
   * data-migratie; de fallback is best-effort tijdens de overgang.)
   */
  _nenName(code, nenMaps, oldMap) {
    if (!code) return '';
    for (const m of nenMaps) { for (const [name, id] of m) if (id === code) return name; }
    return oldMap ? this._codeToName(code, oldMap) : '';
  }

  // ─── Boring editing ──────────────────────────────────────────────────────

  _startMpEdit(e, boringId) {
    if (this.readonly) return;
    e.stopPropagation();
    if (this._mpEdit === boringId) { this._cancelMpEdit(e); return; } // toggle dicht (met flush)
    this._mpFlush(); // ander boring in bewerking? eerst wegschrijven
    const boring = this._findFlatBoring(boringId);
    if (!boring) return;
    this._mpEdit = boringId;
    // Coordinates are stored as WGS84 but shown/edited in the project system (e.g. RD).
    // Keep the original WGS84 + the initial displayed strings so an unchanged save doesn't
    // round-trip (which would drift by ~mm); an edited value converts back to WGS84 on save.
    const sys = COORDINATE_SYSTEMS[this.coordinateSystem] ? this.coordinateSystem : DEFAULT_COORDINATE_SYSTEM;
    const def = COORDINATE_SYSTEMS[sys];
    const conv = (boring.x != null && boring.y != null) ? convertFromWgs84(boring.x, boring.y, sys) : null;
    const coordsX = conv ? conv.x.toFixed(def.decimals) : '';
    const coordsY = conv ? conv.y.toFixed(def.decimals) : '';
    this._mpVal = {
      name:        boring.name || '',
      origName:    boring.name || '',   // voor de potjes-hernoem-cascade bij afsluiten
      boormeester: boring.boormeester || this._currentUserName || '',
      gwsFound:    boring.gwsFound ?? null,
      datum:       boring.datum ?? '',
      gws:         boring.gwsCmMv != null ? String(boring.gwsCmMv) : '',
      // Normaliseren bij het openen: er staan historisch labels ('Boring') en codes ('BO')
      // in dit veld. Zonder dit selecteert de dropdown niets bij een oude boring en zou een
      // simpele save de keuze wissen.
      type:        normalizeBoringType(boring.type) || '',
      trialPitLength: boring.trialPitLengthM != null ? String(boring.trialPitLengthM) : '',
      trialPitWidth:  boring.trialPitWidthM != null ? String(boring.trialPitWidthM) : '',
      massTotal:   boring.massTotalKg != null ? String(boring.massTotalKg) : '',
      massGt20:    boring.massGt20Kg != null ? String(boring.massGt20Kg) : '',
      massLt20:    boring.massLt20Kg != null ? String(boring.massLt20Kg) : '',
      coordsX,
      coordsY,
      coordsXInit: coordsX,
      coordsYInit: coordsY,
      coordsWgsX:  boring.x ?? null,
      coordsWgsY:  boring.y ?? null,
      coordsSys:   sys,
      coordsZ:     boring.z != null ? String(boring.z) : '',
      referencePlane: boring.referencePlane || DEFAULT_REFERENCE_PLANE,
      stopReason:  boring.stopReason || '',
      groundLevelType: boring.groundLevelType || '',
      opmerking:   boring.opmerking || '',
    };
  }

  _cancelMpEdit(e) {
    e?.stopPropagation();
    this._mpFlush();
    this._mpEdit = null;
    this._mpVal  = null;
  }

  /** Enter-toets: lopende autosave flushen en het kopgegevens-formulier sluiten. */
  _saveMpEdit(e) {
    e?.stopPropagation();
    if (!this._mpEdit || !this._mpVal) return;
    this._mpFlush();
    this._mpEdit = null;
    this._mpVal  = null;
  }

  /** Bouw het boring-update-payload uit de werkkopie (coördinaten → WGS84 voor opslag). */
  /**
   * Sleufmaten en zeefmassa's — bij een inspectiegat met boring en een graafgat (hasPitFields).
   *
   * Bij dit type wordt een sleuf gegraven en gaat de uitkomende grond door een zeef van
   * 20 mm; wat er doorheen valt en wat erop blijft liggen worden apart gewogen. Die twee
   * getallen zijn de meting. Het totaal wordt vaak ook los gewogen en wijkt dan iets af van
   * de som, dus het veld staat er wel maar is niet verplicht: leeg = de som, en wie een eigen
   * weging invult overschrijft die. De percentages worden getoond en niet opgeslagen.
   */
  _renderInspectiegatFields(v) {
    const f = massFractions({
      massTotalKg: this._numOrNull(v.massTotal),
      massGt20Kg: this._numOrNull(v.massGt20),
      massLt20Kg: this._numOrNull(v.massLt20),
    });
    const pct = (n) => (n == null ? '—' : `${n.toLocaleString('nl-NL')}%`);
    const kg = (n) => (n == null ? '—' : `${n.toLocaleString('nl-NL')} kg`);
    return html`
      <div class="expand-field-group" style="grid-template-columns:1fr 1fr;">
        <div class="expand-field">
          <label>Sleuflengte (m)</label>
          <input type="number" step="0.01" min="0" .value=${v.trialPitLength}
            @input=${e => this._setMV('trialPitLength', e.target.value)}>
        </div>
        <div class="expand-field">
          <label>Sleufbreedte (m)</label>
          <input type="number" step="0.01" min="0" .value=${v.trialPitWidth}
            @input=${e => this._setMV('trialPitWidth', e.target.value)}>
        </div>
      </div>
      <div class="expand-field-group" style="grid-template-columns:1fr 1fr;">
        <div class="expand-field">
          <label>Massa fractie &gt; 20 mm (kg)</label>
          <input type="number" step="0.01" min="0" .value=${v.massGt20}
            @input=${e => this._setMV('massGt20', e.target.value)}>
        </div>
        <div class="expand-field">
          <label>Massa fractie &lt; 20 mm (kg)</label>
          <input type="number" step="0.01" min="0" .value=${v.massLt20}
            @input=${e => this._setMV('massLt20', e.target.value)}>
        </div>
      </div>
      <div class="expand-field">
        <label>Totale massa (kg)</label>
        <input type="number" step="0.01" min="0"
          .value=${v.massTotal}
          placeholder=${f.computedTotalKg != null ? String(f.computedTotalKg) : ''}
          @input=${e => this._setMV('massTotal', e.target.value)}>
        <div style="margin-top:4px; font-size:0.78rem; color:var(--mv-text-muted);">
          ${f.effectiveTotalKg == null
            ? html`Vul de twee fracties in; het totaal wordt dan opgeteld.`
            : html`
              Totaal ${kg(f.effectiveTotalKg)} —
              &gt; 20 mm ${pct(f.gt20Pct)}, &lt; 20 mm ${pct(f.lt20Pct)}.
              ${f.overridden
                ? html`<br>Ingevuld totaal wijkt ${kg(Math.abs(f.differenceKg))} af van de som (${kg(f.computedTotalKg)}).`
                : ''}
            `}
        </div>
      </div>
    `;
  }

  /** '' → null, '12,5' → 12.5. Een leeg veld is geen nul: nul kilo is een meting. */
  _numOrNull(v) {
    if (v === null || v === undefined || String(v).trim() === '') return null;
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  _mpPayload(v) {
    const numOrNull = (x) => this._numOrNull(x);
    // Bewerkte coords (projectsysteem) terug naar WGS84; ongewijzigd → oorspronkelijke
    // WGS84 hergebruiken zodat er geen round-trip-drift (~mm) ontstaat.
    let wgsX = null, wgsY = null;
    if (v.coordsX !== '' && v.coordsY !== '') {
      if (v.coordsX === v.coordsXInit && v.coordsY === v.coordsYInit) {
        wgsX = v.coordsWgsX; wgsY = v.coordsWgsY;
      } else {
        const back = convertToWgs84(Number(v.coordsX), Number(v.coordsY), v.coordsSys || DEFAULT_COORDINATE_SYSTEM);
        if (back) { wgsX = back.longitude; wgsY = back.latitude; }
      }
    }
    return {
      name:        v.name,
      boormeester: v.boormeester || null,
      gwsFound:    v.gwsFound ?? null,
      datum:       v.datum || null,
      gwsCmMv:     (v.gwsFound === true && v.gws !== '') ? Number(v.gws) : null,
      type:        v.type || null,
      // Sleufmaten horen bij een inspectiegat. Wisselt iemand het type terug naar 'boring',
      // dan gaan ze mee weg -- anders staat er een sleufmaat op iets dat geen sleuf is en
      // belandt die alsnog in de TerraIndex-export.
      trialPitLengthM: hasPitFields(v.type) ? numOrNull(v.trialPitLength) : null,
      trialPitWidthM:  hasPitFields(v.type) ? numOrNull(v.trialPitWidth) : null,
      massTotalKg: hasPitFields(v.type) ? numOrNull(v.massTotal) : null,
      massGt20Kg:  hasPitFields(v.type) ? numOrNull(v.massGt20) : null,
      massLt20Kg:  hasPitFields(v.type) ? numOrNull(v.massLt20) : null,
      x:           wgsX,
      y:           wgsY,
      z:           v.coordsZ !== '' ? Number(v.coordsZ) : null,
      referencePlane: v.referencePlane || null,
      stopReason:  v.stopReason || null,
      groundLevelType: v.groundLevelType || null,
      opmerking:   v.opmerking || null,
    };
  }

  _mpSaveSchedule() {
    const boringId = this._mpEdit;
    if (!boringId) return;
    this._mpSaveState = { ...this._mpSaveState, [boringId]: 'saving' };
    clearTimeout(this._mpSaveTimers.t);
    this._mpSaveTimers.t = setTimeout(() => this._mpSaveNow(), 700);
  }
  /** Persisteer de kopgegevens zonder te sluiten/ouder te herladen. Géén potjes-cascade
   *  tijdens het typen (die gebeurt eenmalig bij afsluiten in _mpFlush). */
  _mpSaveNow() {
    if (!this._mpEdit || !this._mpVal) return;
    const boringId = this._mpEdit;
    const payload = this._mpPayload(this._mpVal);
    this.borings = (this.borings || []).map(b => b.id === boringId ? { ...b, ...payload } : b);
    this._mpSaveState = { ...this._mpSaveState, [boringId]: 'saving' };
    Promise.resolve(this._crud.updateBoring(boringId, payload))
      .then(() => { this._mpSaveState = { ...this._mpSaveState, [boringId]: 'saved' }; })
      .catch(() => { this._mpSaveState = { ...this._mpSaveState, [boringId]: 'error' }; });
  }
  /** Flush bij wisselen/sluiten: laatste save wegschrijven, potjes-hernoem-cascade eenmalig
   *  toepassen (automatisch benoemde potjes "<oud>-<n>" → "<nieuw>-<n>") en één ouder-refresh. */
  _mpFlush() {
    const boringId = this._mpEdit;
    if (this._mpSaveTimers.t) { clearTimeout(this._mpSaveTimers.t); this._mpSaveTimers.t = null; this._mpSaveNow(); }
    if (boringId && this._mpVal) {
      const oldName = this._mpVal.origName ?? '';
      const newName = this._mpVal.name ?? '';
      if (oldName && newName !== oldName) {
        const autoRe = new RegExp(`^${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}-(\\d+)$`);
        this.borings = (this.borings || []).map(b => b.id !== boringId ? b : {
          ...b, monsters: (b.monsters || []).map(m => { const mm = (m.name || '').match(autoRe); return mm ? { ...m, name: `${newName}-${mm[1]}` } : m; }),
        });
      }
    }
    if (boringId && this._mpSaveState?.[boringId]) this._notifyChanged();
  }

  _setMV(key, value) {
    this._mpVal = { ...this._mpVal, [key]: value };
  }

  /** Boormeester options = org members; keep a spoken/custom value visible even
   *  when it isn't an org member (e.g. AI extracted "Donald Trump"). */
  _boormeesterOptions(current) {
    const opts = [...this._memberNames];
    const c = (current || '').trim();
    if (c && !opts.includes(c)) opts.unshift(c);
    return opts;
  }

  /** Open accordion for a layer — loads fields from flat layer data. */
  _startInlineEdit(e, boringId, layerId) {
    if (this._is5104()) return this._startInlineEdit5104(e, boringId, layerId);
    // Read-only (validatie, kolom 'Verwacht'): uitklappen mag, bewerken niet. Dichtgeklapt is
    // niet te zien of 'lucht' een bijzonder bestanddeel is of een opmerking -- die staan in de
    // samenvatting in dezelfde kolom. Zie _renderLayerReadOnly.
    if (this.readonly) {
      e.stopPropagation();
      this._roLayer = this._roLayer === layerId ? null : layerId;
      return;
    }
    e.stopPropagation();
    // Toggle: clicking active row closes it
    if (this._inlineEdit?.layerId === layerId) {
      this._cancelInlineEdit(e);
      return;
    }
    this._layerFlush(); // wisselen van rij: eerst de vorige autosave wegschrijven
    const l = this._findFlatLayer(boringId, layerId);
    if (!l) return;

    const bzbItems = l.bzb?.length
      ? l.bzb.map(b => ({
          type:     this._nenName(b.code, [this._N.BZB_SOORT, this._N.GESTEENTE], tiCodelists.LaSpecialCompounds) || b.type || '',
          // Bij 'wortels' bevat gradation de dichtheid-term (niet in BZB_VOLUME) → fallback op de ruwe waarde.
          gradatie: this._nenName(b.gradation, [this._N.BZB_VOLUME], tiCodelists.LaSpecialCompoundsGradiation) || b.gradation || '',
          herkomst: this._nenName(b.origin, [this._N.BZB_HERKOMST], tiCodelists.LaSpecialCompoundsOrigin) || '',
          grootte:  b.size || '', // wortels
          asfaltType: b.asphaltType || '', // asfalt
          ballastFractie: b.ballastFraction || '', // ballast
        }))
      : [{ type: '', gradatie: '', herkomst: '', grootte: '', asfaltType: '', ballastFractie: '' }];

    // Een rotslaag slaat het gesteentetype (Bijlage C.2) op in soilType; herken dat en toon
    // hoofdbestanddeel 'rots' + het gesteente in een tweede dropdown.
    const rockTerm = this._nenName(l.soilType, [this._N.GESTEENTE]);

    this._inlineEdit = { boringId, layerId };
    this._inlineVal  = {
      from:     String(l.upperDepth ?? ''),
      to:       String(l.lowerDepth ?? ''),
      drillingSystem: l.drillingSystem || '',
      gs_hoofd: rockTerm ? 'rots' : this._nenName(l.soilType, [this._N.GRONDSOORT, this._N.LAAGTYPE_SPECIAAL], tiCodelists.bodemsoortHistorisch),
      gs_gesteente: rockTerm || '',
      gs_mineraalarm: !!l.mineraalarm,
      gs_consistentie: l.consistentie || '',
      gs_toev1: this._nenName(l.toevoeging1, [this._N.BIJMENGING], tiCodelists.toevoeging),
      gs_grad1: this._nenName(l.gradatie1, [this._N.INTENSITEIT], tiCodelists.gradatie),
      gs_toev2: this._nenName(l.toevoeging2, [this._N.BIJMENGING], tiCodelists.toevoeging),
      gs_grad2: this._nenName(l.gradatie2, [this._N.INTENSITEIT], tiCodelists.gradatie),
      gs_toev3: this._nenName(l.toevoeging3, [this._N.BIJMENGING], tiCodelists.toevoeging),
      gs_grad3: this._nenName(l.gradatie3, [this._N.INTENSITEIT], tiCodelists.gradatie),
      // How many extra admixture rows (bijmenging 2/3) are revealed; derived
      // from filled values, extended via the "+ Bijmenging toevoegen" button.
      extraAdmix: (l.toevoeging3 || l.gradatie3) ? 2 : (l.toevoeging2 || l.gradatie2) ? 1 : 0,
      // Normaliseren: 'fijn'/'middelgrof' bij zand zijn vervallen keuzes. Zonder dit staat de
      // keuzelijst bij een oude laag op leeg, en zou opslaan de mediaan stilletjes wissen.
      mediaan:  this._N.normalizeMediaan(l.soilType, this._nenName(l.mediaan, [this._N.MEDIAAN_KLASSE], tiCodelists.mediaan)) || '',
      kleurMain:  this._nenName(l.kleurMain, [this._N.KLEUR], tiCodelists.kleur),
      kleurSub:   this._nenName(l.kleurSub, [this._N.KLEUR], tiCodelists.kleur),
      kleurShade: this._nenName(l.kleurShade, [this._N.KLEUR_TINT], tiCodelists.kleurSterkte),
      oilWater: this._codeToName(l.oilWaterReaction, tiCodelists.olieWaterReactie),
      oilWaterObservation: l.oilWaterObservation || '',
      geur: this._codeToName(l.geurType, tiCodelists.geurType),
      geurIntensity: this._codeToName(l.geurIntensity, tiCodelists.geurSterkte) || 'geen',
      bzbItems,
      gsize: { ...(l.grainSize || {}) },
      opmerking: l.opmerking || '',
      organic: l.organic || '',
      grainAngularity: l.grainAngularity || '',
      grainShape: l.grainShape || '',
      peatTexture: l.peatTexture || '',
    };
  }

  _cancelInlineEdit(e) {
    e?.stopPropagation();
    this._layerFlush(); // eventuele lopende autosave nog wegschrijven
    this._inlineEdit = null;
    this._inlineVal  = null;
  }

  _fixDepthConflict(e, conflict) {
    e.stopPropagation();
    if (!conflict.b.id) return;
    this._optimistic(
      bs => bs.map(b => ({ ...b, layers: (b.layers || []).map(l => l.id === conflict.b.id ? { ...l, upperDepth: conflict.a.lowerDepth } : l) })),
      () => this._crud.updateLayer(conflict.b.id, { upperDepth: conflict.a.lowerDepth }),
      'Herstellen mislukt',
    );
  }

  /** Bouw het NEN 6693-update-payload uit een laag-werkkopie. */
  _layerPayload6693(v) {
    // Rotslaag: sla de gesteente-NEN-code op in soilType (terugval 'ROTS' als er nog geen
    // gesteente gekozen is). Anders de normale grondsoort-/laagtype-code.
    const soilCode = v.gs_hoofd === 'rots'
      ? (this._N.GESTEENTE.get(v.gs_gesteente) || 'ROTS')
      : this._nenTermToCode(v.gs_hoofd, this._N.GRONDSOORT, this._N.LAAGTYPE_SPECIAAL);
    return {
      upperDepth:  Number(v.from),
      lowerDepth:  Number(v.to),
      drillingSystem: v.drillingSystem || null,
      soilType:    soilCode,
      toevoeging1: this._nenTermToCode(v.gs_toev1, this._N.BIJMENGING),
      gradatie1:   this._nenTermToCode(v.gs_grad1, this._N.INTENSITEIT),
      toevoeging2: this._nenTermToCode(v.gs_toev2, this._N.BIJMENGING),
      gradatie2:   this._nenTermToCode(v.gs_grad2, this._N.INTENSITEIT),
      toevoeging3: this._nenTermToCode(v.gs_toev3, this._N.BIJMENGING),
      gradatie3:   this._nenTermToCode(v.gs_grad3, this._N.INTENSITEIT),
      mediaan:     this._N.MEDIAAN_GRONDSOORTEN.has(soilCode) ? this._nenTermToCode(v.mediaan, this._N.MEDIAAN_KLASSE) : null,
      grainSize:   this._gsizePayload(v),
      mineraalarm: this._N.MINERAALARM_GRONDSOORTEN.has(soilCode) ? !!v.gs_mineraalarm : null,
      consistentie: this._N.CONSISTENTIE_GRONDSOORTEN.has(soilCode) ? (v.gs_consistentie || null) : null,
      kleurMain:   this._nenTermToCode(v.kleurMain, this._N.KLEUR),
      // Geen subkleur/tint zonder hoofdkleur.
      kleurSub:    v.kleurMain ? this._nenTermToCode(v.kleurSub, this._N.KLEUR) : null,
      kleurShade:  v.kleurMain ? this._nenTermToCode(v.kleurShade, this._N.KLEUR_TINT) : null,
      oilWaterReaction: tiCodelists.olieWaterReactie.get(v.oilWater) || null,
      // Andere waarneming olie-watertest alleen als er een reactie gekozen is.
      oilWaterObservation: (v.oilWater && v.oilWater !== 'Geen') ? (v.oilWaterObservation || null) : null,
      // Geur: opslaan als TI-codes; gradatie alleen bij een gekozen geur.
      geurType: tiCodelists.geurType.get(v.geur) || null,
      geurIntensity: v.geur ? (tiCodelists.geurSterkte.get(v.geurIntensity) ?? null) : null,
      bzb: (v.bzbItems || []).filter(b => b.type).map(b => {
        const isWortels = b.type === this._N.BZB_WORTELS;
        const isAsfalt = b.type === this._N.BZB_ASFALT;
        const isBallast = b.type === this._N.BZB_BALLAST;
        return {
          code:      this._nenTermToCode(b.type, this._N.BZB_SOORT, this._N.GESTEENTE),
          type:      b.type,
          // Wortels: dichtheid-term (geen BZB_VOLUME-code), altijd gebiedseigen, + grootte.
          gradation: isWortels ? (b.gradatie || null) : this._nenTermToCode(b.gradatie, this._N.BZB_VOLUME),
          // Asfalt en ballast zijn altijd antropogeen; wortels altijd gebiedseigen.
          origin:    isWortels ? 'gebiedseigen' : (isAsfalt || isBallast) ? 'antropogeen' : this._nenTermToCode(b.herkomst, this._N.BZB_HERKOMST),
          size:      isWortels ? (b.grootte || null) : null,
          asphaltType: isAsfalt ? (b.asfaltType || null) : null,
          ballastFraction: isBallast ? (b.ballastFractie || null) : null,
        };
      }),
      opmerking: v.opmerking || null,
      // NEN-EN-ISO 14688-kenmerken. Alleen bij ISO meesturen, zodat een NEN 6693-laag ze niet wist.
      ...(this._isIso() ? {
        organic: iso14688.acceptsOrganic(soilCode) ? (v.organic || null) : null,
        grainAngularity: iso14688.KORRELVORM_GRONDSOORTEN.has(soilCode) ? (v.grainAngularity || null) : null,
        grainShape: iso14688.KORRELVORM_GRONDSOORTEN.has(soilCode) ? (v.grainShape || null) : null,
        peatTexture: iso14688.ORGANISCHE_GRONDSOORTEN.has(soilCode) ? (v.peatTexture || null) : null,
      } : {}),
    };
  }

  /** Enter-toets: lopende autosave flushen en de rij sluiten. */
  _saveInlineEdit(e) {
    e?.stopPropagation();
    if (!this._inlineEdit || !this._inlineVal) return;
    this._layerFlush();
    this._inlineEdit = null;
    this._inlineVal  = null;
  }

  async _removeLayer(e) {
    e?.stopPropagation();
    if (!this._inlineEdit) return;
    const confirmed = await confirmDialog({
      title: 'Laag verwijderen',
      message: 'Weet je zeker dat je deze laag wilt verwijderen?',
      confirmLabel: 'Verwijderen',
      danger: true,
    });
    if (!confirmed) return;
    clearTimeout(this._layerSaveTimers.t); // geen autosave meer na verwijderen
    this._layerSaveTimers.t = null;
    const { boringId, layerId } = this._inlineEdit;
    this._inlineEdit = null;
    this._inlineVal  = null;
    this._optimistic(
      bs => bs.map(b => b.id !== boringId ? b : { ...b, layers: (b.layers || []).filter(l => l.id !== layerId) }),
      () => this._crud.deleteLayer(layerId),
      'Verwijderen mislukt',
    );
  }

  /** Update a key on _inlineVal immutably to trigger re-render. */
  _setIV(key, value) {
    this._inlineVal = { ...this._inlineVal, [key]: value };
  }

  // ─── Korrelverdeling ──────────────────────────────────────────────────────

  /** De zes velden van het korrelverdelingsblok, in de volgorde waarin ze in beeld staan. */
  static GSIZE_VELDEN = [
    { key: 'lutumPct',   label: 'Lutum',     eenheid: '%',  kleur: '#8C5A3C' },
    { key: 'siltPct',    label: 'Silt',      eenheid: '%',  kleur: '#C4A06A' },
    // Leem staat achter zijn twee delen, want hij is er de som van: vul je die allebei in, dan
    // rekent dit veld zichzelf uit. Andersom mag ook -- leem plus één van de twee is genoeg.
    { key: 'leemPct',    label: 'Leem',      eenheid: '%',  kleur: '#A97B52', hint: 'lutum + silt' },
    { key: 'grindPct',   label: 'Grind',     eenheid: '%',  kleur: '#6B7A8F' },
    { key: 'organicPct', label: 'Organisch', eenheid: '%',  kleur: '#3F3A34' },
    { key: 'm50Um',      label: 'M50',       eenheid: 'µm', kleur: '#9AA6B2', hint: 'mediaan zandfractie' },
  ];

  /**
   * Leem als lutum + silt allebei bekend zijn, anders null.
   *
   * Leem ís per definitie die som, dus dan valt er niets te kiezen en hoort het veld te tonen
   * wat eruit komt in plaats van een leeg vakje aan te bieden waar je iets tegenstrijdigs in
   * kunt zetten.
   */
  _gsizeLeem(g) {
    const n = (k) => (g?.[k] === '' || g?.[k] == null ? null : Number(g[k]));
    const lutum = n('lutumPct');
    const silt = n('siltPct');
    return lutum != null && silt != null ? lutum + silt : null;
  }

  /** De korrelverdeling zoals hij meegaat naar de backend; leeg blok = null. */
  _gsizePayload(v) {
    const g = v?.gsize || {};
    const afgeleideLeem = this._gsizeLeem(g) != null;
    const uit = {};
    for (const { key } of RecordingRowDetail.GSIZE_VELDEN) {
      // Leem weglaten zodra lutum en silt er zijn: hij is dan hun som en een eigen waarde
      // ernaast kan er alleen maar mee in tegenspraak raken.
      if (key === 'leemPct' && afgeleideLeem) continue;
      const n = Number(g[key]);
      if (g[key] !== '' && g[key] != null && Number.isFinite(n)) uit[key] = n;
    }
    return hasGrainSizeInput(uit) ? uit : null;
  }

  /**
   * Wat de korrelverdeling van deze laag oplevert, of null als er niets is ingevuld.
   *
   * Null betekent: de gebruiker kiest zelf, en de keuzelijsten doen wat ze altijd deden.
   * Niet-null betekent: deze velden zijn afgeleid en horen niet met de hand bewerkt te kunnen
   * worden -- dat is de hele regel.
   */
  _gsizeKlasse(v) {
    if (!this.grainSizeInput) return null;
    const invoer = this._gsizePayload(v);
    // ISO 14688 classificeert (nog) niet uit de korrelverdeling: de rekenregels zijn die van
    // NEN 6693, met o.a. humeus als bijmenging, en dat kent ISO niet. Dan kiest de gebruiker zelf.
    if (this._isIso()) return null;
    return invoer ? classifyGrainSize(invoer, this._is5104() ? 'nen5104' : 'nen6693') : null;
  }

  /**
   * Een percentage bijwerken, begrensd op wat er nog over is.
   *
   * De vier minerale fracties tellen samen op tot hoogstens 100; organische stof telt daar niet
   * in mee (dat is een percentage van de totale droge massa, zie grainSize.ts). Zonder grens
   * kun je 80 % lutum en 80 % silt invullen en komt er niets uit -- een lege uitkomst zonder
   * uitleg is verwarrender dan een waarde die blijft steken op wat er past.
   */
  _setGsize(key, waarde) {
    const g = { ...(this._inlineVal?.gsize || {}) };
    const tekst = String(waarde ?? '').replace(',', '.');
    if (tekst === '') { delete g[key]; this._setIV('gsize', g); this._gsizeMelding = null; return; }
    let n = Number(tekst);
    if (!Number.isFinite(n)) return;
    let melding = null;
    if (n < 0) { n = 0; melding = { key, tekst: 'Minimaal 0' }; }
    if (key !== 'm50Um') {
      const max = this._gsizeMax(key, g);
      if (n > max) { n = max; melding = { key, tekst: max >= 100 ? 'Maximaal 100%' : `Nog ${max}% beschikbaar` }; }
    } else if (n > 63000) { n = 63000; melding = { key, tekst: 'Maximaal 63000 µm' }; }
    g[key] = n;
    this._setIV('gsize', g);
    this._gsizeMelding = melding;
    clearTimeout(this._gsizeMeldingTimer);
    if (melding) this._gsizeMeldingTimer = setTimeout(() => { this._gsizeMelding = null; }, 2600);
  }

  /** Hoeveel procent er nog te verdelen is voor dit veld, gegeven de rest. */
  _gsizeMax(key, g) {
    const n = (k) => (g[k] === '' || g[k] == null ? null : Number(g[k]));
    const lutum = n('lutumPct') ?? 0;
    const silt = n('siltPct');
    const leem = n('leemPct');
    const grind = n('grindPct') ?? 0;
    const fijn = silt != null ? lutum + silt : (leem != null ? Math.max(lutum, leem) : lutum);
    const ruimte = {
      lutumPct: silt != null ? 100 - silt - grind : 100 - grind,
      siltPct: 100 - lutum - grind,
      leemPct: 100 - grind,
      grindPct: 100 - fijn,
      organicPct: 100,
    }[key];
    return Math.max(0, Math.min(100, ruimte ?? 100));
  }

  /**
   * De werkkopie zoals hij in beeld hoort te staan als de korrelverdeling leidt.
   *
   * De backend rekent de afgeleide velden opnieuw uit bij het opslaan, maar het formulier moet
   * ze NU al tonen -- anders staat er een leeg vakje met 'auto' erbij en lijkt het alsof de
   * korrelverdeling niets doet. De termen komen rechtstreeks uit de classificatie en passen op
   * de keuzelijsten van beide normen (die delen hun woordenschat).
   */
  _gsizeView(iv, klasse) {
    if (!klasse) return iv;
    const bij = klasse.admixtures || [];
    return {
      ...iv,
      gs_hoofd: klasse.soil || '',
      gs_gesteente: '',
      gs_toev1: bij[0]?.admixture || '', gs_grad1: bij[0]?.intensity || '',
      gs_toev2: bij[1]?.admixture || '', gs_grad2: bij[1]?.intensity || '',
      gs_toev3: bij[2]?.admixture || '', gs_grad3: bij[2]?.intensity || '',
      // Zoveel rijen als er bijmengingen zijn; een lege rij eronder zou uitnodigen tot iets
      // wat hier niet kan.
      extraAdmix: Math.max(0, bij.length - 1),
      mediaan: klasse.median || '',
      gs_mineraalarm: !!klasse.mineraalarm,
    };
  }

  /**
   * Diepte en boorsysteem: de eerste regel van het laagformulier.
   *
   * 'Van' en 'Tot' hadden allebei hun eigen kopje, maar het zijn geen twee gegevens -- het is
   * één diepte met een begin en een eind. Eén kopje 'Diepte (cm)' boven de twee vakjes zegt
   * dat, en zegt meteen in welke eenheid; 'Van 0 Tot 20' liet de eenheid nergens zien.
   */
  _renderDiepteRij(iv) {
    return html`
      <div>
        <div style="display:flex; align-items:center; gap:12px;">
          <div class="expand-field" style="flex:none;">
            <label>Diepte (cm)</label>
            <div style="display:flex; align-items:center; gap:8px;">
              <input type="number" style="width:80px;" .value=${iv.from}
                @input=${e => this._setIV('from', e.target.value)}>
              <span style="color:var(--mv-text-muted);">&ndash;</span>
              <input type="number" style="width:80px;" .value=${iv.to}
                @input=${e => this._setIV('to', e.target.value)}>
            </div>
          </div>
          <div class="expand-field" style="flex:1; min-width:140px;">
            <label>Boorsysteem</label>
            <searchable-select
              placeholder="Kies of zoek…"
              .options=${nen6693.BOORSYSTEEM.map(b => ({ value: b.code, label: b.label }))}
              .value=${iv.drillingSystem || ''}
              @change=${e => this._setIV('drillingSystem', e.detail.value)}>
            </searchable-select>
          </div>
        </div>
      </div>`;
  }

  /**
   * Het codeveld ('Zs1g3') met zijn kopje, rechts van de titel van het grondsoortblok.
   *
   * Staat de korrelverdeling aan, dan heeft dat blok een eigen kader met een eigen kop en hoort
   * het codeveld daarbinnen. Staat hij uit, dan is er geen kader en zou die kop de sectiekop
   * erboven dubbelen; dan staat het codeveld rechts in de sectiekop zelf.
   *
   * Het veld blijft leeg als er geen code is. Een voorbeeldcode als placeholder stond er vet
   * en gecentreerd in, net als een echte waarde, en las daardoor alsof de laag al 'Zs1g3' was.
   * Het voorbeeld zit nu in de tooltip.
   *
   * @param {object} iv          de laag in bewerking
   * @param {string} voorbeeld   voorbeeldcode voor de tooltip; de twee normen schrijven hem anders
   * @param {number} breedte     breedte van het invoerveld in pixels
   * @param {boolean} hoofdletters  NEN 5104 schrijft de code in hoofdletters; 6693 niet
   *   ('Zs1g3'), en dat is de reden dat .bzb-code's hoofdletters daar uit moeten
   */
  _grondsoortCode(iv, voorbeeld, breedte, hoofdletters) {
    const vast = !!this._gsizeKlasse(iv);
    const fout = this._invalidShortcode;
    return html`
      <div style="display:flex; align-items:center; gap:6px;">
        <span style="font-size:0.65rem; font-weight:700; color:var(--mv-text-muted); text-transform:uppercase;">Code</span>
        <input type="text" class="bzb-code" title="Grondsoortcode, bijvoorbeeld ${voorbeeld}" ?disabled=${vast}
          .value=${this._getShortcode(this._gsizeView(iv, this._gsizeKlasse(iv)))}
          @input=${e => this._applyShortcode(e.target.value)}
          style="width:${breedte}px; border-color:${fout ? '#DC2626' : 'var(--mv-border)'}; color:${fout ? '#DC2626' : 'var(--mv-primary)'};${hoofdletters ? '' : ' text-transform:none;'}">
      </div>`;
  }

  /**
   * De kop van een onderdeel in het laagformulier: naam, en rechts wat erbij hoort.
   *
   * Eén vorm voor Grondsoort, Kleur, Bijzondere bestanddelen en Waarnemingen, met de
   * scheidingslijn erin (zie .sectie-kop). De samenvatting is er wel, maar wordt nergens
   * gebruikt: wat eronder in de velden staat herhalen leest dubbel.
   */
  _sectieKop(titel, samenvatting = '', rechts = '') {
    return html`
      <div class="sectie-kop">
        <strong>${titel}</strong>
        ${samenvatting ? html`<span>${samenvatting}</span>` : ''}
        ${rechts ? html`<div class="sectie-kop-rechts">${rechts}</div>` : ''}
      </div>`;
  }

  /**
   * Een rijtje keuzeknoppen in plaats van een keuzelijst.
   *
   * Voor korte, geordende schalen als geen/zwak/matig/sterk/uiterst: je ziet de hele schaal in
   * één oogopslag én waar je keuze daarin ligt, en kiezen kost één klik in plaats van twee.
   * Bij lange lijsten (geurtype, bijzondere bestanddelen) blijft de keuzelijst, want daar zou
   * een rijtje knoppen het scherm overnemen.
   */
  _keuzeKnoppen(opties, waarde, kies) {
    return html`
      <div class="keuzeknoppen">
        ${opties.map((o) => html`
          <button type="button" class="keuzeknop ${o === waarde ? 'aan' : ''}"
                  @click=${() => kies(o === waarde ? '' : o)}>${this._cap(o)}</button>`)}
      </div>`;
  }

  /**
   * Eén bijzonder bestanddeel, als eigen tegel met eigen kopjes.
   *
   * Het was een tabel met vaste kolommen, maar die kolommen betekenen niet bij elk type
   * hetzelfde: wortels hebben een grootte, asfalt een type asfalt en ballast een fractie, en
   * die drie kennen juist géén herkomst. In een tabel moest dat extra veld dan op een eigen
   * regel eronder, los van zijn kopje -- of het kaapte een kolom die bij de buren iets anders
   * betekent. Een tegel per bestanddeel draagt zijn eigen kopjes, dus het derde veld heet
   * gewoon waar het over gaat.
   *
   * Welke velden een type kent is een normregel (NEN 6693); die staat hier niet, alleen waar
   * ze terechtkomen.
   *
   * @param {boolean} met6693  de extra velden en de herkomstregels van NEN 6693. Het
   *   5104-formulier kent die niet en houdt type, intensiteit en herkomst.
   */
  _renderBzbTegel(iv, b, bi, bzbOpts, bzbGOpts, origOpts, met6693) {
    const isWortels = met6693 && b.type === nen6693.BZB_WORTELS;
    const isAsfalt = met6693 && b.type === nen6693.BZB_ASFALT;
    const isBallast = met6693 && b.type === nen6693.BZB_BALLAST;
    const alleenAntropogeen = met6693 && nen6693.BZB_ALLEEN_ANTROPOGEEN.has(b.type);
    const verwijder = () => {
      const its = iv.bzbItems.filter((_, i) => i !== bi);
      this._inlineVal = { ...iv, bzbItems: its };
    };

    // Het derde veld: wat dit type kent in plaats van een herkomst.
    const derde = isWortels ? { label: 'Grootte', veld: html`
          <select @change=${e => this._setBzbItem(bi, 'grootte', e.target.value)}>
            <option value="">—</option>
            ${nen6693.WORTELS_GROOTTE.map(o => html`<option value=${o.term} ?selected=${o.term === b.grootte}>${this._cap(o.term)}</option>`)}
          </select>` }
      : isAsfalt ? { label: 'Type asfalt', veld: html`
          <searchable-select
            .options=${nen6693.ASFALT_TYPEN}
            .value=${b.asfaltType || ''}
            placeholder="Kies of zoek…"
            @change=${e => this._setBzbItem(bi, 'asfaltType', e.detail.value)}>
          </searchable-select>` }
      : isBallast ? { label: 'Fractie', veld: html`
          <select @change=${e => this._setBzbItem(bi, 'ballastFractie', e.target.value)}>
            <option value="">—</option>
            ${nen6693.BALLAST_FRACTIES.map(o => html`<option value=${o} ?selected=${o === b.ballastFractie}>${o}</option>`)}
          </select>` }
      : { label: 'Herkomst', veld: alleenAntropogeen ? html`
          <select title="Alleen aangebracht ${b.type} is een bijzonder bestanddeel; natuurlijk ${b.type} is textuur."
                  @change=${e => this._setBzbItem(bi, 'herkomst', e.target.value)}>
            <option value="antropogeen" selected>Antropogeen</option>
          </select>` : html`
          <select @change=${e => this._setBzbItem(bi, 'herkomst', e.target.value)}>
            <option value="">—</option>
            ${origOpts.map(o => html`<option value=${o} ?selected=${o === b.herkomst}>${this._cap(o)}</option>`)}
          </select>` };

    return html`
      <div class="bzb-tegel">
        <div class="expand-field">
          <label>Type</label>
          <searchable-select
            .options=${bzbOpts}
            .value=${b.type || ''}
            placeholder="Kies of zoek…"
            @change=${e => met6693 ? this._setBzbType(bi, e.detail.value) : this._setBzbItem(bi, 'type', e.detail.value)}>
          </searchable-select>
        </div>
        <div class="expand-field">
          <label>Intensiteit</label>
          ${isWortels ? html`
            <select @change=${e => this._setBzbItem(bi, 'gradatie', e.target.value)}>
              <option value="">—</option>
              ${nen6693.WORTELS_INTENSITEIT.map(o => html`<option value=${o.term} ?selected=${o.term === b.gradatie}>${this._cap(o.term)}</option>`)}
            </select>` : html`
            <searchable-select
              .options=${bzbGOpts}
              .value=${b.gradatie || ''}
              placeholder="Kies of zoek…"
              @change=${e => this._setBzbItem(bi, 'gradatie', e.detail.value)}>
            </searchable-select>`}
        </div>
        <div class="expand-field">
          <label>${derde.label}</label>
          ${derde.veld}
        </div>
        <div class="expand-field">
          <label>Code</label>
          <input type="text" title="Code van het bestanddeel, bijvoorbeeld HOU3AN" class="bzb-code"
            .value=${this._getBzbShortcode(b)}
            @input=${e => this._applyBzbShortcode(e.target.value, bi)}
            style="border-color:${this._invalidBzbRows.has(bi) ? '#DC2626' : 'var(--mv-border)'};
                   color:${this._invalidBzbRows.has(bi) ? '#DC2626' : 'var(--mv-primary)'};">
        </div>
        <button class="bzb-del-btn" title="Verwijder" @click=${verwijder}>
          ${unsafeSVG(lucideIcon(X, 12))}
        </button>
      </div>`;
  }

  /**
   * Waarnemingen: olie/water-reactie, geur en de vrije opmerking.
   *
   * Een functie voor beide normen. Dit onderdeel stond twee keer apart in de twee
   * laagformulieren en liep daardoor uiteen -- de ene kende een veld dat de andere niet had,
   * en een correctie landde maar in een van de twee.
   *
   * De vier keuzes staan in twee rijen van twee: boven de knoppenrijen (reactie en gradatie),
   * daaronder de keuzelijsten die erbij horen. De gradatie en de geur staan er altijd, ook
   * leeg, anders springt de tweede kolom pas in beeld zodra je iets kiest en verschuift de
   * rest mee.
   *
   * 'Overige waarneming olie-watertest' hoort bij beide normen. De keuzelijst komt uit NEN 6693
   * Tabel 31 -- NEN 5104 kent zo'n lijst niet, maar wat je op het water ziet (regenboogeffect,
   * dof, bolletjes) hangt niet van de gekozen norm af. De kolom is er ook voor allebei; het
   * 5104-formulier gooide de waarde alleen weg bij opslaan, dus liet hij het veld niet zien.
   *
   * De consistentie staat hier alleen bij NEN 5104. Die norm bindt hem niet aan een
   * grondsoort, dus hoort hij bij wat je aan de laag waarneemt. NEN 6693 kent hem alleen bij
   * slib, klei/silt en organische lagen; daar staat hij bij de grondsoort, want daar hángt hij
   * ervan af.
   *
   * @param {object} iv  de laag in bewerking
   * @param {boolean} metConsistentie  alleen NEN 5104
   */
  _renderWaarnemingen(iv, metConsistentie = false) {
    const sjabloon = this.templateDefaults?.layer || {};
    const olie = !sjabloon.hide_oil_water;
    const geur = !sjabloon.hide_geur;
    const leeg = html`<div></div>`;
    const owInfo = () => this._showTableInfo('Andere waarnemingen olie-watertest',
      'Andere waarnemingen bij de olie-watertest. NEN 6693 Tabel 31.',
      nen6693.OLIE_WATER_ANDERE_WAARNEMING);
    const toonOverige = olie;
    return html`
      <div>
        ${this._sectieKop('Waarnemingen')}
        ${!metConsistentie ? '' : html`
          <div class="expand-field-group" style="grid-template-columns:1fr 1fr; margin-bottom:14px;">
            <div class="expand-field">
              <label>Consistentie</label>
              <select @change=${e => this._setIV('consistentie', e.target.value)}>
                <option value="">—</option>
                ${[...tiCodelists.consistentie.keys()].map(o => html`
                  <option value=${o} ?selected=${o === iv.consistentie}>${this._cap(o)}</option>`)}
              </select>
            </div>
            <div></div>
          </div>`}
        ${!olie && !geur ? '' : html`
          <div class="expand-field-group" style="grid-template-columns:1fr 1fr; row-gap:14px;">
            ${!olie ? leeg : html`
              <div class="expand-field">
                <label style="display:flex; align-items:center; gap:5px;">
                  <span>Olie/water-reactie</span>${this._infoBtn(owInfo)}
                </label>
                ${this._keuzeKnoppen([...tiCodelists.olieWaterReactie.keys()], iv.oilWater,
                                     (w) => this._setIV('oilWater', w))}
              </div>`}
            ${!geur ? leeg : html`
              <div class="expand-field">
                <label>Geur gradatie</label>
                ${this._keuzeKnoppen([...tiCodelists.geurSterkte.keys()], iv.geurIntensity,
                                     (w) => this._setIV('geurIntensity', w))}
              </div>`}
            ${!toonOverige || !iv.oilWater || iv.oilWater === 'Geen' ? leeg : html`
              <div class="expand-field">
                <label style="display:flex; align-items:center; gap:5px;">
                  <span>Overige waarneming olie-watertest</span>${this._infoBtn(owInfo)}
                </label>
                <select @change=${e => this._setIV('oilWaterObservation', e.target.value)}>
                  <option value="" ?selected=${!iv.oilWaterObservation}>—</option>
                  ${nen6693.OLIE_WATER_ANDERE_WAARNEMING.map(o => html`
                    <option value=${o.term} ?selected=${o.term === iv.oilWaterObservation}>${this._cap(o.term)}</option>`)}
                </select>
              </div>`}
            ${!geur ? leeg : html`
              <div class="expand-field">
                <label>Geur</label>
                <searchable-select placeholder="Kies of zoek…"
                  .options=${[...tiCodelists.geurType.keys()].map(o => ({ value: o, label: this._cap(o) }))}
                  .value=${iv.geur || ''}
                  @change=${e => this._setIV('geur', e.detail.value)}></searchable-select>
              </div>`}
          </div>`}

        <div class="expand-field" style="margin-top:12px;">
          <label>Opmerking</label>
          <textarea rows="3" placeholder="Bijv. puinresten aangetroffen, sterk wisselend"
            .value=${iv.opmerking || ''}
            @input=${e => this._setIV('opmerking', e.target.value)}></textarea>
        </div>
      </div>`;
  }

  /** Alle percentages weg: terug naar zelf kiezen. */
  _gsizeWissen() {
    this._setIV('gsize', {});
    this._gsizeMelding = null;
    this._korrelUitleg = false;
  }

  /**
   * Iemand probeert een veld te wijzigen dat de korrelverdeling bepaalt.
   *
   * Die velden staan op slot, en een veld dat niet reageert zonder uitleg laat je zoeken naar
   * wat er stuk is. De uitleg permanent tonen zou ruis zijn bij elke laag die je alleen even
   * bekijkt; daarom verschijnt hij pas als je het probeert, en verdwijnt hij weer.
   *
   * De velden zelf zijn `disabled` en vangen geen klikken; via `pointer-events: none` komt de
   * klik bij het omringende kader terecht.
   */
  _korrelUitlegTonen() {
    this._korrelUitleg = true;
    clearTimeout(this._korrelUitlegTimer);
    this._korrelUitlegTimer = setTimeout(() => { this._korrelUitleg = false; }, 6000);
  }

  /**
   * Het korrelverdelingsblok boven Grondsoort.
   *
   * Drie toestanden, en die moeten uit elkaar te houden zijn:
   *  - leeg      -> 'nog geen percentages, grondsoort wordt handmatig gekozen'
   *  - ingevuld  -> chip LEIDEND, en de velden eronder zijn afgeleid
   *  - daartussen zit de overgang: wie iets invult ziet zijn handmatige keuze verdwijnen, en
   *    daar hoort de knop 'Percentages wissen' als weg terug bij.
   */
  _renderKorrelverdeling(iv) {
    if (!this.grainSizeInput) return '';
    const g = iv?.gsize || {};
    const klasse = this._gsizeKlasse(iv);
    const ingevuld = !!this._gsizePayload(iv);
    const f = klasse?.fractions;
    // De gestapelde balk: de vier minerale fracties plus organische stof. Die laatste hoort er
    // strikt genomen niet in -- hij telt over de totale droge massa, niet over de minerale
    // delen -- maar zonder hem zie je niet dat je hem hebt ingevuld. Hij staat daarom achteraan,
    // met zijn eigen getal in de legenda.
    const segmenten = f ? [
      ['Lutum', f.lutum, '#8C5A3C'],
      ['Silt', f.silt, '#C4A06A'],
      ['Zand (rest)', f.zand, '#E3CFA4'],
      ['Grind', f.grind, '#6B7A8F'],
      ['Organisch', Number(g.organicPct) || 0, '#3F3A34'],
    ].filter(([, v]) => v > 0) : [];
    const totaal = segmenten.reduce((som, [, v]) => som + v, 0) || 100;
    const getal = (v) => String(Math.round(v * 10) / 10).replace('.', ',');

    return html`
      <div class="korrel ${this._korrelDicht ? 'dicht' : ''}"
           title=${this._korrelDicht ? 'Klik om de korrelverdeling te tonen' : ''}
           @click=${() => { if (this._korrelDicht) this._korrelDicht = false; }}>
        <div class="korrel-kop">
          <span class="expand-section-title">Korrelverdeling</span>
          <span class="korrel-chip ${ingevuld ? '' : 'uit'}">${ingevuld ? 'Leidend' : 'Leeg'}</span>
          <span class="korrel-uitleg">${ingevuld
            ? 'Bepaalt de grondsoort, de bijmengingen en de mediaan hieronder.'
            : 'Vul percentages in om de grondsoort te laten bepalen.'}</span>
          ${this._korrelDicht ? '' : html`
            <button type="button" class="korrel-wissen" ?disabled=${!ingevuld}
                    title="Terug naar zelf kiezen" @click=${() => this._gsizeWissen()}>
              Percentages wissen
            </button>`}
          <button type="button" class="korrel-klap"
                  title=${this._korrelDicht ? 'Korrelverdeling tonen' : 'Korrelverdeling inklappen'}
                  aria-expanded=${this._korrelDicht ? 'false' : 'true'}
                  @click=${(e) => { e.stopPropagation(); this._korrelDicht = !this._korrelDicht; }}>
            ${unsafeSVG(lucideIcon(ChevronDown, 14))}
          </button>
        </div>

        ${this._korrelDicht ? '' : html`
        <div class="korrel-velden">
          ${RecordingRowDetail.GSIZE_VELDEN.map((veld) => {
            // Leem rekent zichzelf uit zodra lutum en silt er allebei staan; dan valt er niets
            // te kiezen en toont het veld de som in plaats van een leeg vakje.
            const som = veld.key === 'leemPct' ? this._gsizeLeem(g) : null;
            return html`
            <div class="expand-field korrel-veld ${som != null ? 'auto' : ''}">
              <label>
                <span class="korrel-vlag" style="background:${veld.kleur};"></span>
                <span>${veld.label}</span>
                <span class="korrel-eenheid">${veld.eenheid}</span>
              </label>
              ${som != null
                ? html`<input type="text" disabled .value=${`= ${getal(som)}`}>`
                : html`<input type="number" inputmode="decimal" placeholder="—"
                         .value=${g[veld.key] ?? ''}
                         @input=${(e) => this._setGsize(veld.key, e.target.value)}>`}
              ${this._gsizeMelding?.key === veld.key
                ? html`<span class="korrel-grens">${this._gsizeMelding.tekst}</span>`
                : veld.hint ? html`<span class="korrel-hint">${veld.hint}</span>` : ''}
            </div>`;
          })}
        </div>`}

        ${this._korrelDicht || !segmenten.length ? '' : html`
          <div class="korrel-balk">
            ${segmenten.map(([, v, kleur]) => html`
              <div style="width:${(v / totaal) * 100}%; background:${kleur};"></div>`)}
          </div>
          <div class="korrel-legenda">
            ${segmenten.map(([naam, v, kleur]) => html`
              <span><i class="korrel-vlag" style="background:${kleur};"></i>${naam} ${getal(v)}%</span>`)}
          </div>`}

        ${klasse?.notes?.length && !this._korrelDicht ? html`
          <div class="korrel-hint">${klasse.notes.join(' ')}</div>` : ''}
      </div>
      <div class="korrel-pijl">
        ${klasse
          ? html`↓ bepaalt: ${grainClassificationName(klasse)}`
          : html`↓ nog geen percentages, grondsoort wordt handmatig gekozen`}
      </div>
    `;
  }

  /**
   * Remove admixture row n (2 or 3) from the inline layer form, shifting any
   * later row up so there are no gaps, and decrement the revealed-row count.
   */
  _removeAdmix(n) {
    const v = { ...this._inlineVal };
    // Alles onder de weggehaalde rij schuift één op; een gat in het midden zou betekenen dat
    // 'bijmenging 2' leeg is terwijl 'bijmenging 3' gevuld -- en zo slaan we ze niet op.
    if (n === 1) {
      v.gs_toev1 = v.gs_toev2 || '';
      v.gs_grad1 = v.gs_grad2 || '';
    }
    if (n <= 2) {
      v.gs_toev2 = v.gs_toev3 || '';
      v.gs_grad2 = v.gs_grad3 || '';
    }
    v.gs_toev3 = '';
    v.gs_grad3 = '';
    v.extraAdmix = Math.max(0, (v.extraAdmix ?? 0) - 1);
    this._inlineVal = v;
  }

  /** Update a key inside _inlineVal.bzbItems[idx] for BZB. */
  _setBzbItem(idx, key, value) {
    const bzbItems = this._inlineVal.bzbItems.map((it, i) =>
      i === idx ? { ...it, [key]: value } : it
    );
    this._inlineVal = { ...this._inlineVal, bzbItems };
  }

  // Type-wissel van een BZB-rij. Wortels is speciaal: eigen dichtheid/grootte, altijd
  // gebiedseigen (herkomst-keuze vervalt). Reset de niet-passende velden bij de overgang.
  _setBzbType(idx, value) {
    const bzbItems = this._inlineVal.bzbItems.map((it, i) => {
      if (i !== idx) return it;
      const wasSpecial = it.type === nen6693.BZB_WORTELS || it.type === nen6693.BZB_ASFALT
        || it.type === nen6693.BZB_BALLAST;
      if (value === nen6693.BZB_WORTELS) return { ...it, type: value, gradatie: '', herkomst: 'gebiedseigen', grootte: '', asfaltType: '', ballastFractie: '' };
      // Asfalt: altijd antropogeen (herkomst-keuze vervalt) + extra 'type asfalt'-keuze.
      if (value === nen6693.BZB_ASFALT) return { ...it, type: value, herkomst: 'antropogeen', grootte: '', asfaltType: it.asfaltType || '', ballastFractie: '' };
      // Ballast: idem, met de fractie (korrelmaat) als vervolgkeuze.
      if (value === nen6693.BZB_BALLAST) return { ...it, type: value, herkomst: 'antropogeen', grootte: '', asfaltType: '', ballastFractie: it.ballastFractie || '' };
      if (wasSpecial) return { ...it, type: value, gradatie: '', herkomst: '', grootte: '', asfaltType: '', ballastFractie: '' };
      // Bestanddelen die alleen aangebracht kunnen zijn (grind): herkomst staat vast. Natuurlijk
      // grind is textuur en hoort in de bijmenging, niet hier -- zie BZB_ALLEEN_ANTROPOGEEN.
      if (nen6693.BZB_ALLEEN_ANTROPOGEEN.has(value)) return { ...it, type: value, herkomst: 'antropogeen' };
      return { ...it, type: value };
    });
    this._inlineVal = { ...this._inlineVal, bzbItems };
  }

  // Klein info-icoon (ⓘ) dat een popup opent.
  _infoBtn(handler) {
    return html`<button type="button" title="Uitleg" @click=${handler}
      style="display:inline-flex; align-items:center; justify-content:center; width:16px; height:16px; padding:0; border:none; background:transparent; color:var(--mv-text-muted); cursor:pointer; border-radius:50%; line-height:1;"
      @mouseover=${e => e.currentTarget.style.color = 'var(--mv-primary)'}
      @mouseout=${e => e.currentTarget.style.color = 'var(--mv-text-muted)'}>
      ${unsafeSVG(lucideIcon(Info, 14))}</button>`;
  }

  // ─── Shortcode Sync ──────────────────────────────────────────────────────

  // Kapitaliseer alleen de weergave (eerste letter); de opgeslagen waarde blijft de kleine-
  // letter-term. Voor native <select>-opties, want text-transform op <option> is onbetrouwbaar.
  _cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

  // Herbruikbare tabel voor de NEN-info-popups; kolomkoppen instelbaar.
  _infoTable(items, col1 = 'Keuzelijst', col2 = 'Omschrijving') {
    const cell = 'padding:6px 10px; vertical-align:top; border-bottom:1px solid var(--mv-border);';
    return html`
      <table style="width:100%; border-collapse:collapse; font-size:0.88rem; margin-top:4px;">
        <thead>
          <tr>
            <th style="${cell} text-align:left; width:34%; font-weight:700; color:var(--mv-text); border-bottom:2px solid var(--mv-border);">${col1}</th>
            <th style="${cell} text-align:left; font-weight:700; color:var(--mv-text); border-bottom:2px solid var(--mv-border);">${col2}</th>
          </tr>
        </thead>
        <tbody>
          ${items.map(o => html`<tr><td style="${cell} font-weight:600; color:var(--mv-text);">${this._cap(o.term)}</td><td style="${cell} color:var(--mv-text-muted);">${o.description}</td></tr>`)}
        </tbody>
      </table>`;
  }

  // Simpele info-popup met een subtitel + één tabel (klei/silt, organisch, wortels, mediaan…).
  _showTableInfo(title, subtitle, items, col1, col2) {
    alertDialog({
      title, okLabel: 'Sluiten', maxWidth: 640,
      message: html`<div style="text-align:left; white-space:normal; max-height:60vh; overflow:auto;"><div style="font-size:0.85rem; color:var(--mv-text-muted); margin-bottom:6px;">${subtitle}</div>${this._infoTable(items, col1, col2)}</div>`,
    });
  }

  // Beslishulp-popup bij Hoofdbestanddeel: 'Welke grondsoort kies ik?' (NEN bijlage B-volgorde).
  // Rustige weergave: neutrale rij met een klein gekleurd cirkeltje als enige accent.
  _showHoofdbestanddeelInfo() {
    const dot = { neutral: 'var(--mv-text-muted)', blue: '#2563EB', green: '#16A34A' };
    alertDialog({
      title: 'Welke grondsoort kies ik?',
      okLabel: 'Sluiten',
      maxWidth: 560,
      message: html`<div style="text-align:left; white-space:normal; max-height:62vh; overflow:auto;"><div style="font-size:0.85rem; color:var(--mv-text-muted); margin-bottom:10px;">Loop de lijst van boven naar beneden. De eerste die past, is je keuze.</div>${this._N.HOOFDBESTANDDEEL_KEUZEHULP.map(o => html`<div style="display:flex; gap:9px; padding:7px 2px; border-bottom:1px solid var(--mv-border);"><span style="flex:none; width:8px; height:8px; margin-top:6px; border-radius:50%; background:${dot[o.tone]};"></span><div><div style="font-weight:600; color:var(--mv-text);">${o.title}</div><div style="font-size:0.88rem; color:var(--mv-text-muted); margin-top:1px;">${o.description}</div></div></div>`)}<div style="font-size:0.8rem; color:var(--mv-text-muted); margin-top:10px;">Volgorde volgens de NEN (bijlage B).</div></div>`,
    });
  }

  // Info-popup bij het Consistentie-veld. Voor slib: consistentie onder waterkolom (Tabel 22)
  // + rijping boven waterkolom (Tabel 25). Voor klei/silt: minerale fijne lagen (Tabel 24).
  _showConsistentieInfo(hoofdCode) {
    if (this._N.MINERALE_CONSISTENTIE_GRONDSOORTEN.has(hoofdCode)) {
      this._showTableInfo('Consistentie (klei en silt)', 'Consistentie van minerale fijne lagen. NEN 6693 Tabel 24.', this._N.MINERALE_CONSISTENTIE);
      return;
    }
    if (this._N.ORGANISCHE_CONSISTENTIE_GRONDSOORTEN.has(hoofdCode)) {
      this._showTableInfo('Consistentie (organische lagen)', 'Consistentie van organische lagen. NEN 6693 Tabel 23.', this._N.ORGANISCHE_CONSISTENTIE);
      return;
    }
    alertDialog({
      title: 'Consistentie / rijping van slib',
      okLabel: 'Sluiten',
      maxWidth: 640,
      message: html`<div style="text-align:left; white-space:normal; max-height:60vh; overflow:auto;">${this._N.SLIB_CONSISTENTIE_GROEPEN.map(group => html`<div style="margin-bottom:16px;"><div style="font-weight:700; color:var(--mv-text);">${group.label}</div><div style="font-size:0.85rem; color:var(--mv-text-muted); margin-top:2px;">${group.hint}</div>${this._infoTable(group.items)}</div>`)}</div>`,
    });
  }

  _getShortcode(v) {
    if (this._is5104()) return this._getShortcode5104(v);
    if (this._isIso()) return this._getShortcodeIso(v);
    if (!v) return '';
    const hoofd = v.gs_hoofd || v.hoofd;
    // Rotslaag: de code = het gesteentetype (Bijlage C.2), geen bijmengingen.
    if (hoofd === 'rots') return this._N.GESTEENTE.get(v.gs_gesteente) || '';
    // NEN 6693-notatie: hoofdletter/kleine-letter zijn betekenisvol (grondsoort 'Gr'/'Z',
    // bijmenging 'gr'/'s'/'z' klein). GEEN toUpperCase. Fallback op de oude lijst voor
    // nog-niet-gemigreerde data.
    const soilCode = this._N.GRONDSOORT.get(hoofd) || this._N.LAAGTYPE_SPECIAAL.get(hoofd) || tiCodelists.bodemsoortHistorisch.get(hoofd) || '';
    // Mediaan (alleen zand/grind) als cijfer direct na de grondsoort: Z5 = zand 300-420 µm.
    // Het cijfer is de plaats in de lijst van déze grondsoort — zand heeft er zeven, grind drie.
    const med = this._N.MEDIAAN_GRONDSOORTEN.has(soilCode)
      ? this._N.mediaanDigit(soilCode, v.mediaan) : '';
    // Mineraalarm (alleen organisch) als 'm' direct na de grondsoort: Vm = veen mineraalarm.
    const mineraalarm = (this._N.MINERAALARM_GRONDSOORTEN.has(soilCode) && v.gs_mineraalarm)
      ? this._N.MINERAALARM_CODE : '';
    const bij = (t, g) => {
      const tc = this._N.BIJMENGING.get(t) || tiCodelists.toevoeging.get(t) || '';
      const gc = this._N.INTENSITEIT.get(g) || tiCodelists.gradatie.get(g) || '';
      return tc + gc;
    };
    return soilCode + med + mineraalarm
      + bij(v.gs_toev1 || v.toev1, v.gs_grad1 || v.grad1)
      + bij(v.gs_toev2 || v.toev2, v.gs_grad2 || v.grad2)
      + bij(v.gs_toev3 || v.toev3, v.gs_grad3 || v.grad3);
  }

  // Reverse-lookups voor de NEN 6693-shortcode-parsers. Sleutels genormaliseerd naar één
  // casing zodat invoer hoofdletter-ongevoelig is (positie bepaalt betekenis, niet de casing);
  // de canonieke NEN-notatie wordt bij weergave weer opgebouwd door de _get*Shortcode-composers.
  _getNenReverseMaps() {
    if (this._nenRev && this._nenRevN === this._N) return this._nenRev;
    this._nenRevN = this._N;
    // grondsoort + speciaal laagtype: code (lowercased) → { term, code }
    const soil = new Map();
    for (const [term, code] of this._N.GRONDSOORT) soil.set(code.toLowerCase(), { term, code });
    for (const [term, code] of this._N.LAAGTYPE_SPECIAAL) soil.set(code.toLowerCase(), { term, code });
    const soilCodes = [...soil.keys()].sort((a, b) => b.length - a.length); // langste prefix eerst
    const bijmenging = new Map(); // enkele letter (klein) → term
    for (const [term, code] of this._N.BIJMENGING) bijmenging.set(code.toLowerCase(), term);
    const intensity = new Map(); // cijfer → term
    for (const [term, digit] of this._N.INTENSITEIT) intensity.set(digit, term);
    const colour = new Map(); // 2-letter (klein) → term
    for (const [term, code] of this._N.KLEUR) colour.set(code.toLowerCase(), term);
    const tint = new Map(); // li/do (klein) → term ('' = standaard heeft geen prefix)
    for (const [term, code] of this._N.KLEUR_TINT) if (code) tint.set(code.toLowerCase(), term);
    const gesteente = new Map(); // 3-letter (klein) → term, voor een rotslaag
    for (const [term, code] of this._N.GESTEENTE) gesteente.set(code.toLowerCase(), term);
    // BZB-soort = C.1 (bijzondere bestanddelen) + C.2 (gesteenten), beide 3-letter (hoofdletter).
    const bzbSoort = new Map();
    for (const [term, code] of this._N.BZB_SOORT) bzbSoort.set(code.toUpperCase(), term);
    for (const [term, code] of this._N.GESTEENTE) if (!bzbSoort.has(code.toUpperCase())) bzbSoort.set(code.toUpperCase(), term);
    const bzbVolume = new Map(); // cijfer → term
    for (const [term, digit] of Object.entries(this._N.BZB_VOLUME_SHORT)) bzbVolume.set(digit, term);
    const bzbHerkomst = new Map(); // AN/GE → term
    for (const [term, code] of Object.entries(this._N.BZB_HERKOMST_SHORT)) bzbHerkomst.set(code.toUpperCase(), term);
    this._nenRev = { soil, soilCodes, gesteente, bijmenging, intensity, colour, tint, bzbSoort, bzbVolume, bzbHerkomst };
    return this._nenRev;
  }

  _getBzbShortcode(b) {
    if (this._is5104()) return this._getBzbShortcode5104(b);
    if (!b?.type) return '';
    // Wortels: alleen de code (WO); dichtheid/grootte staan los, altijd gebiedseigen.
    if (b.type === nen6693.BZB_WORTELS) return nen6693.BZB_SOORT.get(nen6693.BZB_WORTELS);
    // NEN 6693 BZB-code = 3-letter soort (GLT/AWK/…, óók gesteenten uit C.2) + volume-cijfer + AN/GE.
    const typeCode = nen6693.BZB_SOORT.get(b.type) || nen6693.GESTEENTE.get(b.type) || tiCodelists.LaSpecialCompounds.get(b.type) || '';
    if (!typeCode) return '';
    const gradCode   = b.gradatie ? (nen6693.BZB_VOLUME_SHORT[b.gradatie] || '') : '';
    const originCode = b.herkomst ? (nen6693.BZB_HERKOMST_SHORT[b.herkomst] || '') : '';
    return typeCode + gradCode + originCode;
  }

  _applyBzbShortcode(code, bi) {
    if (this._is5104()) return this._applyBzbShortcode5104(code, bi);
    const raw = (code || '').trim().toUpperCase();
    if (!raw) {
      // Clear all three fields atomically in one state update
      const bzbItems = this._inlineVal.bzbItems.map((it, i) =>
        i === bi ? { ...it, type: '', gradatie: '', herkomst: '' } : it
      );
      this._inlineVal = { ...this._inlineVal, bzbItems };
      // Clear validation
      const inv = new Set(this._invalidBzbRows);
      inv.delete(bi);
      this._invalidBzbRows = inv;
      return;
    }

    const invalid = () => {
      const inv = new Set(this._invalidBzbRows); inv.add(bi); this._invalidBzbRows = inv;
    };

    // Wortels: 2-letter code 'WO' → type wortels (dichtheid/grootte via de dropdowns).
    if (raw === nen6693.BZB_SOORT.get(nen6693.BZB_WORTELS).toUpperCase()) {
      const inv = new Set(this._invalidBzbRows); inv.delete(bi); this._invalidBzbRows = inv;
      const bzbItems = this._inlineVal.bzbItems.map((it, i) =>
        i === bi ? { ...it, type: nen6693.BZB_WORTELS, herkomst: 'gebiedseigen' } : it);
      this._inlineVal = { ...this._inlineVal, bzbItems };
      return;
    }

    // NEN 6693 BZB-soort = 3-letter (GLT/AWK/…).
    if (raw.length < 3) { invalid(); return; }

    const { bzbSoort, bzbVolume, bzbHerkomst } = this._getNenReverseMaps();
    const typeName = bzbSoort.get(raw.slice(0, 3));
    if (!typeName) { invalid(); return; }
    let rest = raw.slice(3);

    // Herkomst = laatste 2 tekens als het AN/GE is (soort is alfa, volume is een cijfer).
    let herkomstName = '';
    if (rest.endsWith('AN') || rest.endsWith('GE')) {
      herkomstName = bzbHerkomst.get(rest.slice(-2)) || '';
      if (!herkomstName) { invalid(); return; }
      rest = rest.slice(0, -2);
    }

    // Wat overblijft is het (optionele) volume-cijfer (1-6).
    let gradatieName = '';
    if (rest) {
      gradatieName = bzbVolume.get(rest) || '';
      if (!gradatieName) { invalid(); return; }
    }

    // All valid — clear error and apply
    const inv = new Set(this._invalidBzbRows); inv.delete(bi); this._invalidBzbRows = inv;
    this._setBzbItem(bi, 'type',     typeName);
    this._setBzbItem(bi, 'gradatie', gradatieName);
    this._setBzbItem(bi, 'herkomst', herkomstName);
  }

  _getColorShortcode(v) {
    if (this._is5104()) return this._getColorShortcode5104(v);
    if (!v) return '';
    // Inline edit: kleurMain / kleurSub / kleurShade
    // Full edit:   kleur / sterkte  (no sub in full edit panel)
    const colorName = v.kleurMain || v.kleur || '';
    const subName   = v.kleurSub  || '';
    const shadeName = v.kleurShade || v.sterkte || '';
    // NEN 6693: tint-prefix 'li'/'do' (standaard = geen prefix) + kleur klein 2-letter, bv.
    // 'libr' = licht bruin. Geen toUpperCase. Fallback op de oude lijst voor oude data.
    const color = this._N.KLEUR.get(colorName) || tiCodelists.kleur.get(colorName) || '';
    if (!color) return ''; // zonder hoofdkleur geen code (ook geen losse subkleur/tint)
    const sub   = this._N.KLEUR.get(subName) || tiCodelists.kleur.get(subName) || '';
    // Geen tint-prefix bij wit/zwart/beige/crème/kleurloos (Tabel 16).
    const shade = this._N.KLEUR_TINT_UITGESLOTEN.has(color)
      ? '' : (this._N.KLEUR_TINT.get(shadeName) ?? tiCodelists.kleurSterkte.get(shadeName) ?? '');
    return shade + color + sub;
  }

  _applyColorShortcode(code) {
    if (this._is5104()) return this._applyColorShortcode5104(code);
    // NEN 6693-kleurnotatie: optionele tint-prefix (li/do) + hoofdkleur (2-letter) + optionele
    // bijkleur (2-letter), bv. `libr` = licht bruin, `grbr` = grijs bruin. Casing-ongevoelig
    // ingevoerd; standaard-tint = géén prefix.
    const c = (code || '').trim().toLowerCase();
    if (!c) {
      this._invalidColorShortcode = false;
      this._inlineVal = { ...this._inlineVal, kleurMain:'', kleurSub:'', kleurShade:'' };
      return;
    }

    const { colour, tint } = this._getNenReverseMaps();

    let shadeVal = '';
    let rest = c;
    // Tint alleen als de eerste 2 tekens li/do zijn én er nog een kleur volgt.
    if (rest.length > 2 && tint.has(rest.slice(0, 2))) {
      shadeVal = tint.get(rest.slice(0, 2));
      rest = rest.slice(2);
    }

    // Hoofdkleur (verplicht, 2-letter).
    if (rest.length < 2) { this._invalidColorShortcode = true; return; }
    const mainCode = rest.slice(0, 2);
    const mainVal = colour.get(mainCode);
    if (!mainVal) { this._invalidColorShortcode = true; return; }
    rest = rest.slice(2);

    // Tint verboden bij wit/zwart/beige/crème/kleurloos (Tabel 16).
    if (shadeVal && this._N.KLEUR_TINT_UITGESLOTEN.has(mainCode)) {
      this._invalidColorShortcode = true; return;
    }

    // Optionele bijkleur (2-letter); mag niet gelijk zijn aan de hoofdkleur.
    let subVal = '';
    if (rest.length) {
      if (rest.length !== 2) { this._invalidColorShortcode = true; return; }
      if (rest === mainCode) { this._invalidColorShortcode = true; return; }
      subVal = colour.get(rest);
      if (!subVal) { this._invalidColorShortcode = true; return; }
    }

    this._invalidColorShortcode = false;
    this._inlineVal = {
      ...this._inlineVal,
      kleurMain:  mainVal,
      kleurSub:   subVal,
      kleurShade: shadeVal,
    };
  }

  _applyShortcode(code) {
    if (this._is5104()) return this._applyShortcode5104(code);
    if (this._isIso()) return this._applyShortcodeIso(code);
    // NEN 6693-composietnotatie: grondsoort/laagtype-code (Z, Gr, DET, SLIB, …) + tot drie
    // bijmengingen (letter g/h/k/s/z + optioneel intensiteit-cijfer 1-4). Mediaan zit NIET in
    // de code (los veld). Casing-ongevoelig ingevoerd; positie bepaalt de betekenis.
    const c = (code || '').trim();
    if (!c) {
      this._invalidShortcode = false;
      this._inlineVal = { ...this._inlineVal, gs_hoofd:'', gs_gesteente:'', gs_mineraalarm:false, gs_consistentie:'', mediaan:'', gs_toev1:'', gs_grad1:'', gs_toev2:'', gs_grad2:'', gs_toev3:'', gs_grad3:'' };
      return;
    }

    const { soil, soilCodes, gesteente, bijmenging, intensity } = this._getNenReverseMaps();
    const lc = c.toLowerCase();

    // 0) Gesteentecode (Bijlage C.2) → rotslaag (exact, want rots kent geen bijmenging).
    //    Vóór de grondsoort-prefix, zodat bv. 'gra' (graniet) niet als 'gr' + rest matcht.
    const rockTerm = gesteente.get(lc);
    if (rockTerm) {
      this._invalidShortcode = false;
      this._inlineVal = {
        ...this._inlineVal,
        gs_hoofd: 'rots', gs_gesteente: rockTerm, gs_mineraalarm: false, gs_consistentie: '', mediaan: '',
        gs_toev1:'', gs_grad1:'', gs_toev2:'', gs_grad2:'', gs_toev3:'', gs_grad3:'',
      };
      return;
    }

    // 1) Langste grondsoort-/laagtype-prefix.
    let matched = null;
    for (const key of soilCodes) { if (lc.startsWith(key)) { matched = soil.get(key); break; } }
    if (!matched) { this._invalidShortcode = true; return; }
    const h = matched.term;
    let rest = lc.slice(matched.code.length);

    // Mediaan: optioneel cijfer direct na een zand-/grind-grondsoort (Z5 = zand 300-420 µm).
    // Zit vóór de bijmengingen en botst dus niet met een intensiteit-cijfer (dat volgt op een
    // letter). Hoe ver het cijfer loopt hangt af van de grondsoort: zand kent zeven klassen,
    // grind drie — vandaar mediaanFromDigit en geen vaste reeks.
    let mediaan = this._N.MEDIAAN_GRONDSOORTEN.has(matched.code) ? this._inlineVal.mediaan : '';
    if (this._N.MEDIAAN_GRONDSOORTEN.has(matched.code) && /^[0-9]/.test(rest)) {
      mediaan = this._N.mediaanFromDigit(matched.code, rest[0]) || '';
      if (!mediaan) { this._invalidShortcode = true; return; }
      rest = rest.slice(1);
    }

    // Mineraalarm: 'm' direct na een organische grondsoort (veen/humus/detritus/gyttja).
    let mineraalarm = false;
    if (this._N.MINERAALARM_GRONDSOORTEN.has(matched.code) && rest[0] === 'm') {
      mineraalarm = true;
      rest = rest.slice(1);
    }

    // Slib/water/rots/BVL/bijzondere laag kennen geen textuur-bijmenging.
    if (this._N.LAAGTYPE_ZONDER_TEXTUUR.has(matched.code) && rest.length) {
      this._invalidShortcode = true; return;
    }

    // 2) Tot drie bijmengingen: composiet-letter + optioneel intensiteit-cijfer.
    const pairs = [];
    while (rest.length) {
      const m = rest.match(/^(gr|[hksz])([1-4])?/); // grindig = 'gr' (2 tekens), rest enkel
      if (!m) { this._invalidShortcode = true; return; }
      const t = bijmenging.get(m[1]);
      if (!t) { this._invalidShortcode = true; return; }
      // Een grondsoort mag zijn eigen bijmenging niet krijgen (geen 'Zz', 'Kk', …).
      if (this._N.GRONDSOORT_EIGEN_BIJMENGING[matched.code] === m[1]) { this._invalidShortcode = true; return; }
      // Bij een organische grondsoort geldt Tabel 10: alleen kleiig/zandig, met een
      // verplichte klasse zwak (1) of sterk (3). Zie admixtureRule in @mv/models.
      const rule = this._N.admixtureRule(matched.code, m[1]);
      if (!rule.allowed) { this._invalidShortcode = true; return; }
      let g = '';
      if (m[2]) {
        g = intensity.get(m[2]) || '';
        if (!g || !rule.intensities.includes(g)) { this._invalidShortcode = true; return; }
      } else if (rule.required) {
        // Tabel 10 kent Vk1 en Vk3, geen kale Vk.
        this._invalidShortcode = true; return;
      }
      pairs.push({ t, g });
      if (pairs.length > this._N.maxAdmixtures(matched.code)) { this._invalidShortcode = true; return; }
      rest = rest.slice(m[0].length);
    }

    this._invalidShortcode = false;
    this._inlineVal = {
      ...this._inlineVal,
      gs_hoofd: h,
      gs_gesteente: '', // geen rotslaag
      gs_mineraalarm: mineraalarm,
      gs_consistentie: matched.code === 'SLIB' ? this._inlineVal.gs_consistentie : '',
      mediaan, // uit de code geparsed (of behouden bij zand/grind, leeg bij andere grondsoort)
      gs_toev1: pairs[0]?.t || '',
      gs_grad1: pairs[0]?.g || '',
      gs_toev2: pairs[1]?.t || '',
      gs_grad2: pairs[1]?.g || '',
      gs_toev3: pairs[2]?.t || '',
      gs_grad3: pairs[2]?.g || '',
      // Reveal the bijmenging 2/3 rows when the shortcode fills them.
      extraAdmix: pairs.length >= 3 ? 2 : pairs.length >= 2 ? 1 : (this._inlineVal.extraAdmix ?? 0),
    };
  }

  async _deleteJar(e, jar, rawBoring) {
    e.stopPropagation();
    const confirmed = await confirmDialog({
      title: 'Potje verwijderen',
      message: `Weet je zeker dat je dit potje${jar.barcode ? ` (${jar.barcode})` : ''} wilt verwijderen?`,
      confirmLabel: 'Verwijderen',
      danger: true,
    });
    if (!confirmed) return;
    // Als het te verwijderen potje in bewerking staat: autosave stoppen en de edit sluiten.
    if (this._jarEdit === this._jarSleutel(jar)) {
      clearTimeout(this._jarSaveTimers.t);
      this._jarSaveTimers.t = null;
      this._jarEdit = null; this._jarVal = null; this._jarBoringId = null;
    }
    const scanData = this._getScanData(rawBoring);
    // Alleen dit ene potje weghalen (ook als er een tweede met precies dezelfde gegevens is).
    const weg = scanData.findIndex(j => this._jarSleutel(j) === this._jarSleutel(jar) && j.scannedAt === jar.scannedAt);
    const updated = scanData.filter((_, i) => i !== weg);
    this.dispatchEvent(new CustomEvent('update-scan-jar', {
      bubbles: true, composed: true,
      detail: { boringId: rawBoring.id, jars: updated }
    }));
  }

  _startJarEdit(e, jar, rawBoring) {
    if (this.readonly) return;
    e.stopPropagation();
    if (this._jarEdit === this._jarSleutel(jar)) {
      this._cancelJarEdit(e);
      return;
    }
    this._jarFlush(); // wisselen: eerst de vorige grondmonster-autosave wegschrijven
    this._jarEdit = this._jarSleutel(jar);
    this._jarBoringId = rawBoring.id;
    this._jarVal = {
      name: jar.name,
      barcode: jar.barcode,
      from: jar.layerDepthFrom,
      to: jar.layerDepthTo,
      geroerd: jar.geroerd !== false,   // default Ja
      packingType: jar.packingType ?? this.templateDefaults?.monster?.packing_type ?? DEFAULT_MONSTER_VERPAKKING,
    };
  }

  _cancelJarEdit(e) {
    e?.stopPropagation();
    this._jarFlush(); // eventuele lopende autosave nog wegschrijven
    this._jarEdit = null;
    this._jarScan = false;
    this._jarVal = null;
    this._jarBoringId = null;
  }


  /**
   * Waaraan we een potje herkennen tijdens het bewerken. Vroeger was dat de barcode, en kreeg een
   * nieuw potje daarom een verzonnen barcode (NEW-1234) die nooit klopte. De id kan het niet zijn:
   * bij elke opslag maakt de backend de potjes opnieuw aan, met nieuwe ids. Na elke opslag
   * schuift de sleutel mee (_jarSaveNow).
   */
  _jarSleutel(j) {
    return JSON.stringify([j?.barcode || '', j?.name || '', j?.layerDepthFrom ?? null, j?.layerDepthTo ?? null]);
  }

  /**
   * Zit deze barcode al op een ander potje of een watermonsterfles van deze boring? Geeft de
   * naam terug, anders null. Zelfde controle als de veldapp: twee potjes met dezelfde barcode
   * zijn in het lab niet meer uit elkaar te houden.
   */
  _barcodeAlGebruikt(code, rawBoring) {
    const c = String(code || '').trim();
    if (!c) return null;
    const ander = this._getScanData(rawBoring)
      .find(j => this._jarSleutel(j) !== this._jarEdit && (j.barcode || '').trim() === c);
    if (ander) return ander.name || ander.barcode;
    for (const wm of rawBoring?.watermonsters || []) {
      if ((wm.bottles || []).some(b => String(b.barcode || '').trim() === c)) return `watermonster ${wm.name || ''}`.trim();
    }
    return null;
  }

  _addJar(e, boreholeName, rawBoring) {
    if (this.readonly) return;
    e.stopPropagation();
    this._jarFlush(); // eerst een eventuele lopende autosave wegschrijven
    const scanData = this._getScanData(rawBoring);
    const { from, to } = nextJarDepth(scanData.map(j => j.layerDepthTo));
    // Pre-fill the name with "<boring>-<n>"; the user can still overwrite it.
    const base = boreholeName || rawBoring.name || '';
    const autoName = base ? `${base}-${scanData.length + 1}` : '';
    // Geen barcode verzinnen: leeg tot hij ingevuld of gescand is.
    const newJar = {
      name: autoName,
      barcode: '',
      boreholeName: boreholeName,
      layerDepthFrom: from,
      layerDepthTo: to,
      scannedAt: new Date().toISOString()
    };

    const updated = [...scanData, newJar];

    this.dispatchEvent(new CustomEvent('update-scan-jar', {
      bubbles: true, composed: true,
      detail: { boringId: rawBoring.id, jars: updated }
    }));

    // Auto-edit the new jar
    setTimeout(() => {
      this._jarEdit = this._jarSleutel(newJar);
      this._jarBoringId = rawBoring.id;
      // Sjabloon-default voor de verpakking; anders de systeemdefault (glazen pot 580 ml).
      const tplPacking = this.templateDefaults?.monster?.packing_type || DEFAULT_MONSTER_VERPAKKING;
      this._jarVal = { name: autoName, barcode: '', from, to, geroerd: true, packingType: tplPacking };
      this.requestUpdate();
    }, 50);
  }

  _getScanData(rawBoring) {
    if (!Array.isArray(rawBoring?.monsters)) return [];
    return rawBoring.monsters
      .filter(m => m.source === 'scan' || m.type === 'Jar')
      .map(m => ({
        id: m.id,
        name: m.name || '',
        // Geen barcode = leeg. Vroeger viel dit terug op de naam, waardoor een potje zonder
        // barcode zijn naam ("SLEUF3-3") als barcode liet zien -- en die bij de volgende opslag
        // ook als barcode wegschreef.
        barcode: m.identificatie ?? '',
        layerDepthFrom: m.upperDepth ?? m.upper_depth ?? null,
        layerDepthTo: m.lowerDepth ?? m.lower_depth ?? null,
        geroerd: m.geroerd ?? null,
        packingType: m.packingType ?? m.packing_type ?? null,
        // Via parseTimestamp: de backend levert 'YYYY-MM-DD HH:MM:SS' zonder tijdzone, en
        // `new Date()` leest dat als LOKALE tijd -- 's zomers twee uur mis. Deze waarde wordt
        // bij het opslaan weer als created_at weggeschreven, dus zonder dit verschuift het
        // scanmoment van een potje bij elke bewerking.
        scannedAt: parseTimestamp(m.created_at)?.getTime() ?? 0,
      }));
  }


  // ─── Render ─────────────────────────────────────────────────────────────


  /**
   * Het transcript zoals deze pagina het gebruikt. Hield de hallucinatie preventie de opname
   * tegen, dan is de bewaarde tekst verzonnen: die tonen we niet, zetten we niet in het
   * bewerkvak, en verwerken we ook niet opnieuw -- dan kwam er alsnog een verzonnen boring uit.
   */
  _transcript(rec) {
    if (rec?.status === 'error' && preventieVoorGebruiker(rec.error)) return '';
    return rec?.transcription || '';
  }

  /**
   * De tekst in het transcriptievak. Hield de hallucinatie preventie deze opname tegen, dan
   * zeggen we dát, in plaats van "Geen transcriptie beschikbaar".
   */
  _renderTranscriptieTekst(rec) {
    const p = rec.status === 'error' ? preventieVoorGebruiker(rec.error) : null;
    if (p) {
      return html`
        <div class="transcription-box preventie">
          <div class="preventie-titel">${unsafeSVG(lucideIcon(AlertTriangle, 14))} ${p.title}</div>
          <div>${p.text}</div>
        </div>`;
    }
    return html`<div class="transcription-box">${rec.transcription || 'Geen transcriptie beschikbaar.'}</div>`;
  }

  render() {
    const r = this.recording;
    if (!r) return html``;

    const borings = this.borings || [];
    const hasBorings = borings.length > 0;
    const pm = hasBorings ? ProjectModel.fromFlat({ borings }) : null;

    if (this.boringOnly) {
      return html`
        <div @click=${(e) => e.stopPropagation()} style="padding: 0.5rem 0;">
          ${pm ? this._renderResultPanel(pm) : html`
            <div style="color:var(--mv-text-muted); font-size:0.875rem; padding:0.5rem 0;">Geen lagen gevonden.</div>
          `}
        </div>
      `;
    }

    return html`
      <div class="root" @click=${(e) => e.stopPropagation()} style="grid-template-columns:${this._leftCollapsed ? '34px 1fr' : '1fr 34px 1.6fr'};">

        <!-- Left: audio + transcription (inklapbaar) -->
        ${this._leftCollapsed ? '' : html`
        <div>
          ${r.type === 'audio' ? html`
            <audio controls preload="metadata" style="width:100%; margin-bottom:${(this.extraRecordings?.length) ? '0.5rem' : '1rem'};">
              <source src="${this._getFileUrl(r.filename)}" type="${this._getMimeType(r.filename)}">
            </audio>
          ` : ''}
          ${(this.extraRecordings || []).map((ex, i) => html`
            <div style="font-size:0.7rem; font-weight:700; letter-spacing:0.04em; color:var(--mv-primary); margin:0.35rem 0 0.2rem;">Audio ${i + 2} · ${this._domainDesc(ex.domain)}</div>
            <audio controls preload="metadata" style="width:100%; margin-bottom:${i === this.extraRecordings.length - 1 ? '1rem' : '0.5rem'};">
              <source src="${this._getFileUrl(ex.filename)}" type="${this._getMimeType(ex.filename)}">
            </audio>
          `)}

          <div class="section-label">
            <span>Transcriptie</span>
          </div>

          ${this._renderEditableTranscript(r, `Transcript 1 · ${this._domainDesc(r.domain)}`)}
          ${(this.extraRecordings || []).map((ex, i) => this._renderEditableTranscript(ex, `Transcript ${i + 2} · ${this._domainDesc(ex.domain)}`))}

          ${this._transcript(r) ? html`
            <div class="transcript-actions" style="display:flex; gap:8px; margin-top:0.75rem;">
              ${!r.parent_recording_id ? html`
                <div style="position:relative; display:inline-flex;">
                  <button class="extra-recording-btn"
                    @click=${this._addExtraRecording}
                    title="Voeg een extra audio-opname toe aan deze meting"
                    style="display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:1px solid var(--mv-primary); border-radius:8px; background:var(--mv-surface); color:var(--mv-primary); font-weight:700; font-size:0.85rem; cursor:pointer;">
                    ${unsafeSVG(lucideIcon(Plus, 14))}
                    Extra opname
                    ${unsafeSVG(lucideIcon(ChevronDown, 13))}
                  </button>
                  ${this._extraMenuOpen ? html`
                    <div @click=${() => this._extraMenuOpen = false} style="position:fixed; inset:0; z-index:49;"></div>
                    <div @click=${(e) => e.stopPropagation()} style="position:absolute; top:calc(100% + 4px); left:0; z-index:50; min-width:210px; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,0.14); overflow:hidden;">
                      <div style="padding:8px 12px 4px; font-size:0.7rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-muted);">Wat wil je toevoegen?</div>
                      ${RRD_EXTRA_DOMAINS.map(d => html`
                        <button @click=${(e) => this._pickExtraDomain(d.value, e)}
                          style="display:block; width:100%; text-align:left; padding:9px 12px; border:none; background:none; font-size:0.85rem; color:var(--mv-text); cursor:pointer; font-family:inherit;"
                          @mouseenter=${(e) => e.currentTarget.style.background = 'var(--mv-surface-alt, #f3f4f6)'}
                          @mouseleave=${(e) => e.currentTarget.style.background = 'none'}>${d.label}</button>`)}
                    </div>` : ''}
                </div>
                <input id="extra-file-input" type="file" accept="audio/*" style="display:none" @change=${this._onExtraFileSelected} />
              ` : ''}
              <button class="reprocess-btn"
                ?disabled=${['transcribing', 'processing', 'syncing'].includes(r.status)}
                @click=${this._reprocessTranscript}
                title="Verwerk het transcript opnieuw met AI"
                style="display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface); color:var(--mv-text); font-weight:600; font-size:0.85rem; cursor:pointer;">
                <span style="display:inline-flex; ${['transcribing', 'processing', 'syncing'].includes(r.status) ? 'animation:rotate 1s linear infinite;' : ''}">${unsafeSVG(lucideIcon(RefreshCw, 14))}</span>
                Opnieuw verwerken
              </button>
              ${this._renderStandardTile()}
            </div>
          ` : ''}
        </div>
        `}

        <!-- Midden: verticale knop om het opname/transcript-paneel in of uit te klappen -->
        <div title=${this._leftCollapsed ? 'Toon opname & transcript' : 'Verberg opname & transcript'}
          @click=${() => this._toggleLeftCollapsed()}
          style="display:flex; align-items:stretch; justify-content:center; cursor:pointer;">
          <div style="display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; width:28px; padding:10px 0; border:1px solid var(--mv-border); border-radius:8px; background:var(--mv-surface); color:var(--mv-text-muted);">
            ${unsafeSVG(lucideIcon(this._leftCollapsed ? ChevronRight : ChevronLeft, 15))}
            <span style="writing-mode:vertical-rl; transform:rotate(180deg); font-size:0.72rem; font-weight:700; letter-spacing:0.04em; white-space:nowrap; text-transform:uppercase;">
              ${this._leftCollapsed ? 'Toon audio' : 'Verberg audio'}
            </span>
          </div>
        </div>

        <!-- Right: extracted result data -->
        <div>
          ${['transcribing', 'processing', 'syncing'].includes(r.status) ? html`
            <div class="processing-placeholder">
              <div class="spin-icon" style="color:#FFA800;">${unsafeSVG(lucideIcon(Loader2, 32))}</div>
              <div style="font-size:0.95rem; font-weight:700; color:var(--mv-text-sub);">AI Analyse loopt...</div>
              <div style="font-size:0.85rem; color:var(--mv-text-muted); max-width:280px; line-height:1.5;">
                We genereren de transcriptie en extraheren de bodemlagen en meetpunten.
              </div>
              <div style="width:100%; max-width:240px; display:flex; flex-direction:column; gap:8px;">
                <div class="skeleton-bar"></div>
                <div class="skeleton-bar" style="width:70%;"></div>
                <div class="skeleton-bar" style="width:85%;"></div>
              </div>
            </div>
          ` : pm ? this._renderResultPanel(pm) : html`
            <div style="color:var(--mv-text-muted); font-size:0.875rem; padding:0.5rem 0;">
              ${r.status === 'processed' ? 'Geen data gevonden.'
                : r.status === 'error' && preventieVoorGebruiker(r.error) ? 'Geen boorprofiel gemaakt: er werd (bijna) niets gezegd in deze opname.'
                : 'Nog niet verwerkt.'}
            </div>
          `}
        </div>
      </div>
    `;
  }

  _renderResultPanel(projectModel) {
    const { meetpunten } = projectModel;
    if (!meetpunten.length) {
      return html`<p style="color:var(--mv-text-muted); font-size:0.875rem;">Geen meetpunten gevonden.</p>`;
    }
    return html`
      <div>
        ${meetpunten.map(mp => this._renderBoringCard(mp))}
      </div>
    `;
  }

  _renderBoringCard(mp) {
    const boringNaam   = mp.name || 'Naamloos';
    const mpLagen      = mp.layers || [];
    const mpMonsters   = mp.monsters || [];
    const rawBoring    = (this.borings || []).find(b => b.id === mp.guid);
    const mpPeilbuizen = rawBoring?.peilbuizen || [];
    // `onlyTabs` beperkt de zichtbare tabbladen (validatie: één model = één tabblad, zodat
    // je niet langs vijf lege tabs hoeft te klikken). Leeg/afwezig = alles tonen.
    const allowTab     = (id) => !this.onlyTabs?.length || this.onlyTabs.includes(id);
    const defaultTab   = allowTab('lagen') ? 'lagen' : this.onlyTabs[0];
    const storedTab    = (this._boringTab || {})[mp.guid];
    const activeTab    = storedTab && allowTab(storedTab) ? storedTab : defaultTab;
    const setTab       = (tab) => { this._boringTab = { ...this._boringTab, [mp.guid]: tab }; };
    const _hb          = this._hlBoring(mp); // diff highlight (validation)
    const scanData     = this._getScanData(rawBoring);
    // monsters are already scoped to this boring via boring_id; legacy scan_data uses boreholeName
    const boreholeJars = rawBoring?.monsters?.length
      ? scanData
      : scanData.filter(j => j.boreholeName === boringNaam);
    const potjesCount  = boreholeJars.length;
    const afwWarnings  = this._afwWarnings(mp, mpLagen, mpPeilbuizen); // niet-blokkerend, alleen 6693

    /* ── Meta items ── */
    const metaItems = [];
    if (mp.type)        metaItems.push({ label: 'Type',         value: mp.type });
    if (mp.boormeester) metaItems.push({ label: 'Boormeester',  value: mp.boormeester });
    if (mp.groundLevelType) metaItems.push({ label: 'Maaiveldtype', value: nen6693.maaiveldtypeLabel(mp.groundLevelType) || mp.groundLevelType });
    if (mp.gwsFound != null) metaItems.push({ label: 'Grondwater aangetroffen', value: mp.gwsFound ? 'Ja' : 'Nee' });
    if (mp.gws != null) metaItems.push({ label: 'GWS',          value: `${mp.gws} cm mv` });
    if (mp.coordinates) {
      // Stored as WGS84 "lon,lat"; show it in the project's coordinate system.
      const sys = COORDINATE_SYSTEMS[this.coordinateSystem] ? this.coordinateSystem : DEFAULT_COORDINATE_SYSTEM;
      const [lonS, latS] = String(mp.coordinates).split(',');
      const lon = parseFloat(lonS), lat = parseFloat(latS);
      const conv = (Number.isFinite(lon) && Number.isFinite(lat)) ? convertFromWgs84(lon, lat, sys) : null;
      const def = COORDINATE_SYSTEMS[sys];
      const value = conv
        ? `${conv.x.toFixed(def.decimals)}, ${conv.y.toFixed(def.decimals)} (${def.label})`
        : mp.coordinates;
      metaItems.push({ label: 'Coördinaten', value });
    }
    if (rawBoring?.z != null) {
      // z is the ground-level height already reduced to the project's datum (NAP) — show verbatim.
      const datum = HEIGHT_DATUMS[this.heightDatum] ? this.heightDatum : DEFAULT_HEIGHT_DATUM;
      metaItems.push({ label: 'Maaiveldhoogte', value: formatHeight(rawBoring.z, datum) });
    }
    if (rawBoring?.referencePlane) {
      const rp = REFERENCE_PLANES[rawBoring.referencePlane];
      if (rp) metaItems.push({ label: 'Referentievlak', value: rp.label });
    }

    const hasUsefulData = mpLagen.length > 0 || mpMonsters.length > 0 ||
      mp.boormeester || (mp.gws != null) || mp.opmerking;

    return html`
      <div class="card">
        <!-- Empty extraction warning — only for audio borings (a manual boring is
             expected to start empty, so the "geen bodembeschrijving" hint is noise). -->
        ${!hasUsefulData && !this.boringOnly ? html`
          <div style="display:flex; align-items:flex-start; gap:10px; padding:0.6rem 0.875rem; background:var(--mv-warning-panel-bg,#FFFBEB); border:1px solid var(--mv-warning-panel-border,#FDE68A); border-radius:0.5rem; margin-bottom:0.75rem;">
            <span style="color:#D97706; flex-shrink:0; margin-top:1px;">${unsafeSVG(lucideIcon(AlertTriangle, 15))}</span>
            <div>
              <div style="font-size:0.82rem; font-weight:700; color:var(--mv-warning-text,#92400E);">Geen bruikbare data gevonden</div>
              <div style="font-size:0.78rem; color:var(--mv-warning-text-sub,#B45309); margin-top:2px; line-height:1.4;">De transcriptie bevat geen herkenbare bodembeschrijving. U kunt handmatig lagen toevoegen.</div>
            </div>
          </div>
        ` : ''}

        <!-- Card header with edit button -->
        <div class="card-header">
          <div style="display:flex; align-items:center; gap:6px; font-weight:700; font-size:0.9rem; color:var(--mv-text);">
            ${unsafeSVG(lucideIcon(FileText, 14))}
            ${this._mpEdit === mp.guid ? html`
              <input type="text" .value=${this._mpVal.name}
                @input=${e => this._setMV('name', e.target.value)}
                @click=${e => e.stopPropagation()}
                style="font-size:0.88rem;font-weight:700;padding:2px 6px;border:1px solid var(--mv-primary);border-radius:4px;font-family:inherit;background:var(--mv-surface);color:var(--mv-text);width:120px;">
            ` : (this.readonly ? boringNaam : html`
              <!-- Hernoemen begint waar je het verwacht: op de naam zelf. Dit opent dezelfde
                   bewerkmodus als de knop rechts, zodat er maar één opslagpad blijft --
                   inclusief de hernoem-cascade over de grondmonsters. -->
              <span title="Klik om te hernoemen"
                @click=${e => this._startMpEdit(e, mp.guid)}
                style="cursor:text; border-bottom:1px dashed transparent; transition:border-color .12s;"
                @mouseenter=${e => { e.currentTarget.style.borderBottomColor = 'var(--mv-text-muted,#9aa0a6)'; }}
                @mouseleave=${e => { e.currentTarget.style.borderBottomColor = 'transparent'; }}>${boringNaam}</span>`)}
          </div>
          <div style="display:flex;align-items:center;gap:8px;">
            ${this.readonly ? '' : this._autosaveStatus(this._boringSaveStatus(mp.guid, activeTab))}
            <span style="font-size:0.75rem; color:var(--mv-text-muted); font-weight:600;">
              ${mpLagen.length} lagen${potjesCount > 0 ? ` · ${potjesCount} grondmonsters` : ''}${mpPeilbuizen.length > 0 ? ` · ${mpPeilbuizen.length} pb` : ''}${(rawBoring?.photos || []).length ? ` · ${rawBoring.photos.length} foto's` : ''}
            </span>
            ${this.showMeta ? html`
            <button class="edit-link" title="Kopgegevens bekijken" style="font-size:0.75rem; display:inline-flex; align-items:center; ${_hb?.meta ? 'color:var(--mv-danger,#f1416c);' : ''}"
              @click=${e => { e.stopPropagation(); this._metaView = this._metaView === mp.guid ? null : mp.guid; }}>
              ${unsafeSVG(lucideIcon(Eye, 13))}
            </button>` : ''}
            ${this.readonly ? '' : html`
            <button class="edit-link" style="font-size:0.75rem;"
              @click=${e => this._startMpEdit(e, mp.guid)}>
              ${unsafeSVG(lucideIcon(this._mpEdit === mp.guid ? X : Edit2, 12))}
              ${this._mpEdit === mp.guid ? 'Sluiten' : 'Bewerken'}
            </button>`}
          </div>
        </div>

        ${this._metaView === mp.guid ? this._renderBoringMetaModal(mp, rawBoring, boringNaam, _hb) : ''}

        <!-- Boring edit accordion -->
        ${this._mpEdit === mp.guid
          ? this._renderBoringEditForm(mp.guid)
          : this._renderBoringMeta(metaItems, mp.opmerking, _hb?.meta)
        }

        <!-- Tab bar -->
        <div style="display:flex; border-bottom:1px solid var(--mv-border); background:var(--mv-surface);">
          ${[
            { id: 'lagen',      label: 'Lagen',      count: mpLagen.length },
            { id: 'potjes',     label: 'Grondmonsters', count: potjesCount },
            { id: 'peilbuizen', label: 'Peilbuis', count: mpPeilbuizen.length },
            { id: 'watermonster', label: 'Watermonsters', count: (rawBoring?.watermonsters || []).length },
            { id: 'afwerking',  label: 'Afwerking',  count: this._afwOf(mp).fillings.length, warn: afwWarnings.length > 0 },
            { id: 'fotos',      label: "Foto's",     count: (rawBoring?.photos || []).length },
            { id: 'boorprofiel', label: 'Boorprofiel', count: null, beta: true },
          ].filter(t => allowTab(t.id)).map(t => {
            const hlTab = _hb?.tabs?.has(t.id);
            const amber = t.warn && !hlTab; // gele afwerking-waarschuwing (niet-blokkerend)
            return html`
            <button @click=${(e) => { e.stopPropagation(); setTab(t.id); }}
              style="background:none; border:none; border-bottom:2px solid ${activeTab === t.id ? 'var(--mv-primary)' : 'transparent'}; padding:6px 12px; font-size:0.75rem; font-weight:700; cursor:pointer; color:${hlTab ? '#f1416c' : amber ? '#B45309' : activeTab === t.id ? 'var(--mv-primary)' : 'var(--mv-text-muted)'}; display:flex; align-items:center; gap:4px; white-space:nowrap;">
              ${t.label}${(hlTab || amber) ? ' ⚠' : ''}
              ${t.beta ? html`<span style="background:var(--mv-primary-tint,#e9f5e6); color:var(--mv-primary); font-size:0.58rem; font-weight:800; text-transform:uppercase; letter-spacing:0.05em; border-radius:4px; padding:1px 5px;">Beta</span>` : ''}
              ${t.count != null ? html`<span style="background:${hlTab ? '#f1416c1f' : amber ? '#FEF3C7' : 'var(--mv-surface-alt)'}; color:${hlTab ? '#f1416c' : amber ? '#B45309' : 'var(--mv-text-muted)'}; font-size:0.65rem; font-weight:700; border-radius:99px; padding:0 5px; min-width:16px; text-align:center;">${t.count}</span>` : ''}
            </button>
          `; })}
        </div>

        <!-- Tab content -->
        <div style="flex-grow:1; min-width:0; display:flex; flex-direction:column;">
          ${activeTab === 'lagen'
            ? this._renderLagenTab(mp, mpLagen, mpMonsters)
            : activeTab === 'potjes'
              ? this._renderPotjesTab(boreholeJars, rawBoring, boringNaam)
              : activeTab === 'peilbuizen'
                ? this._renderPeilbuisTab(mp.guid, mpPeilbuizen)
                : activeTab === 'watermonster'
                  ? this._renderWatermonsterTab(mp.guid, mpPeilbuizen)
                  : activeTab === 'fotos'
                    ? html`<boring-photos
                        .boringId=${mp.guid}
                        .boring=${rawBoring}
                        .photos=${rawBoring?.photos || []}
                        ?readonly=${this.readonly}
                        @photos-changed=${() => this.dispatchEvent(new CustomEvent('borings-changed', { bubbles: true, composed: true }))}
                      ></boring-photos>`
                    : activeTab === 'boorprofiel'
                      ? this._renderBoorprofielTab(mp, mpLagen, mpPeilbuizen)
                      : this._renderAfwerkingTab(mp, mpLagen, mpPeilbuizen, afwWarnings)
          }
        </div>
      </div>
    `;
  }

  _renderBoringMeta(metaItems, opmerking, hlMeta = false) {
    return html`
      ${hlMeta ? html`
        <div style="display:flex; align-items:center; gap:6px; padding:5px 12px; font-size:0.78rem; font-weight:600; color:#f1416c; background:#f1416c14; border-bottom:1px solid var(--mv-border);">
          ${unsafeSVG(lucideIcon(AlertTriangle, 12))}
          <span>Kopgegevens (type / boormeester / gws / opmerking) wijken af van verwacht.</span>
        </div>` : ''}
      ${metaItems.length ? html`
        <div style="display:grid; grid-template-columns:repeat(auto-fill, minmax(140px, 1fr)); gap:4px 12px; padding:0.6rem 0.9rem; border-bottom:1px solid var(--mv-border); background:var(--mv-surface-alt);">
          ${metaItems.map(({ label, value }) => html`
            <div style="display:flex; flex-direction:column; gap:1px;">
              <span style="font-size:0.65rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--mv-text-muted);">${label}</span>
              <span style="font-size:0.8rem; font-weight:600; color:var(--mv-text-sub); word-break:break-word;">${value}</span>
            </div>
          `)}
        </div>
      ` : ''}
      ${opmerking ? html`
        <div style="display:flex; align-items:flex-start; gap:6px; padding:0.4rem 0.9rem; border-bottom:1px solid var(--mv-border); font-size:0.8rem; color:var(--mv-text-sub);">
          <span style="flex-shrink:0; color:var(--mv-text-muted); margin-top:1px;">${unsafeSVG(lucideIcon(MessageSquare, 13))}</span>
          <span style="font-style:italic;">${opmerking}</span>
        </div>
      ` : ''}
    `;
  }

  /**
   * Popup met álle kopgegevens van een boring (read-only). In de validatiepagina worden
   * afwijkende velden rood gemarkeerd via de diff (hb.metaFields).
   */
  _renderBoringMetaModal(mp, rawBoring, displayName, hb) {
    const badSet = hb?.metaFields || null;
    const bad = (...keys) => !!badSet && keys.some(k => badSet.has(k));

    const refLabel = rawBoring?.referencePlane ? (REFERENCE_PLANES[rawBoring.referencePlane]?.label || rawBoring.referencePlane) : null;
    const mvType = mp.groundLevelType ? (nen6693.maaiveldtypeLabel(mp.groundLevelType) || mp.groundLevelType) : null;
    const drillLabel = rawBoring?.drillingSystem ? (nen6693.boorsysteemLabel(rawBoring.drillingSystem) || rawBoring.drillingSystem) : null;
    let stopLabel = null;
    if (rawBoring?.stopReason) { for (const [l, c] of tiCodelists.stopCriterium) { if (c === rawBoring.stopReason) { stopLabel = l; break; } } if (!stopLabel) stopLabel = rawBoring.stopReason; }
    let zVal = null;
    if (rawBoring?.z != null) { const datum = HEIGHT_DATUMS[this.heightDatum] ? this.heightDatum : DEFAULT_HEIGHT_DATUM; zVal = formatHeight(rawBoring.z, datum); }
    let coordVal = null;
    if (mp.coordinates) {
      const sys = COORDINATE_SYSTEMS[this.coordinateSystem] ? this.coordinateSystem : DEFAULT_COORDINATE_SYSTEM;
      const [lonS, latS] = String(mp.coordinates).split(',');
      const lon = parseFloat(lonS), lat = parseFloat(latS);
      const conv = (Number.isFinite(lon) && Number.isFinite(lat)) ? convertFromWgs84(lon, lat, sys) : null;
      const def = COORDINATE_SYSTEMS[sys];
      coordVal = conv ? `${conv.x.toFixed(def.decimals)}, ${conv.y.toFixed(def.decimals)} (${def.label})` : mp.coordinates;
    }

    // Zeefmassa's: percentages erbij, want los zeggen 38,47 en 5,47 kg weinig. Ze staan in
    // dezelfde regel als de massa zelf zodat de popup niet drie regels langer wordt voor
    // getallen die uit elkaar volgen.
    const massa = massFractions({
      massTotalKg: rawBoring?.massTotalKg, massGt20Kg: rawBoring?.massGt20Kg, massLt20Kg: rawBoring?.massLt20Kg,
    });
    const massaRegel = (waarde, pct) => (waarde == null ? null
      : `${Number(waarde).toLocaleString('nl-NL')} kg${pct != null ? ` (${pct.toLocaleString('nl-NL')}%)` : ''}`);
    const sleuf = (n) => (n == null ? null : `${Number(n).toLocaleString('nl-NL')} m`);

    const rows = [
      { label: 'Naam boring', value: displayName, keys: ['name'] },
      { label: 'Type onderzoek', value: getBoringTypeLabel(mp.type), keys: ['type'] },
      { label: 'Sleuflengte', value: sleuf(rawBoring?.trialPitLengthM), keys: ['trialPitLengthM'] },
      { label: 'Sleufbreedte', value: sleuf(rawBoring?.trialPitWidthM), keys: ['trialPitWidthM'] },
      { label: 'Totale massa', value: massa.effectiveTotalKg == null ? null
          : `${massa.effectiveTotalKg.toLocaleString('nl-NL')} kg${massa.overridden ? ' (ingevuld)' : ''}`, keys: ['massTotalKg'] },
      { label: 'Massa > 20 mm', value: massaRegel(rawBoring?.massGt20Kg, massa.gt20Pct), keys: ['massGt20Kg'] },
      { label: 'Massa < 20 mm', value: massaRegel(rawBoring?.massLt20Kg, massa.lt20Pct), keys: ['massLt20Kg'] },
      { label: 'Boormeester', value: mp.boormeester, keys: ['boormeester'] },
      { label: 'Referentievlak', value: refLabel, keys: ['referencePlane'] },
      { label: 'Maaiveldtype', value: mvType, keys: ['groundLevelType'] },
      { label: 'Maaiveldhoogte', value: zVal, keys: ['z'] },
      { label: 'Grondwater aangetroffen', value: mp.gwsFound == null ? null : (mp.gwsFound ? 'Ja' : 'Nee'), keys: ['gwsFound'] },
      { label: 'Diepte grondwaterstand', value: mp.gws != null ? `${mp.gws} cm mv` : null, keys: ['gwsCmMv'] },
      { label: 'Reden boring gestopt', value: stopLabel, keys: ['stopReason'] },
      { label: 'Boorsysteem', value: drillLabel, keys: ['drillingSystem'] },
      { label: 'Coördinaten', value: coordVal, keys: ['x', 'y'] },
      { label: 'Opmerking', value: mp.opmerking, keys: ['opmerking'] },
    ];

    return html`
      <div @click=${e => { e.stopPropagation(); this._metaView = null; }}
        style="position:fixed; inset:0; background:rgba(0,0,0,0.35); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px;">
        <div @click=${e => e.stopPropagation()}
          style="background:var(--mv-surface,#fff); border-radius:12px; width:100%; max-width:520px; max-height:85vh; overflow:auto; box-shadow:0 20px 60px rgba(0,0,0,0.25);">
          <div style="display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid var(--mv-border);">
            <h3 style="margin:0; font-size:1.05rem; color:var(--mv-text);">Kopgegevens — ${displayName}</h3>
            <button @click=${() => this._metaView = null} style="border:none; background:none; cursor:pointer; color:var(--mv-text-muted); font-size:1.1rem; line-height:1; padding:0;">✕</button>
          </div>
          ${hb?.meta ? html`
            <div style="display:flex; align-items:center; gap:6px; padding:8px 20px; font-size:0.8rem; font-weight:600; color:#f1416c; background:#f1416c14;">
              ${unsafeSVG(lucideIcon(AlertTriangle, 13))}<span>Rood gemarkeerde velden wijken af van het verwachte resultaat.</span>
            </div>` : ''}
          <div style="padding:8px 20px 18px;">
            ${rows.map(r => { const isBad = bad(...r.keys); return html`
              <div style="display:flex; justify-content:space-between; gap:16px; padding:8px 0; border-bottom:1px solid var(--mv-border);">
                <span style="font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-muted); flex-shrink:0; min-width:150px;">${r.label}</span>
                <span style="font-size:0.85rem; font-weight:600; text-align:right; word-break:break-word; ${isBad ? 'color:#f1416c; background:#f1416c14; padding:1px 6px; border-radius:4px;' : 'color:var(--mv-text);'}">
                  ${(r.value == null || r.value === '') ? html`<span style="color:var(--mv-text-muted); font-weight:400;">—</span>` : r.value}
                </span>
              </div>`; })}
          </div>
        </div>
      </div>`;
  }

  // ── Afwerking (boorgat dichten) ──────────────────────────────────────────
  /** Werkkopie (of opgeslagen waarde) van de afwerking van een boring. Pure read.
   *  Val terug op de platte boring uit `this.borings` (die de optimistische save
   *  bijwerkt) — NIET op `mp.afwerking`: het meetpunt uit ProjectModel.fromFlat
   *  draagt de afwerking niet mee, waardoor de tab na opslaan leeg leek. */
  _afwNormalize(a) {
    a = a || {};
    // Back-compat: oude 'casings' → nieuwe 'achtergelaten object'-vorm (type CAS).
    const objs = a.achtergelatenObjecten || (a.casings || []).map(c => ({
      // leftInGround 'ja' = in de grond gebleven → 'achtergebleven' (stond omgekeerd).
      aard: c.leftInGround === 'ja' ? 'achtergebleven' : (c.leftInGround === 'nee' ? 'geplaatst' : ''),
      type: 'CAS', van: c.van, tot: c.tot, materiaal: '', opmerking: c.opmerking || '', verwijderdOp: '',
    }));
    return { fillings: a.fillings || [], achtergelatenObjecten: objs, topCompletions: a.topCompletions || [] };
  }
  /** Werkkopie (of, bij afwezigheid, de opgeslagen afwerking) van een boring. */
  _afwOf(mp) {
    const raw = (this.borings || []).find(b => b.id === mp.guid);
    return this._afwNormalize(this._afwVal?.[mp.guid] || raw?.afwerking || {});
  }
  /** Alléén de opgeslagen afwerking — negeert de niet-opgeslagen werkkopie.
   *  Gebruikt voor waarschuwingen, zodat die pas na opslaan verschijnen. */
  _afwSaved(mp) {
    const raw = (this.borings || []).find(b => b.id === mp.guid);
    return this._afwNormalize(raw?.afwerking || {});
  }
  /**
   * Beslisregels voor de afwerking als NIET-blokkerende waarschuwingen. Gelden alleen bij
   * NEN 6693 (bij 5104 leeg). Blokkeert niets — puur signalering, zoals bij vergeten laagvelden.
   */
  _afwWarnings(mp, mpLagen, mpPeilbuizen) {
    if (this._is5104()) return [];
    // Alléén de opgeslagen afwerking beoordelen — niet de niet-opgeslagen werkkopie,
    // zodat een net toegevoegd (nog leeg) object geen directe waarschuwing geeft.
    const afw = this._afwSaved(mp);
    const w = [];
    const boordiepte = (mpLagen || []).reduce((m, l) => Math.max(m, Number(l.lowerDepth ?? l.depth_to ?? 0) || 0), 0);
    const hasPeilbuis = (mpPeilbuizen || []).length > 0;
    const BENTO = ['VUL_BK', 'VUL_BP', 'VUL_BS'];
    // Volledige trajecten (beide diepten ingevuld), gesorteerd van boven (klein) naar onder (groot).
    const F = afw.fillings
      .filter(r => r.van !== '' && r.van != null && r.tot !== '' && r.tot != null)
      .map(r => ({ van: Number(r.van), tot: Number(r.tot), mat: r.materiaal }));
    const sorted = [...F].sort((a, b) => a.van - b.van);

    if (afw.fillings.length === 0) w.push('De NEN 6693 verplicht je de vulling van het boorgat op te geven. Automatiseer dit door gebruik te maken van een sjabloon.');
    for (const r of F) if (r.van >= r.tot) w.push(`Vultraject ${r.van}–${r.tot} cm: 'van' moet kleiner zijn dan 'tot'.`);
    // Achtergelaten objecten — NEN eist wát er ligt + van/tot (om te kunnen tekenen).
    for (const o of afw.achtergelatenObjecten) {
      if (!o.type || o.van === '' || o.van == null || o.tot === '' || o.tot == null) {
        w.push('Achtergelaten object: type, van of tot ontbreekt.');
      }
      if (o.aard === 'achtergebleven' && !(o.opmerking || '').trim()) {
        w.push('Onbedoeld achtergebleven object zonder opmerking — beschrijf wat er ligt.');
      }
      if (o.tot !== '' && o.tot != null && boordiepte && Number(o.tot) > boordiepte) {
        w.push(`Achtergelaten object tot ${o.tot} cm valt buiten de boordiepte (${boordiepte} cm).`);
      }
    }

    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1], cur = sorted[i];
      if (cur.van < prev.tot) w.push(`Vultrajecten overlappen rond ${cur.van} cm.`);
      else if (cur.van > prev.tot) w.push(`Gat tussen de vultrajecten: ${prev.tot}–${cur.van} cm is niet opgevuld.`);
    }
    if (F.length && boordiepte) {
      const deepest = Math.max(...F.map(r => r.tot));
      if (deepest !== boordiepte) w.push(`Onderste vultraject eindigt op ${deepest} cm, maar de boordiepte is ${boordiepte} cm.`);
    }

    const grind = F.filter(r => r.mat === 'VUL_FG');
    const bento = F.filter(r => BENTO.includes(r.mat));
    if (grind.length && bento.length) {
      const grindTop = Math.min(...grind.map(r => r.van));
      if (!bento.some(b => b.tot === grindTop)) w.push('Bentoniettraject sluit niet direct aan op het filtergrind (Protocol 2001 §13.2).');
    }
    if (bento.some(b => b.van === 0) && afw.topCompletions.length) w.push('Bentoniet reikt tot maaiveld terwijl er ook een bovenafwerking staat — meestal zit daar nog iets tussen.');
    if (hasPeilbuis && afw.fillings.length && !grind.length) w.push('Peilbuis geplaatst, maar geen filtergrind in de vulling — klopt dit (bijv. vooromstort)?');
    if (hasPeilbuis && afw.topCompletions.length === 0) w.push('Peilbuis geplaatst, maar nog geen bovenafwerking opgegeven.');
    for (const t of afw.topCompletions) if (t.type === 'BOV_AN' && !(t.merk || '').trim()) w.push("Bovenafwerking 'Anders' zonder merk/type — vul dit aan.");

    return w;
  }

  _afwMutate(mp, fn) {
    const cur = this._afwOf(mp);
    const next = {
      fillings: cur.fillings.map(r => ({ ...r })),
      achtergelatenObjecten: cur.achtergelatenObjecten.map(r => ({ ...r })),
      topCompletions: cur.topCompletions.map(r => ({ ...r })),
    };
    fn(next);
    this._afwVal = { ...this._afwVal, [mp.guid]: next };
    this._afwDirty = { ...this._afwDirty, [mp.guid]: true };
    this._afwSaveSchedule(mp); // autosave (debounced)
  }
  _afwSaveSchedule(mp) {
    const guid = mp.guid;
    this._afwSaveState = { ...this._afwSaveState, [guid]: 'saving' };
    if (!this._afwSaveTimers) this._afwSaveTimers = {};
    clearTimeout(this._afwSaveTimers[guid]);
    this._afwSaveTimers[guid] = setTimeout(() => this._afwSaveNow(mp), 700);
  }
  _afwSaveNow(mp) {
    const guid = mp.guid;
    const cur = this._afwOf(mp);
    const num = (x) => (x === '' || x == null) ? null : Number(x);
    const next = {
      fillings: cur.fillings.map(r => ({ van: num(r.van), tot: num(r.tot), materiaal: r.materiaal || null })),
      achtergelatenObjecten: cur.achtergelatenObjecten.map(r => ({ aard: r.aard || null, type: r.type || null, van: num(r.van), tot: num(r.tot), materiaal: r.materiaal || null, opmerking: r.opmerking || null, verwijderdOp: r.verwijderdOp || null })),
      topCompletions: cur.topCompletions.map(r => ({ type: r.type || null, materiaal: r.materiaal || null, heightCm: num(r.heightCm), afsluitbaar: r.afsluitbaar || null, vloeistofdicht: r.vloeistofdicht || null, afmeting: r.afmeting || null, merk: r.merk || null })),
    };
    this.borings = (this.borings || []).map(b => b.id === guid ? { ...b, afwerking: next } : b);
    this._afwSaveState = { ...this._afwSaveState, [guid]: 'saving' };
    Promise.resolve(this._crud.updateBoring(guid, { afwerking: next }))
      .then(() => { this._afwSaveState = { ...this._afwSaveState, [guid]: 'saved' }; })
      .catch(() => { this._afwSaveState = { ...this._afwSaveState, [guid]: 'error' }; });
  }
  _afwAdd(mp, block) {
    const blank = block === 'fillings' ? { van: '', tot: '', materiaal: '' }
      : block === 'achtergelatenObjecten' ? { aard: 'geplaatst', type: '', van: '', tot: '', materiaal: '', opmerking: '', verwijderdOp: '' }
        : { type: '', materiaal: '', afsluitbaar: '', vloeistofdicht: '', afmeting: '', merk: '' };
    this._afwMutate(mp, a => a[block].push(blank));
  }
  _afwUpd(mp, block, i, field, value) { this._afwMutate(mp, a => { if (a[block][i]) a[block][i][field] = value; }); }
  _afwDel(mp, block, i) { this._afwMutate(mp, a => a[block].splice(i, 1)); }
  /** Waarschuw als een snelle optie bestaande vultrajecten zou overschrijven. */
  async _afwConfirmOverwrite(mp) {
    if (!this._afwOf(mp).fillings.length) return true;
    return confirmDialog({
      title: 'Vulling overschrijven?',
      message: 'Er staat al een vulling voor dit boorgat. Deze snelle optie vervangt de bestaande vultrajecten. Doorgaan?',
      confirmLabel: 'Overschrijven',
      danger: true,
    });
  }

  /** Snelknop: gat gedicht met uitkomende grond, van 0 tot de boordiepte. Slaat direct op. */
  async _afwQuickFill(mp, boordiepte) {
    if (!await this._afwConfirmOverwrite(mp)) return;
    this._afwMutate(mp, a => { a.fillings = [{ van: 0, tot: boordiepte || '', materiaal: 'VUL_UG', opmerking: '' }]; });
  }

  /** Vulling opbouwen uit het sjabloon-afwerkingsprofiel (percentages → cm o.b.v. boordiepte). Slaat direct op. */
  async _afwFromTemplate(mp, boordiepte, hasPeilbuis) {
    const afwT = this.templateDefaults?.afwerking;
    const profile = (hasPeilbuis ? afwT?.metPeilbuis : afwT?.zonderPeilbuis) || [];
    const fillings = nen6693.afwerkingFillingsFromProfile(profile, boordiepte).map(f => ({ van: f.van, tot: f.tot, materiaal: f.materiaal, opmerking: '' }));
    if (!fillings.length) return;
    if (!await this._afwConfirmOverwrite(mp)) return;
    this._afwMutate(mp, a => { a.fillings = fillings; });
  }
  _afwReset(mp) {
    const nv = { ...this._afwVal }; delete nv[mp.guid]; this._afwVal = nv;
    const nd = { ...this._afwDirty }; delete nd[mp.guid]; this._afwDirty = nd;
  }
  async _saveAfwerking(mp) {
    const guid = mp.guid;
    const cur = this._afwOf(mp);
    const num = (x) => (x === '' || x == null) ? null : Number(x);
    const next = {
      fillings: cur.fillings.map(r => ({ van: num(r.van), tot: num(r.tot), materiaal: r.materiaal || null })),
      achtergelatenObjecten: cur.achtergelatenObjecten.map(r => ({ aard: r.aard || null, type: r.type || null, van: num(r.van), tot: num(r.tot), materiaal: r.materiaal || null, opmerking: r.opmerking || null, verwijderdOp: r.verwijderdOp || null })),
      topCompletions: cur.topCompletions.map(r => ({ type: r.type || null, materiaal: r.materiaal || null, afsluitbaar: r.afsluitbaar || null, vloeistofdicht: r.vloeistofdicht || null, afmeting: r.afmeting || null, merk: r.merk || null })),
    };
    const nd = { ...this._afwDirty }; delete nd[guid]; this._afwDirty = nd;
    const nv = { ...this._afwVal }; delete nv[guid]; this._afwVal = nv;
    this._optimistic(
      bs => bs.map(b => b.id === guid ? { ...b, afwerking: next } : b),
      () => this._crud.updateBoring(guid, { afwerking: next }),
    );
  }

  /** Boorprofiel-tab — SVG-tekening, automatisch opgebouwd uit lagen + afwerking +
   *  peilbuizen (NEN 6693 hoofdstuk 13). Read-only weergave. */
  _renderBoorprofielTab(mp, mpLagen, mpPeilbuizen) {
    if (!mpLagen.length) {
      return html`<div style="padding:1rem 0.9rem; color:var(--mv-text-muted); font-size:0.85rem;">Nog geen lagen — voeg lagen toe om het boorprofiel te tekenen.</div>`;
    }
    const raw = (this.borings || []).find(b => b.id === mp.guid);
    const afw = this._afwOf(mp);
    const datum = HEIGHT_DATUMS[this.heightDatum] ? this.heightDatum : DEFAULT_HEIGHT_DATUM;
    const rp = raw?.referencePlane ? REFERENCE_PLANES[raw.referencePlane]?.label : null;

    // Zelfde standaard als de labels hieronder (_is5104), zodat code en tekening dezelfde lijst lezen.
    const standard = this._is5104() ? 'nen5104' : 'nen6693';
    const layers = mpLagen.map(l => {
      // Bijmengingen als volledige term ('siltig'/'grindig'/'kleiig') resolven — NEN 6693
      // eerst, met TI-terugval voor 5104. Zo classificeert het boorprofiel de textuur
      // per bijmenging correct (shortcodes als 'gr'/'k' vielen anders terug op silt).
      const admix = [
        [l.toevoeging1, l.gradatie1],
        [l.toevoeging2, l.gradatie2],
        [l.toevoeging3, l.gradatie3],
      ].filter(([t]) => t).map(([t, g]) => ({
        label: this._nenName(t, [this._N.BIJMENGING], tiCodelists.toevoeging),
        grad:  g ? this._nenName(g, [this._N.INTENSITEIT], tiCodelists.gradatie) : '',
      }));
      const line2 = [
        (mediaanApplies(l.soilType) && l.mediaanLabel) ? `mediaan ${l.mediaanLabel}` : '',
        l.kleurLabel || '',
        (l.bzbLabels || []).join(', '),
        l.opmerking || '',
      ].filter(Boolean).join(' · ');
      // De hoofdnaam moet langs dezelfde standaard-resolutie als de bijmengingen hierboven.
      // 'S' betekent in NEN 6693 SILT en in de SIKB-lijst SLIB, en `l.soilLabel` kent alleen
      // die tweede. Een siltlaag heette daardoor 'slib' in de tekening -- met het bijbehorende
      // patroon, terwijl de lagentabel ernaast gewoon 'silt' zei.
      const soilLabel = this._is5104()
        ? (l.soilLabel || '')
        : (this._nenName(l.soilType, [this._N.GRONDSOORT, this._N.LAAGTYPE_SPECIAAL, this._N.GESTEENTE], tiCodelists.bodemsoortHistorisch) || l.soilLabel || '');
      // Zie boorprofiel.js soilKind: 'zandsteen' bevat 'zand', dus de gesteentevlag moet
      // van hier komen — dit is de plek waar we nog weten uit welke lijst de code komt.
      const isRock = !!l.soilType && [...this._N.GESTEENTE.values()].includes(l.soilType);
      return {
        from: Number(l.upperDepth) || 0, to: Number(l.lowerDepth) || 0, soilLabel, admix, line2, isRock,
        soilCode: l.soilType || null, bzb: profileBzb(l, standard),
      };
    });

    const profile = {
      header: {
        name: mp.name || '',
        maaiveld: raw?.z != null ? formatHeight(raw.z, datum) : null,
        referencePlane: rp,
        boormeester: mp.boormeester || raw?.boormeester || null,
      },
      layers,
      fillings: (afw.fillings || []).map(f => ({ van: f.van, tot: f.tot, code: f.materiaal })),
      achtergelaten: (afw.achtergelatenObjecten || []).map(o => ({ van: o.van, tot: o.tot, aard: o.aard, typeLabel: this._N.achtergelatenTypeLabel(o.type) })),
      peilbuizen: (mpPeilbuizen || []).map(pb => ({ filterTop: pb.filterTopCm ?? pb.filter_from_cm ?? null, filterBottom: pb.filterBottomCm ?? pb.filter_to_cm ?? null })),
      gwsCmMv: mp.gws ?? raw?.gwsCmMv ?? null,
      boordiepteCm: Math.max(0, ...mpLagen.map(l => Number(l.lowerDepth) || 0)),
      standard,
      textures: getCompanyTextures(),
    };
    const svg = buildBoorprofielSvg(profile);

    return html`
      <div style="padding:0.75rem 0.9rem;">
        <div style="display:flex; align-items:center; gap:8px; margin-bottom:8px;">
          <div style="display:flex; align-items:center; gap:6px; flex:1; min-width:0; font-size:0.72rem; color:var(--mv-text-muted);">
            ${unsafeSVG(lucideIcon(Info, 13))}
            <span>Automatisch opgebouwd uit lagen, afwerking en peilbuizen. Bijmengingen zijn proportioneel (klasse-midden, ~10% nauwkeurig).</span>
          </div>
          <button title="Exporteer naar PDF" @click=${() => this._exportBoorprofielPdf(profile, mp.name)}
            style="flex-shrink:0; display:inline-flex; align-items:center; gap:5px; padding:5px 10px; border:1px solid var(--mv-border); border-radius:6px; background:var(--mv-surface); color:var(--mv-text); font-size:0.75rem; font-weight:600; cursor:pointer;">
            ${unsafeSVG(lucideIcon(Download, 14))} PDF
          </button>
        </div>
        <div style="overflow-x:auto; border:1px solid var(--mv-border); border-radius:8px; background:#fff;">
          ${unsafeHTML(svg)}
        </div>
      </div>
    `;
  }

  /** Download het boorprofiel als A4-PDF: dezelfde opmaak als de boorprofielen-export. */
  async _exportBoorprofielPdf(profile, name) {
    try {
      const logo = await api.getCompanyProfile().then(p => p?.logo || null, () => null);
      const doc = await buildBoorprofielenDocFromData([profile], { logo, label: profile.header?.projectCode || '' });
      doc.save(`boorprofiel-${(name || 'boring').replace(/[^\w.-]+/g, '_')}.pdf`);
    } catch (err) {
      alertDialog({ title: 'Export mislukt', message: err?.message || 'Kon het boorprofiel niet exporteren.' });
    }
  }

  /** Eén 'achtergelaten in de grond'-object (codelijst C). `aard` is de kern; opmerking
   *  is verplicht bij aard='achtergebleven'. Type-keuze zet de standaard-aard automatisch. */
  _renderAchtergelatenRow(mp, r, i, ro, num, del) {
    const upd = (f, v) => this._afwUpd(mp, 'achtergelatenObjecten', i, f, v);
    const aardBtn = (val, label, color) => { const active = r.aard === val; return html`<button type="button" ?disabled=${ro} @click=${() => upd('aard', active ? null : val)} style="flex:1; padding:5px 8px; border-radius:5px; border:1px solid ${active ? color : 'var(--mv-border)'}; background:${active ? color : 'var(--mv-surface)'}; color:${active ? '#fff' : 'var(--mv-text)'}; font-weight:700; font-size:0.8rem; font-family:inherit; box-sizing:border-box; cursor:pointer;">${label}</button>`; };
    const opmReq = r.aard === 'achtergebleven';
    return html`
      <div style="border:1px solid var(--mv-border); border-radius:8px; padding:10px; margin-bottom:8px; display:flex; flex-direction:column; gap:8px; background:transparent;">
        <div style="display:flex; gap:6px; align-items:center;">
          ${aardBtn('geplaatst', 'Bewust geplaatst', 'var(--mv-primary)')}
          ${aardBtn('achtergebleven', 'Onbedoeld achter', '#D97706')}
          ${ro ? '' : del(() => this._afwDel(mp, 'achtergelatenObjecten', i))}
        </div>
        <div style="display:grid; grid-template-columns:72px 72px minmax(0,1fr); gap:8px; align-items:end;">
          <div><div class="afw-label">Van</div><input type="number" ?disabled=${ro} .value=${num(r.van)} @input=${e => upd('van', e.target.value)}></div>
          <div><div class="afw-label">Tot</div><input type="number" ?disabled=${ro} .value=${num(r.tot)} @input=${e => upd('tot', e.target.value)}></div>
          <div><div class="afw-label">Type</div><select ?disabled=${ro} @change=${e => upd('type', e.target.value)}><option value="">— kies —</option>${nen6693.ACHTERGELATEN_TYPE.map(t => html`<option value=${t.code} ?selected=${t.code === r.type}>${t.label}</option>`)}</select></div>
        </div>
        <div style="display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1.4fr); gap:8px; align-items:end;">
          <div><div class="afw-label">Materiaal</div><input type="text" ?disabled=${ro} .value=${r.materiaal || ''} placeholder="bv. staal, pvc" @input=${e => upd('materiaal', e.target.value)}></div>
          <div><div class="afw-label">Opmerking${opmReq ? html`<span style="color:var(--mv-danger,#f1416c);"> *</span>` : ''}</div><input type="text" ?disabled=${ro} .value=${r.opmerking || ''} placeholder=${opmReq ? 'beschrijf wat er ligt' : 'optioneel'} @input=${e => upd('opmerking', e.target.value)}></div>
        </div>
      </div>`;
  }

  _renderAfwerkingTab(mp, mpLagen, mpPeilbuizen, warnings = []) {
    const ro = !!this.readonly;
    const afw = this._afwOf(mp);
    const guid = mp.guid;
    const warnOpen = !!this._afwUI?.[`${guid}:warn`];
    const boordiepte = (mpLagen || []).reduce((m, l) => Math.max(m, Number(l.lowerDepth ?? l.depth_to ?? 0) || 0), 0);
    const hasPeilbuis = (mpPeilbuizen || []).length > 0;
    const afwT = this.templateDefaults?.afwerking;
    const tplProfile = (hasPeilbuis ? afwT?.metPeilbuis : afwT?.zonderPeilbuis) || [];
    const num = (v) => (v === '' || v == null) ? '' : String(v);
    const del = (fn) => html`<button title="Verwijderen" @click=${fn} style="border:none;background:none;cursor:pointer;color:var(--mv-danger,#f1416c);font-size:1rem;line-height:1;padding:0 4px;">×</button>`;

    // Dekking van de vulling.
    const vf = afw.fillings
      .filter(r => r.van !== '' && r.van != null && r.tot !== '' && r.tot != null)
      .map(r => ({ van: Number(r.van), tot: Number(r.tot) }))
      .sort((a, b) => a.van - b.van);
    const covTop = vf.length ? Math.min(...vf.map(r => r.van)) : null;
    const covBot = vf.length ? Math.max(...vf.map(r => r.tot)) : null;
    let noGaps = true;
    for (let i = 1; i < vf.length; i++) if (vf[i].van !== vf[i - 1].tot) noGaps = false;
    const fullyCovered = !!(vf.length && boordiepte && covTop === 0 && covBot === boordiepte && noGaps);
    const barDepth = boordiepte || Math.max(1, ...vf.map(r => r.tot));
    const VUL_KLEUR = { VUL_FG: '#FAECE7', VUL_AG: '#FAECE7', VUL_FZ: '#FAEEDA', VUL_AZ: '#FAEEDA', VUL_BK: '#EAF3DE', VUL_BP: '#EAF3DE', VUL_BS: '#EAF3DE', VUL_KL: '#EAF3DE', VUL_CE: '#B4B2A9', VUL_UG: '#F1EFE8' };
    const vulBg = (code) => code === 'VUL_OP' ? 'repeating-linear-gradient(45deg, #88878055 0 4px, transparent 4px 8px)' : (VUL_KLEUR[code] || '#E3E1D9');

    const card = (inner) => html`<div style="border:1px solid var(--mv-border); border-radius:10px; padding:14px 16px; background:var(--mv-surface-alt, #F9FAFB);">${inner}</div>`;
    const pill = (text, kind) => html`<span style="font-size:0.7rem; font-weight:700; padding:2px 9px; border-radius:99px; background:${kind === 'ok' ? 'var(--mv-primary-tint,#e9f5e6)' : kind === 'warn' ? 'var(--mv-warning-panel-bg,#FEF3C7)' : 'var(--mv-surface-alt)'}; color:${kind === 'ok' ? 'var(--mv-primary)' : kind === 'warn' ? '#B45309' : 'var(--mv-text-muted)'};">${text}</span>`;
    const cardHead = (icon, title, right) => html`<div style="display:flex; align-items:center; gap:8px; margin-bottom:10px;">${icon ? unsafeSVG(lucideIcon(icon, 15)) : ''}<span style="font-weight:700; font-size:0.88rem;">${title}</span><span style="margin-left:auto;">${right}</span></div>`;

    const top = afw.topCompletions[0] || null;

    return html`
      <div class="afw-tab" style="padding:0.7rem 0.9rem; display:flex; flex-direction:column; gap:12px;">
        ${warnings.length ? html`
          <div style="border:1px solid var(--mv-warning-panel-border,#FDE68A); background:var(--mv-warning-panel-bg,#FFFBEB); border-radius:10px; padding:10px 14px;">
            <div style="display:flex; align-items:center; justify-content:space-between; cursor:pointer;" @click=${() => this._afwUI = { ...this._afwUI, [`${guid}:warn`]: !warnOpen }}>
              <span style="display:inline-flex; align-items:center; gap:8px; font-size:0.82rem; font-weight:700; color:var(--mv-warning-text,#92400E);">${unsafeSVG(lucideIcon(AlertTriangle, 15))} ${warnings.length} aandachtspunt${warnings.length === 1 ? '' : 'en'}</span>
              <span style="font-size:0.75rem; color:#B45309; font-weight:600;">${warnOpen ? 'Verberg' : 'Toon'}</span>
            </div>
            ${warnOpen ? html`<div style="margin-top:6px;">${warnings.map(w => html`<div style="font-size:0.78rem; color:var(--mv-warning-text-sub,#B45309); line-height:1.45;">• ${w}</div>`)}</div>` : ''}
          </div>` : ''}

        ${card(html`
          ${cardHead(Download, 'Vulling boorgat', vf.length ? pill(fullyCovered ? `Volledig gedekt · ${covTop} tot ${covBot} cm` : 'Gedeeltelijk gedekt', fullyCovered ? 'ok' : 'warn') : pill('Leeg', 'neutral'))}
          ${ro ? '' : html`<div style="display:flex; gap:6px; flex-wrap:wrap; margin-bottom:10px;">
            ${tplProfile.length && boordiepte ? html`<button class="afw-quick-btn" @click=${() => this._afwFromTemplate(mp, boordiepte, hasPeilbuis)}>Op basis van sjabloon</button>` : ''}
            <button class="afw-quick-btn" @click=${() => this._afwQuickFill(mp, boordiepte)}>Dichten met uitgekomen grond</button>
          </div>`}
          ${vf.length ? html`<div style="position:relative; height:8px; border-radius:4px; background:var(--mv-surface-alt,#F3F4F6); overflow:hidden; margin-bottom:10px;"
            @mousemove=${tipMove} @mouseleave=${tipHide}>
            ${afw.fillings.map(f => {
              if (f.van === '' || f.van == null || f.tot === '' || f.tot == null) return '';
              const v = Number(f.van), t = Number(f.tot);
              if (!(t > v)) return '';
              const left = Math.max(0, Math.min(100, v / barDepth * 100));
              const w = Math.max(0, Math.min(100 - left, (t - v) / barDepth * 100));
              const tip = `${v}–${t} cm · ${nen6693.vulmateriaalLabel(f.materiaal) || 'geen materiaal'}`;
              return html`<div data-tip=${tip} style="position:absolute; top:0; bottom:0; left:${left}%; width:${w}%; background:${vulBg(f.materiaal)};"></div>`;
            })}
          </div>` : ''}
          ${afw.fillings.length ? html`<div style="display:grid; grid-template-columns:72px 72px minmax(0,1fr) ${ro ? '' : '26px'}; gap:6px 8px; align-items:center;">
            <span class="afw-label">Van</span><span class="afw-label">Tot</span><span class="afw-label">Materiaal</span>${ro ? '' : html`<span></span>`}
            ${afw.fillings.map((r, i) => html`
              <input type="number" ?disabled=${ro} .value=${num(r.van)} placeholder="cm" @input=${e => this._afwUpd(mp, 'fillings', i, 'van', e.target.value)}>
              <input type="number" ?disabled=${ro} .value=${num(r.tot)} placeholder="cm" @input=${e => this._afwUpd(mp, 'fillings', i, 'tot', e.target.value)}>
              <select ?disabled=${ro} @change=${e => this._afwUpd(mp, 'fillings', i, 'materiaal', e.target.value)}><option value="">— kies —</option>${nen6693.VULMATERIAAL.map(v => html`<option value=${v.code} ?selected=${v.code === r.materiaal}>${v.label}</option>`)}</select>
              ${ro ? '' : del(() => this._afwDel(mp, 'fillings', i))}
            `)}
          </div>` : html`<div style="font-size:0.82rem; color:var(--mv-text-muted);">Nog geen vultrajecten.</div>`}
          ${ro ? '' : html`<button class="bzb-add-btn" style="margin-top:8px;" @click=${() => this._afwAdd(mp, 'fillings')}>+ Traject</button>`}
        `)}

        <!-- Bovenafwerking staat er ALTIJD: ook zonder peilbuis kan er een afdekking liggen.
             Met peilbuis is het verplicht (vandaar de waarschuwing), zonder peilbuis optioneel. -->
        ${card(html`
          ${cardHead(null, 'Bovenafwerking',
            top?.type ? pill('Ingevuld', 'ok')
              : hasPeilbuis ? pill('Vereist', 'warn')
                : pill('Optioneel', 'neutral'))}
          ${top ? html`
            <div style="display:grid; grid-template-columns:minmax(0,1fr) 200px; gap:8px; align-items:end;">
              <div><div class="afw-label">Type</div><select ?disabled=${ro} @change=${e => this._afwUpd(mp, 'topCompletions', 0, 'type', e.target.value)}><option value="">— kies —</option>${nen6693.BOVENAFWERKING.map(b => html`<option value=${b.code} ?selected=${b.code === nen6693.bovenafwerkingMigrate(top.type, top.materiaal)}>${b.label}</option>`)}</select></div>
              <div><div class="afw-label">Hoogte t.o.v. maaiveld (cm)</div><input type="number" ?disabled=${ro} .value=${num(top.heightCm)} @input=${e => this._afwUpd(mp, 'topCompletions', 0, 'heightCm', e.target.value)}></div>
            </div>
            ${ro ? '' : html`<button class="afw-quick-btn" style="margin-top:10px;" @click=${() => this._afwDel(mp, 'topCompletions', 0)}>Bovenafwerking verwijderen</button>`}
          ` : html`
            <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
              <span style="display:inline-flex; align-items:center; gap:6px; font-size:0.8rem; flex:1; min-width:180px;
                color:${hasPeilbuis ? '#B45309' : 'var(--mv-text-muted)'};">
                ${unsafeSVG(lucideIcon(hasPeilbuis ? AlertTriangle : Info, 14))}
                ${hasPeilbuis
                  ? 'Er is een peilbuis geplaatst — vul de bovenafwerking in.'
                  : 'Alleen invullen als er een afdekking op het boorgat ligt.'}
              </span>
              ${ro ? '' : html`<button class="afw-quick-btn" @click=${() => this._afwAdd(mp, 'topCompletions')}>+ Bovenafwerking</button>`}
            </div>
          `}
        `)}

        ${card(html`
          ${cardHead(null, 'Achtergelaten in de grond', ro ? '' : html`<button class="afw-quick-btn" @click=${() => this._afwAdd(mp, 'achtergelatenObjecten')}>+ Object</button>`)}
          <div style="font-size:0.72rem; color:var(--mv-text-muted); margin:-6px 0 8px;">Zelden nodig · objecten die in de bodem blijven</div>
          ${afw.achtergelatenObjecten.map((r, i) => this._renderAchtergelatenRow(mp, r, i, ro, num, del))}
        `)}
      </div>`;
  }

  _renderBoringEditForm(boringId) {
    const v = this._mpVal;
    return html`
      <div class="layer-expand" @click=${e => e.stopPropagation()} @keydown=${e => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') this._saveMpEdit(e); }}>
        <div class="expand-field-group" style="grid-template-columns:1fr 1fr;">
          <div class="expand-field">
            <label>Naam boring</label>
            <input type="text" .value=${v.name}
              @input=${e => this._setMV('name', e.target.value)}>
          </div>
          <div class="expand-field">
            <label>Boormeester</label>
            <searchable-select
              allowCustom
              placeholder="— kies of typ —"
              .options=${this._boormeesterOptions(v.boormeester)}
              .value=${v.boormeester || ''}
              @change=${e => this._setMV('boormeester', e.detail.value)}>
            </searchable-select>
          </div>
        </div>
        <div class="expand-field-group" style="grid-template-columns:1fr 1fr;">
          <!-- De datum staat naast 'Grondwater aangetroffen' en niet ergens onderaan: hij hoort bij de
               kopgegevens van de boring en is verplicht voor de export. -->
          <div class="expand-field">
            <label>Datum</label>
            <input type="date" .value=${v.datum || ''}
              @input=${e => this._setMV('datum', e.target.value)}>
          </div>
          <!--
            Twee tegels in plaats van een keuzelijst: het is een ja/nee-vraag, en dan is één
            klik beter dan uitklappen en kiezen. Zelfde vorm als 'Geroerd' bij een potje.
            Nog niets gekozen blijft mogelijk (beide tegels uit) -- dat is iets anders dan
            'nee', en het is wat er staat als de veldwerker het niet heeft ingesproken. Klikken
            op de actieve tegel maakt het veld weer leeg.
          -->
          <div class="expand-field">
            <label>Grondwater aangetroffen?</label>
            <div style="display:flex; gap:6px;">
              ${[true, false].map(opt => {
                const active = v.gwsFound === opt;
                const activeColor = opt ? 'var(--mv-primary)' : '#DC2626';
                return html`
                  <button type="button"
                    title=${active ? 'Klik nogmaals om leeg te laten' : (opt ? 'Grondwater aangetroffen' : 'Geen grondwater aangetroffen')}
                    @click=${() => this._setMV('gwsFound', active ? null : opt)}
                    style="flex:1; padding:5px 8px; border-radius:5px; border:1px solid ${active ? activeColor : 'var(--mv-border)'}; background:${active ? activeColor : 'var(--mv-surface)'}; color:${active ? '#fff' : 'var(--mv-text)'}; font-weight:700; font-size:0.82rem; font-family:inherit; box-sizing:border-box; cursor:pointer;">
                    ${opt ? 'Ja' : 'Nee'}
                  </button>`;
              })}
            </div>
          </div>
        </div>
        ${v.gwsFound === true ? html`
          <div class="expand-field-group" style="grid-template-columns:1fr 1fr;">
            <div class="expand-field">
              <label>Diepte grondwaterstand (GWS) (cm)</label>
              <input type="number" .value=${v.gws}
                @input=${e => this._setMV('gws', e.target.value)}>
            </div>
          </div>
        ` : ''}
        <div class="expand-field-group" style="grid-template-columns:1fr 1fr;">
          <div class="expand-field">
            <label>Type onderzoek</label>
            <select @change=${e => this._setMV('type', e.target.value)}>
              <option value="">— kies —</option>
              ${BORING_TYPE.map(o => html`
                <option value=${o.code} ?selected=${o.code === v.type}>${o.label}</option>
              `)}
            </select>
          </div>
          <div class="expand-field">
            <label>Coördinaten (${(COORDINATE_SYSTEMS[this.coordinateSystem] || COORDINATE_SYSTEMS[DEFAULT_COORDINATE_SYSTEM]).label})</label>
            <div style="display:flex;gap:4px;">
              <input type="text" placeholder="X" .value=${v.coordsX}
                @input=${e => this._setMV('coordsX', e.target.value)} style="flex:1;">
              <input type="text" placeholder="Y" .value=${v.coordsY}
                @input=${e => this._setMV('coordsY', e.target.value)} style="flex:1;">
            </div>
          </div>
        </div>
        ${hasPitFields(v.type) ? this._renderInspectiegatFields(v) : ''}
        ${(() => {
          // Deellocatie en geplande boring naast elkaar: twee korte keuzes, en zo blijft het
          // formulier compact. Is er maar één van de twee, dan krijgt die de hele breedte.
          const dl = this._renderSublocationField(boringId);
          const pp = this._renderPlannedPointField(boringId);
          return dl && pp
            ? html`<div class="expand-field-group" style="grid-template-columns:1fr 1fr; align-items:start;">${dl}${pp}</div>`
            : html`${dl}${pp}`;
        })()}
        <div class="expand-field">
          <label>Referentievlak</label>
          <select @change=${e => this._setMV('referencePlane', e.target.value)}>
            ${Object.values(REFERENCE_PLANES).map(rp => html`
              <option value=${rp.id} ?selected=${rp.id === (v.referencePlane || DEFAULT_REFERENCE_PLANE)}>${rp.label}</option>
            `)}
          </select>
        </div>
        <div class="expand-field-group" style="grid-template-columns:1fr 1fr;">
          <div class="expand-field">
            <label>Maaiveldtype</label>
            <select @change=${e => this._setMV('groundLevelType', e.target.value)}>
              <option value="">— kies —</option>
              ${nen6693.MAAIVELDTYPE.map(m => html`
                <option value=${m.code} ?selected=${m.code === v.groundLevelType}>${m.label}</option>
              `)}
            </select>
          </div>
          <div class="expand-field">
            <label>Maaiveldhoogte (m +NAP)</label>
            <input type="text" inputmode="decimal" placeholder="Bijv. 2.35" .value=${v.coordsZ}
              @input=${e => this._setMV('coordsZ', e.target.value)}>
          </div>
        </div>
        <div class="expand-field">
          <label>Reden boring gestopt</label>
          <select @change=${e => this._setMV('stopReason', e.target.value)}>
            <option value="">— kies —</option>
            ${[...tiCodelists.stopCriterium].map(([label, code]) => html`
              <option value=${code} ?selected=${code === v.stopReason}>${label}</option>
            `)}
          </select>
        </div>
        <div class="expand-field">
          <label>Opmerking boring</label>
          <input type="text" .value=${v.opmerking}
            @input=${e => this._setMV('opmerking', e.target.value)}>
        </div>
        <!-- Autosave: geen Opslaan-knop meer; status + Sluiten staan in de boringkop -->
        <div class="expand-actions" style="align-items:center;">
          <button class="expand-cancel-btn" style="margin-left:auto;" @click=${e => this._cancelMpEdit(e)}>Sluiten</button>
        </div>
      </div>
    `;
  }

  _renderLagenTab(mp, mpLagen, mpMonsters) {
    if (this._is5104()) return this._renderLagenTab5104(mp, mpLagen, mpMonsters);
    if (!mpLagen.length) {
      return html`
        <div style="padding:0.75rem 0.9rem; color:var(--mv-text-muted); font-size:0.85rem;">Geen lagen gevonden.</div>
        <div style="padding:0.5rem 0.9rem; border-top:1px solid var(--mv-border);">
          ${this._renderAddLayerButton(mp, mpLagen)}
        </div>
      `;
    }

    const _hb = this._hlBoring(mp);
    const conflicts = mp.depthConflicts;
    const conflictByIdx = new Map(conflicts.map(c => [c.afterIdx, c]));

    // NEN 6693-opties (nieuw model). Hoofdbestanddeel = gegroepeerde grondsoorten/laagtypen
    // met niet-selecteerbare categorie-koppen (<optgroup>).
    const soilGroups = this._N.HOOFDBESTANDDEEL_GROEPEN;
    const toevOpts = [...this._N.BIJMENGING.keys()];
    const gradOpts = [...this._N.INTENSITEIT.keys()];
    // De mediaanlijst hangt aan de grondsoort: zand kent de onderverdeling uit de
    // korrelgroottetabel, grind niet. Zie this._N.medianOptions.
    // Gesteenten (alleen bij rots, Tabel C.2) — waarde = term, label toont de NEN-code, bv. "basalt (BST)".
    const gesteenteOpts = [...this._N.GESTEENTE].map(([term, code]) => ({ value: term, label: `${term} (${code})` }));
    const kleurOpts = [...this._N.KLEUR.keys()].sort((a, b) => a.localeCompare(b, 'nl'));
    const shadeOpts = [...this._N.KLEUR_TINT.keys()]; // licht/standaard/donker
    // BZB gegroepeerd: bijzondere bestanddelen (C.1) + gesteenten (C.2), met niet-selecteerbare
    // koppen ({ header }). Waarde = term, label toont de NEN-code (bv. "glauconiet (GLT)").
    const bzbCode = t => this._N.BZB_SOORT.get(t) || this._N.GESTEENTE.get(t) || '';
    const bzbOpts = this._N.BZB_GROEPEN.flatMap(g => [
      { header: g.label },
      ...g.items.map(t => ({ value: t, label: bzbCode(t) ? `${t} (${bzbCode(t)})` : t })),
    ]);
    const bzbGOpts  = [...this._N.BZB_VOLUME.keys()]; // volumeaandeel (6 klassen)
    const origOpts  = [...this._N.BZB_HERKOMST.keys()];

    return html`
      <div>
        <div class="layer-header">
          <span>Diepte (cm)</span><span>Grondsoort</span><span>Kleur</span><span>Bijzonderheden</span>
        </div>
        ${mpLagen.map((l, idx) => {
          // Read-only kent zijn eigen uitgeklapte rij: _inlineEdit hoort bij het bewerken en
          // blijft daar leeg.
          const isActive = this.readonly ? this._roLayer === l.id : this._inlineEdit?.layerId === l.id;
          const isLast   = idx === mpLagen.length - 1 && mpMonsters.length === 0;
          const iv       = this._inlineVal;

          // NEN 6693 slaat NEN-codes op ('H'/'BK'/'Gr'/'h'/'z'/'s'/…); resolve die naar de
          // volledige term (NEN 6693 eerst, TI-terugval) i.p.v. de shortcode te tonen.
          const _toev1 = l.toevoeging1 ? this._nenName(l.toevoeging1, [this._N.BIJMENGING], tiCodelists.toevoeging) : '';
          const _grad1 = l.gradatie1   ? this._nenName(l.gradatie1,   [this._N.INTENSITEIT], tiCodelists.gradatie)  : '';
          const _toev2 = l.toevoeging2 ? this._nenName(l.toevoeging2, [this._N.BIJMENGING], tiCodelists.toevoeging) : '';
          const _grad2 = l.gradatie2   ? this._nenName(l.gradatie2,   [this._N.INTENSITEIT], tiCodelists.gradatie)  : '';
          const _toev3 = l.toevoeging3 ? this._nenName(l.toevoeging3, [this._N.BIJMENGING], tiCodelists.toevoeging) : '';
          const _grad3 = l.gradatie3   ? this._nenName(l.gradatie3,   [this._N.INTENSITEIT], tiCodelists.gradatie)  : '';
          const _soilTerm = this._nenName(l.soilType, [this._N.GRONDSOORT, this._N.LAAGTYPE_SPECIAAL, this._N.GESTEENTE], tiCodelists.bodemsoortHistorisch) || l.soilLabel;
          const soil   = [
            _soilTerm,
            _toev1 ? [_toev1, _grad1].filter(Boolean).join(' ') : null,
            _toev2 ? [_toev2, _grad2].filter(Boolean).join(' ') : null,
            _toev3 ? [_toev3, _grad3].filter(Boolean).join(' ') : null,
            this._isIso() && l.organic ? `${l.organic} organisch` : null,
          ].filter(Boolean).join(', ');
          const extra = [l.bzbLabels.join(', '), l.opmerking || ''].filter(Boolean).join(' · ') || '-';

          // Diff highlight (validation): mark cells/rows that differ from "Verwacht".
          const _lhl = _hb?.layers?.[idx];
          const HL = 'background:#f1416c1f;color:#f1416c;border-radius:4px;padding:0 4px;font-weight:600;';
          const hlCol = (col) => _lhl && (_lhl.extra || _lhl.cols?.has(col)) ? HL : '';

          return html`
            <!-- Summary row -->
            <div class="layer-row ${isActive ? 'is-active' : ''}"
              style="border-left:3px solid ${_lhl?.extra ? '#f1416c' : l.colorHex}; ${isLast && !isActive ? 'border-bottom:none;' : ''} align-items:center;"
              @click=${(e) => this._startInlineEdit(e, mp.guid, l.id)}>
              <span style="font-weight:700;font-family:monospace;color:var(--mv-text-sub);${hlCol('diepte')}">${l.upperDepth}–${l.lowerDepth}</span>
              <span style="font-weight:500;${hlCol('grondsoort')}">${soil}${mediaanApplies(l.soilType) && l.mediaanLabel ? html` <span style="font-weight:400; color:var(--mv-text-muted); font-size:0.85em;">(${l.mediaanLabel})</span>` : ''}</span>
              <span style="color:var(--mv-text-sub);${hlCol('kleur')}">${l.kleurLabel || '-'}</span>
              <span style="color:var(--mv-text-sub);display:flex;align-items:center;gap:4px;">
                <span style="flex:1;min-width:0;${hlCol('bijzonderheden')}">${extra}</span>
                ${this.readonly
                  // Read-only: een chevron in plaats van het potlood. Zonder enig teken lijkt de
                  // rij dood en komt niemand op het idee erop te klikken; een potlood zou juist
                  // bewerken beloven.
                  ? html`<span class="row-edit-hint" style="display:flex; transform:rotate(${isActive ? 180 : 0}deg);">${unsafeSVG(lucideIcon(ChevronDown, 11))}</span>`
                  : html`<span class="row-edit-hint">${unsafeSVG(lucideIcon(Edit2, 11))}</span>`}
              </span>
            </div>

            <!-- Accordion edit form -->
            ${isActive ? (this.readonly
              ? this._renderLayerReadOnly(l, _soilTerm, [[_toev1, _grad1], [_toev2, _grad2], [_toev3, _grad3]])
              : this._renderLayerEditForm(iv, idx, mp.guid, l, soilGroups, toevOpts, gradOpts, gesteenteOpts, kleurOpts, shadeOpts, bzbOpts, bzbGOpts, origOpts)) : ''}

            <!-- Depth conflict indicator -->
            ${conflictByIdx.has(idx) ? (() => {
              const c = conflictByIdx.get(idx);
              const isOverlap = c.type === 'overlap';
              return html`
                <div style="display:flex; align-items:center; gap:6px; padding:3px 10px; font-size:0.75rem; font-weight:600;
                  background:${isOverlap ? 'rgba(239,68,68,0.07)' : 'rgba(245,158,11,0.07)'};
                  color:${isOverlap ? '#DC2626' : '#D97706'};
                  border-left:3px solid ${isOverlap ? '#DC2626' : '#F59E0B'};">
                  ${unsafeSVG(lucideIcon(AlertTriangle, 11))}
                  <span style="flex:1;">
                    ${isOverlap
                      ? `Overlapping: laag eindigt op ${c.a.lowerDepth} cm maar volgende begint op ${c.b.upperDepth} cm`
                      : `Hiaat: laag eindigt op ${c.a.lowerDepth} cm maar volgende begint op ${c.b.upperDepth} cm`}
                  </span>
                  <button @click=${e => this._fixDepthConflict(e, c)} style="background:${isOverlap ? '#DC2626' : '#D97706'}; color:#fff; border:none; border-radius:4px; padding:2px 8px; font-size:0.7rem; font-weight:700; cursor:pointer; white-space:nowrap;">Herstellen</button>
                </div>
              `;
            })() : ''}
          `;
        })}
      </div>
      ${_hb?.missingLayers ? html`
        <div style="display:flex; align-items:center; gap:6px; padding:5px 12px; font-size:0.78rem; font-weight:600; color:#f1416c; background:#f1416c14; border-top:1px solid var(--mv-border);">
          ${unsafeSVG(lucideIcon(AlertTriangle, 12))}
          <span>${_hb.missingLayers} laag/lagen minder dan verwacht.</span>
        </div>` : ''}
      <div style="padding:0.5rem 0.9rem; border-top:1px solid var(--mv-border);">
        ${this._renderAddLayerButton(mp, mpLagen)}
      </div>
    `;
  }

  _renderAddLayerButton(mp, mpLagen) {
    if (this.readonly) return '';
    return html`
      <button class="bzb-add-btn" style="width:100%;"
        @click=${e => {
          e.stopPropagation();
          const upperDepth = mpLagen.reduce((max, l) => Math.max(max, l.lowerDepth), 0);
          const lowerDepth = upperDepth + 20;
          const tmpId = `pending-${Date.now()}`;
          // Sjabloon-default voor boorsysteem meteen meegeven (net als de field-recorder).
          const drillingSystem = this.templateDefaults?.layer?.drilling_system || null;
          const payload = { upperDepth, lowerDepth, ...(drillingSystem ? { drillingSystem } : {}) };
          this._optimistic(
            bs => bs.map(b => b.id !== mp.guid ? b : { ...b, layers: [...(b.layers || []), { id: tmpId, upperDepth, lowerDepth, drillingSystem }] }),
            () => this._crud.createLayer(mp.guid, payload),
            'Laag toevoegen mislukt',
          );
        }}>
        ${unsafeSVG(lucideIcon(Plus, 12))} Laag toevoegen
      </button>
    `;
  }

  /**
   * Laagdetails zonder invoervelden.
   *
   * Bewust GEEN uitgeschakeld bewerkformulier. Dit is de kolom 'Verwacht' van de validatie: het
   * gouden voorbeeld. Vergeet je daar één `disabled`, dan bewerkt iemand ongemerkt de norm
   * waartegen we alles afmeten. Een weergave zonder velden kan dat per definitie niet.
   *
   * De aanleiding: in de samenvattingsregel staan bijzondere bestanddelen en de opmerking in
   * dezelfde kolom, gescheiden door ' · '. Is er maar één van de twee, dan valt dat scheidings-
   * teken weg en zie je alleen 'lucht' -- zonder te weten wat het is. Hier staan ze uit elkaar,
   * elk met hun eigen kopje, en lege velden laten we weg in plaats van als streepje te tonen.
   */
  _renderLayerReadOnly(l, soilTerm, bijmengingen) {
    const rij = (label, waarde) => waarde
      ? html`
        <div style="display:flex; gap:10px; padding:3px 0; font-size:0.82rem;">
          <span style="flex:0 0 9.5rem; color:var(--mv-text-muted);">${label}</span>
          <span style="flex:1; color:var(--mv-text);">${waarde}</span>
        </div>`
      : '';

    const bij = (bijmengingen || [])
      .map(([t, g]) => (t ? [t, g].filter(Boolean).join(' ') : null))
      .filter(Boolean);

    return html`
      <div class="layer-expand" @click=${e => e.stopPropagation()}>
        ${rij('Diepte', `${l.upperDepth}–${l.lowerDepth} cm`)}
        ${rij('Grondsoort', soilTerm || l.soilLabel)}
        ${rij('Mediaan', l.mediaanLabel)}
        ${rij('Bijmengingen', bij.length ? bij.join(', ') : null)}
        ${rij('Kleur', l.kleurLabel)}
        <!-- De twee die dichtgeklapt niet uit elkaar te houden zijn: -->
        ${rij('Bijzondere bestanddelen', l.bzbLabels?.length ? l.bzbLabels.join(', ') : null)}
        ${rij('Opmerking', l.opmerking)}
        ${!l.bzbLabels?.length && !l.opmerking ? html`
          <div style="padding:3px 0; font-size:0.82rem; color:var(--mv-text-muted);">
            Geen bijzondere bestanddelen of opmerking.
          </div>` : ''}
      </div>`;
  }

  _renderLayerEditForm(iv, idx, boringGuid, l, soilGroups, toevOpts, gradOpts, gesteenteOpts, kleurOpts, shadeOpts, bzbOpts, bzbGOpts, origOpts) {
    const codeVeld = this._isIso() ? this._grondsoortCode(iv, 'si1FSa2', 110, false) : this._grondsoortCode(iv, 'Zs1g3', 96, false);
    return html`
      <div class="layer-expand" @click=${e => e.stopPropagation()} @keydown=${e => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') this._saveInlineEdit(e); }}>

        <!-- Diepte + boorsysteem (per laag, → TI LaDrillingSystemTypeCode) -->
        ${this._renderDiepteRij(iv)}

        <div class="laagdeel">
        ${this._sectieKop('Grondsoort', '', this.grainSizeInput ? '' : codeVeld)}
        ${this._renderKorrelverdeling(iv)}

        <!-- Grondsoort -->
        <div class="grondsoort-blok ${this._gsizeKlasse(iv) ? 'vast' : ''}"
             @click=${() => { if (this._gsizeKlasse(iv)) this._korrelUitlegTonen(); }}>
          ${!this.grainSizeInput ? '' : html`
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
            <div style="display:flex; align-items:center; gap:8px;">
              <div class="expand-section-title" style="margin:0;">Grondsoort</div>
              ${this._gsizeKlasse(iv) ? '' : html`
                <span class="korrel-chip uit">Handmatig</span>
                <span class="korrel-uitleg">Typ een code of kies hieronder. Vul een percentage in om het automatisch te bepalen.</span>`}
            </div>
            ${codeVeld}
          </div>`}
          ${!this._gsizeKlasse(iv) || !this._korrelUitleg ? '' : html`
            <div class="korrel-let-op">
              <span>Deze velden volgen de korrelverdeling en worden bij elke wijziging
                    overschreven. Wis de percentages om handmatig te kiezen.</span>
              <button type="button" class="korrel-wissen" @click=${() => this._gsizeWissen()}>
                Percentages wissen
              </button>
            </div>`}
          ${(() => {
            // 'Vast' = door de korrelverdeling bepaald. Te lezen, niet te wijzigen: twee bronnen
            // voor dezelfde waarheid lopen vroeg of laat uit elkaar, en dan staat er een
            // grondsoort die zijn eigen meetwaarden tegenspreekt. `v` is wat er in beeld hoort
            // te staan -- de uitkomst, of de eigen keuze als er geen percentages zijn.
            const _klasse = this._gsizeKlasse(iv);
            const vast = !!_klasse;
            const v = this._gsizeView(iv, _klasse);
            const autoMerk = vast ? html`<span class="auto-merk">auto</span>` : '';
            // Interne code van het gekozen hoofdbestanddeel (grondsoort of speciaal laagtype).
            const hoofdCode = v.gs_hoofd === 'rots' ? 'ROTS'
              : (this._N.GRONDSOORT.get(v.gs_hoofd) || this._N.LAAGTYPE_SPECIAAL.get(v.gs_hoofd));
            const showMediaan = this._N.MEDIAAN_GRONDSOORTEN.has(hoofdCode);
            const showGesteente = v.gs_hoofd === 'rots'; // rotslaag → gesteentetype-keuze (C.2)
            const showMineraalarm = this._N.MINERAALARM_GRONDSOORTEN.has(hoofdCode); // veen/humus/detritus/gyttja
            const showConsistentie = this._N.CONSISTENTIE_GRONDSOORTEN.has(hoofdCode); // slib
            // Textuur-bijmengingen alleen bij grondsoorten met textuur (niet slib/rots/BVL/…).
            const showTextuur = !!v.gs_hoofd && !this._N.LAAGTYPE_ZONDER_TEXTUUR.has(hoofdCode);
            // Een organische grondsoort draagt er maar één (Tabel 10 / bijlage D).
            const admixSlots = this._N.maxAdmixtures(hoofdCode);
            const extra = Math.min(v.extraAdmix ?? 0, admixSlots - 1);
            const two = 'grid-template-columns:1fr 1fr;';
            // Een grondsoort mag zijn eigen bijmenging niet krijgen (geen 'zandig zand' e.d.).
            const exclBijm = this._N.GRONDSOORT_EIGEN_BIJMENGING[this._N.GRONDSOORT.get(v.gs_hoofd)];
            const bijmOpts = (exclBijm ? toevOpts.filter(t => this._N.BIJMENGING.get(t) !== exclBijm) : toevOpts)
              // Bij organisch bestaan alleen kleiig en zandig; de rest hoort er niet in te staan.
              .filter(t => this._N.admixtureRule(hoofdCode, this._N.BIJMENGING.get(t)).allowed);
            // Weghalen staat rechts van de rij, als rode knop -- hetzelfde gebaar als bij de
            // bijzondere bestanddelen. Als klein grijs kruisje in het label was hij nauwelijks
            // te zien, terwijl het wel iets weggooit.
            const removeBtn = (onRemove) => onRemove
              ? html`<button type="button" class="bzb-del-btn" title="Bijmenging verwijderen"
                       @click=${onRemove}>${unsafeSVG(lucideIcon(X, 12))}</button>`
              : html`<span></span>`;
            const admixRow = (label, toevKey, gradKey, toevVal, gradVal, onRemove) => {
              // Intensiteit-schaal is per bijmenging (NEN 6693): siltig kent 'uiterst' (s4),
              // zandig/grindig/humeus alleen zwak/matig/sterk, kleiig géén intensiteit → veld
              // verdwijnt dan helemaal (net als mediaan bij niet-zand/grind).
              const bijmCode = this._N.BIJMENGING.get(toevVal);
              const allowed = (toevVal && bijmCode) ? this._N.admixtureRule(hoofdCode, bijmCode).intensities : [];
              const intensityOpts = gradOpts.filter(o => allowed.includes(o));
              const showIntensity = intensityOpts.length > 0;
              return html`
              <div class="expand-field-group admix-rij" style="margin-bottom:6px;">
                <div class="expand-field ${vast ? 'auto' : ''}">
                  <label><span>${label}${autoMerk}</span></label>
                  <select ?disabled=${vast} @change=${e => {
                    const nv = e.target.value;
                    this._setIV(toevKey, nv);
                    // Wis de intensiteit als de nieuwe bijmenging die niet (meer) toestaat.
                    const code = this._N.BIJMENGING.get(nv);
                    const allow = code ? this._N.admixtureRule(hoofdCode, code).intensities : [];
                    if (!allow.includes(gradVal)) this._setIV(gradKey, '');
                  }}>
                    <option value="">—</option>
                    ${bijmOpts.map(o => html`<option value=${o} ?selected=${o === toevVal}>${this._cap(o)}</option>`)}
                  </select>
                </div>
                ${showIntensity ? html`
                  <div class="expand-field ${vast ? 'auto' : ''}">
                    <label>${label === 'Bijmenging' ? 'Intensiteit' : `Intensiteit ${label.split(' ')[1]}`}${autoMerk}</label>
                    <select ?disabled=${vast} @change=${e => this._setIV(gradKey, e.target.value)}>
                      <option value="">—</option>
                      ${intensityOpts.map(o => html`<option value=${o} ?selected=${o === gradVal}>${this._cap(o)}</option>`)}
                    </select>
                  </div>
                ` : html`<div></div>`}
                ${removeBtn(vast ? null : onRemove)}
              </div>
            `;
            };
            return html`
              <!-- Hoofdbestanddeel + (optioneel) Mediaan ernaast -->
              <div class="expand-field-group zonder-knop-kolom" style="${two}margin-bottom:6px;">
                <div class="expand-field ${vast ? 'auto' : ''}">
                  <label style="display:flex; align-items:center; gap:5px;"><span>Hoofdbestanddeel</span>${autoMerk}${this._infoBtn(() => this._showHoofdbestanddeelInfo())}</label>
                  <select ?disabled=${vast} @change=${e => {
                    const val = e.target.value;
                    this._setIV('gs_hoofd', val);
                    if (!this._N.MEDIAAN_GRONDSOORTEN.has(this._N.GRONDSOORT.get(val))) this._setIV('mediaan', '');
                    if (val !== 'rots') this._setIV('gs_gesteente', '');
                    if (!this._N.MINERAALARM_GRONDSOORTEN.has(this._N.GRONDSOORT.get(val))) this._setIV('gs_mineraalarm', false);
                    // Consistentie-lijst verschilt per grondsoort (slib vs klei/silt); wis een
                    // waarde die in de nieuwe lijst niet voorkomt.
                    const consCode = this._N.GRONDSOORT.get(val) || this._N.LAAGTYPE_SPECIAAL.get(val);
                    if (!this._N.consistentieTerms(consCode).includes(this._inlineVal.gs_consistentie)) this._setIV('gs_consistentie', '');
                    // Wis een bijmenging die gelijk is aan het nieuwe hoofdbestanddeel (bv. zand → zandig).
                    const excl = this._N.GRONDSOORT_EIGEN_BIJMENGING[this._N.GRONDSOORT.get(val)];
                    if (excl) {
                      for (const [tk, gk] of [['gs_toev1','gs_grad1'],['gs_toev2','gs_grad2'],['gs_toev3','gs_grad3']]) {
                        if (this._N.BIJMENGING.get(this._inlineVal[tk]) === excl) { this._setIV(tk, ''); this._setIV(gk, ''); }
                      }
                    }
                  }}>
                    <option value="">— kies —</option>
                    ${soilGroups.map(group => html`
                      <optgroup label=${group.label}>
                        ${group.items.map(o => html`<option value=${o.term} ?selected=${o.term === v.gs_hoofd}>${this._cap(o.label)}</option>`)}
                      </optgroup>
                    `)}
                  </select>
                </div>
                ${(() => {
                  // Extra velden naast het hoofdbestanddeel. Meestal één; organische lagen
                  // (veen/humus/detritus/gyttja) hebben er twee: mineraalarm + consistentie.
                  const fields = [];
                  if (showMediaan) {
                    // Info-icoon met de korrelgrootte-tabel (NEN Bijlage E), grondsoort-afhankelijk.
                    const korrel = this._N.MEDIAAN_KORRELGROOTTE[hoofdCode];
                    const korrelNaam = hoofdCode === 'Z' ? 'zand' : 'grind';
                    const mediaanOpts = this._N.medianOptions(hoofdCode);
                    fields.push(html`
                    <div class="expand-field ${vast ? 'auto' : ''}">
                      <label style="display:flex; align-items:center; gap:5px;"><span>Mediaan</span>${autoMerk}${korrel ? this._infoBtn(() => this._showTableInfo(`Mediaan ${korrelNaam} — korrelgrootte`, `Mediaanklassen voor ${korrelNaam}. NEN 6693 korrelgroottetabel.`, korrel, 'Mediaanklasse', 'Korrelgrootte')) : ''}</label>
                      <select ?disabled=${vast} @change=${e => this._setIV('mediaan', e.target.value)}>
                        <option value="">—</option>
                        ${mediaanOpts.map((o) => {
                          // Zand kent twee samenvattende klassen met hun fracties eronder. Die
                          // staan vooraan en de fracties springen in, zodat te zien is dat
                          // 'Fijn' iets anders is dan 'Fijn 105-150' -- het eerste zegt dat de
                          // fractie niet gemeten is, het tweede dat hij dat wél is.
                          const hoofd = this._N.isMediaanHoofdklasse(hoofdCode, o);
                          const onder = !hoofd && (korrel || []).some((m) => m.hoofdklasse && o.startsWith(`${m.term} `));
                          return html`<option value=${o} ?selected=${o === v.mediaan}
                            >${onder ? '\u00a0\u00a0\u00a0\u00a0' : ''}${this._cap(o)}</option>`;
                        })}
                      </select>
                    </div>`);
                  }
                  if (showGesteente) fields.push(html`
                    <div class="expand-field">
                      <label>Gesteentetype</label>
                      <searchable-select
                        .options=${gesteenteOpts}
                        .value=${v.gs_gesteente || ''}
                        placeholder="Kies of zoek…"
                        @change=${e => this._setIV('gs_gesteente', e.detail.value)}>
                      </searchable-select>
                    </div>`);
                  if (showConsistentie) {
                    const consOpts = this._N.MINERALE_CONSISTENTIE_GRONDSOORTEN.has(hoofdCode) ? this._N.MINERALE_CONSISTENTIE
                      : this._N.ORGANISCHE_CONSISTENTIE_GRONDSOORTEN.has(hoofdCode) ? this._N.ORGANISCHE_CONSISTENTIE
                      : null; // null = slib (gegroepeerd)
                    fields.push(html`
                    <div class="expand-field">
                      <label style="display:flex; align-items:center; gap:5px;">
                        <span>${consOpts ? 'Consistentie' : 'Rijping/consistentie'}</span>
                        <button type="button" title="Uitleg consistentie"
                          @click=${() => this._showConsistentieInfo(hoofdCode)}
                          style="display:inline-flex; align-items:center; justify-content:center; width:16px; height:16px; padding:0; border:none; background:transparent; color:var(--mv-text-muted); cursor:pointer; border-radius:50%; line-height:1;"
                          @mouseover=${e => e.currentTarget.style.color = 'var(--mv-primary)'}
                          @mouseout=${e => e.currentTarget.style.color = 'var(--mv-text-muted)'}>
                          ${unsafeSVG(lucideIcon(Info, 14))}</button>
                      </label>
                      <select @change=${e => this._setIV('gs_consistentie', e.target.value)}>
                        <option value="" ?selected=${!v.gs_consistentie}>—</option>
                        ${consOpts
                          ? consOpts.map(o => html`<option value=${o.term} ?selected=${o.term === v.gs_consistentie}>${this._cap(o.term)}</option>`)
                          : this._N.SLIB_CONSISTENTIE_GROEPEN.map(group => html`
                              <optgroup label=${group.label}>
                                ${group.items.map(o => html`<option value=${o.term} ?selected=${o.term === v.gs_consistentie}>${this._cap(o.term)}</option>`)}
                              </optgroup>
                            `)}
                      </select>
                    </div>`);
                  }
                  // Mineraalarm ná consistentie, zodat consistentie altijd direct rechts van
                  // het hoofdbestanddeel staat (mineraalarm valt dan naar de tweede rij).
                  // NEN-EN-ISO 14688: organisch bij elke grondsoort, korrelhoekigheid en vorm bij
                  // grind, zand en keitjes (deel 1, 5.2). Zelfde termen als TerraIndex.
                  if (this._isIso() && hoofdCode && hoofdCode !== 'W') {
                    const keuze = (label, key, opts) => html`
                      <div class="expand-field">
                        <label>${label}</label>
                        <select @change=${e => this._setIV(key, e.target.value)}>
                          <option value="" ?selected=${!v[key]}>—</option>
                          ${opts.map(o => html`<option value=${o} ?selected=${o === v[key]}>${this._cap(o)}</option>`)}
                        </select>
                      </div>`;
                    if (iso14688.acceptsOrganic(hoofdCode)) fields.push(keuze('Organisch', 'organic', iso14688.ORGANISCH));
                    if (iso14688.KORRELVORM_GRONDSOORTEN.has(hoofdCode)) {
                      fields.push(keuze('Korrelhoekigheid', 'grainAngularity', iso14688.KORRELHOEKIGHEID));
                      fields.push(keuze('Vorm', 'grainShape', iso14688.KORRELVORM));
                    }
                    if (iso14688.ORGANISCHE_GRONDSOORTEN.has(hoofdCode)) fields.push(keuze('Veentextuur', 'peatTexture', iso14688.VEENTEXTUUR));
                  }
                  if (showMineraalarm) fields.push(html`
                    <div class="expand-field">
                      <label>Mineraalarm</label>
                      <select @change=${e => this._setIV('gs_mineraalarm', e.target.value === 'ja')}>
                        <option value="nee" ?selected=${!v.gs_mineraalarm}>Nee</option>
                        <option value="ja" ?selected=${!!v.gs_mineraalarm}>Ja</option>
                      </select>
                    </div>`);
                  return fields.length ? fields : html`<div></div>`;
                })()}
              </div>
              <!-- Bijmengingen alleen bij grondsoorten met textuur (niet slib/rots/BVL/…) -->
              ${!showTextuur ? '' : html`
                ${admixRow('Bijmenging', 'gs_toev1', 'gs_grad1', v.gs_toev1, v.gs_grad1, () => this._removeAdmix(1))}
                ${extra >= 1 ? admixRow('Bijmenging 2', 'gs_toev2', 'gs_grad2', v.gs_toev2, v.gs_grad2, () => this._removeAdmix(2)) : ''}
                ${extra >= 2 ? admixRow('Bijmenging 3', 'gs_toev3', 'gs_grad3', v.gs_toev3, v.gs_grad3, () => this._removeAdmix(3)) : ''}
                ${vast
                  ? html`<div class="vast-tegel" style="margin-top:2px;">Bijmengingen worden bepaald door de korrelverdeling</div>`
                  : extra < admixSlots - 1 ? html`
                  <button class="bzb-add-btn" style="margin-top:2px;"
                    @click=${() => this._setIV('extraAdmix', Math.min(admixSlots - 1, (iv.extraAdmix ?? 0) + 1))}>
                    + Bijmenging toevoegen
                  </button>
                ` : ''}
              `}
            `;
          })()}
        </div>
        </div>

        <!-- Kleur -->
        <div>
          ${this._sectieKop('Kleur')}
          <div class="bzb-tegels">
            <div class="bzb-tegel">
              <div class="expand-field">
                <label>Hoofdkleur</label>
                <select @change=${e => {
                  const val = e.target.value;
                  this._setIV('kleurMain', val);
                  // wit/zwart/beige/crème/kleurloos kennen geen tint → wis een eventuele tint.
                  if (this._N.KLEUR_TINT_UITGESLOTEN.has(this._N.KLEUR.get(val))) this._setIV('kleurShade', '');
                  // zonder hoofdkleur geen subkleur/tint; bijkleur mag niet gelijk zijn aan hoofdkleur.
                  if (!val) { this._setIV('kleurSub', ''); this._setIV('kleurShade', ''); }
                  else if (iv.kleurSub === val) this._setIV('kleurSub', '');
                }}>
                  <option value="">—</option>
                  ${kleurOpts.map(o => html`<option value=${o} ?selected=${o === iv.kleurMain}>${this._cap(o)}</option>`)}
                </select>
              </div>
              <div class="expand-field">
                <label>Subkleur</label>
                <select ?disabled=${!iv.kleurMain}
                  title=${!iv.kleurMain ? 'Kies eerst een hoofdkleur' : ''}
                  @change=${e => this._setIV('kleurSub', e.target.value)}>
                  <option value="">—</option>
                  ${kleurOpts.filter(o => o !== iv.kleurMain).map(o => html`<option value=${o} ?selected=${o === iv.kleurSub}>${this._cap(o)}</option>`)}
                </select>
              </div>
              <div class="expand-field">
                <label>Tint</label>
                ${this._N.KLEUR_TINT_UITGESLOTEN.has(this._N.KLEUR.get(iv.kleurMain))
                  ? html`<span class="bzb-nvt">n.v.t.</span>`
                  : html`
                    <select @change=${e => this._setIV('kleurShade', e.target.value)}>
                      ${shadeOpts.map(o => html`<option value=${o} ?selected=${o === (iv.kleurShade || 'standaard')}>${this._cap(o)}</option>`)}
                    </select>`}
              </div>
              <div class="expand-field">
                <label>Code</label>
                <input type="text" title="Kleurcode, bijvoorbeeld libr" class="bzb-code"
                  .value=${this._getColorShortcode(iv)}
                  @input=${e => this._applyColorShortcode(e.target.value)}
                  style="border-color:${this._invalidColorShortcode ? '#DC2626' : 'var(--mv-border)'};
                         color:${this._invalidColorShortcode ? '#DC2626' : 'var(--mv-primary)'};">
              </div>
              <!-- Leeg: hier staat bij een bestanddeel de verwijderknop. Een laag heeft precies
                   één kleur, maar de kolom blijft, zodat de code op dezelfde plek staat. -->
              <span></span>
            </div>
          </div>
        </div>

        <!-- BZB -->
        <div>
          ${this._sectieKop('Bijzondere bestanddelen')}
          <div class="bzb-tegels">
            ${!(iv.bzbItems || []).length ? html`
              <div class="bzb-leeg">Nog geen bijzondere bestanddelen.</div>` :
              (iv.bzbItems || []).map((b, bi) => this._renderBzbTegel(iv, b, bi, bzbOpts, bzbGOpts, origOpts, true))}
            <button class="bzb-add-btn"
              @click=${() => this._inlineVal = { ...iv, bzbItems: [...(iv.bzbItems||[]), { type:'', gradatie:'', herkomst:'', grootte:'' }] }}>
              + BZB toevoegen
            </button>
          </div>
        </div>

        ${this._renderWaarnemingen(iv)}

        <!-- Actions (autosave: geen Opslaan-knop meer; status staat in de boringkop) -->
        <div class="expand-actions" style="align-items:center;">
          <button class="expand-danger-btn" @click=${e => this._removeLayer(e)}>
            ${unsafeSVG(lucideIcon(Trash2, 13))} Verwijderen
          </button>
          <button class="expand-cancel-btn" style="margin-left:auto;" @click=${e => this._cancelInlineEdit(e)}>Sluiten</button>
        </div>
      </div>
    `;
  }

  // ─── NEN 5104-pad (oude systematiek) ─────────────────────────────────────
  // Wanneer de meting op NEN 5104 staat, gebruiken we het oude laag-bewerkformulier
  // (SIKB/TerraIndex-codes via tiCodelists) i.p.v. de NEN 6693-varianten. De publieke
  // methoden (_startInlineEdit, _getShortcode, _renderLagenTab, …) dispatchen hierheen.

  /** True als deze meting volgens de oude NEN 5104-systematiek verwerkt wordt. */
  _is5104() { return normalizeSoilStandard(this.standard) === 'nen5104'; }

  /** NEN-EN-ISO 14688: het NEN 6693-formulier met de ISO-lijsten (zie codelists/iso14688.ts). */
  _isIso() { return normalizeSoilStandard(this.standard) === 'iso14688'; }

  /**
   * De codelijsten van het formulier: nen6693, of iso14688 bij een ISO-meting. iso14688 erft
   * alles van nen6693 en overschrijft alleen wat ISO anders doet, dus elke `this._N.X` bestaat
   * in beide.
   */
  get _N() { return this._isIso() ? iso14688 : nen6693; }

  /** De grondsoortcode in ISO-notatie: `si1Z` → `si1Sa`, met de subfractie: `si1FSa2`. */
  _getShortcodeIso(v) {
    if (!v) return '';
    const N = iso14688;
    const hoofd = v.gs_hoofd || v.hoofd;
    const soilType = N.GRONDSOORT.get(hoofd) || N.LAAGTYPE_SPECIAAL.get(hoofd) || '';
    const admixtures = [1, 2, 3]
      .map(n => ({ code: N.BIJMENGING.get(v[`gs_toev${n}`] || v[`toev${n}`]) || '', intensity: v[`gs_grad${n}`] || v[`grad${n}`] || null }))
      .filter(a => a.code);
    return N.formatCode({ soilType, median: N.MEDIAAN_GRONDSOORTEN.has(soilType) ? (v.mediaan || null) : null, admixtures });
  }

  _applyShortcodeIso(code) {
    const leeg = { gs_hoofd: '', mediaan: '', gs_toev1: '', gs_grad1: '', gs_toev2: '', gs_grad2: '', gs_toev3: '', gs_grad3: '' };
    if (!(code || '').trim()) { this._invalidShortcode = false; this._inlineVal = { ...this._inlineVal, ...leeg }; return; }
    const N = iso14688;
    const parsed = N.parseCode(code);
    if (!parsed) { this._invalidShortcode = true; return; }
    this._invalidShortcode = false;
    const term = (map, c) => { for (const [t, x] of map) if (x === c) return t; return ''; };
    const a = parsed.admixtures;
    const v = { ...leeg, gs_hoofd: term(N.GRONDSOORT, parsed.soilType), mediaan: parsed.median || '' };
    a.forEach((x, i) => { v[`gs_toev${i + 1}`] = term(N.BIJMENGING, x.code); v[`gs_grad${i + 1}`] = x.intensity || ''; });
    this._inlineVal = { ...this._inlineVal, ...v, extraAdmix: a.length >= 3 ? 2 : a.length >= 2 ? 1 : (this._inlineVal.extraAdmix ?? 0) };
  }

  _startInlineEdit5104(e, boringId, layerId) {
    // Zelfde afspraak als bij 6693: read-only mag uitklappen, niet bewerken.
    if (this.readonly) {
      e.stopPropagation();
      this._roLayer = this._roLayer === layerId ? null : layerId;
      return;
    }
    e.stopPropagation();
    if (this._inlineEdit?.layerId === layerId) { this._cancelInlineEdit(e); return; }
    this._layerFlush(); // wisselen van rij: eerst de vorige autosave wegschrijven
    const l = this._findFlatLayer(boringId, layerId);
    if (!l) return;

    const bzbItems = l.bzb?.length
      ? l.bzb.map(b => ({
          type:     this._codeToName(b.code, tiCodelists.LaSpecialCompounds) || b.type || '',
          gradatie: this._codeToName(b.gradation, tiCodelists.LaSpecialCompoundsGradiation) || '',
          herkomst: this._codeToName(b.origin, tiCodelists.LaSpecialCompoundsOrigin) || '',
        }))
      : [{ type: '', gradatie: '', herkomst: '' }];

    this._inlineEdit = { boringId, layerId };
    this._inlineVal  = {
      from:     String(l.upperDepth ?? ''),
      to:       String(l.lowerDepth ?? ''),
      drillingSystem: l.drillingSystem || '',
      gs_hoofd: this._codeToName(l.soilType, tiCodelists.bodemsoortHistorisch),
      gs_toev1: this._codeToName(l.toevoeging1, tiCodelists.toevoeging),
      gs_grad1: this._codeToName(l.gradatie1, tiCodelists.gradatie),
      gs_toev2: this._codeToName(l.toevoeging2, tiCodelists.toevoeging),
      gs_grad2: this._codeToName(l.gradatie2, tiCodelists.gradatie),
      gs_toev3: this._codeToName(l.toevoeging3, tiCodelists.toevoeging),
      gs_grad3: this._codeToName(l.gradatie3, tiCodelists.gradatie),
      extraAdmix: (l.toevoeging3 || l.gradatie3) ? 2 : (l.toevoeging2 || l.gradatie2) ? 1 : 0,
      mediaan:  this._codeToName(l.mediaan, tiCodelists.mediaan),
      kleurMain:  this._codeToName(l.kleurMain, tiCodelists.kleur),
      kleurSub:   this._codeToName(l.kleurSub, tiCodelists.kleur),
      kleurShade: this._codeToName(l.kleurShade, tiCodelists.kleurSterkte),
      oilWater: this._codeToName(l.oilWaterReaction, tiCodelists.olieWaterReactie),
      oilWaterObservation: l.oilWaterObservation || '',
      geur: this._codeToName(l.geurType, tiCodelists.geurType),
      geurIntensity: this._codeToName(l.geurIntensity, tiCodelists.geurSterkte) || 'geen',
      consistentie: l.consistentie || '',
      bzbItems,
      gsize: { ...(l.grainSize || {}) },
      opmerking: l.opmerking || '',
    };
  }

  /** Bouw het NEN 5104-update-payload uit een laag-werkkopie. */
  _layerPayload5104(v) {
    return {
      upperDepth:  Number(v.from),
      lowerDepth:  Number(v.to),
      drillingSystem: v.drillingSystem || null,
      soilType:    tiCodelists.bodemsoortHistorisch.get(v.gs_hoofd) || null,
      toevoeging1: tiCodelists.toevoeging.get(v.gs_toev1) || null,
      gradatie1:   tiCodelists.gradatie.get(v.gs_grad1) || null,
      toevoeging2: tiCodelists.toevoeging.get(v.gs_toev2) || null,
      gradatie2:   tiCodelists.gradatie.get(v.gs_grad2) || null,
      toevoeging3: tiCodelists.toevoeging.get(v.gs_toev3) || null,
      gradatie3:   tiCodelists.gradatie.get(v.gs_grad3) || null,
      mediaan:     mediaanApplies(v.gs_hoofd) ? (tiCodelists.mediaan.get(v.mediaan) || null) : null,
      kleurMain:   tiCodelists.kleur.get(v.kleurMain) || null,
      kleurSub:    tiCodelists.kleur.get(v.kleurSub) || null,
      kleurShade:  tiCodelists.kleurSterkte.get(v.kleurShade) || null,
      oilWaterReaction: tiCodelists.olieWaterReactie.get(v.oilWater) || null,
      geurType: tiCodelists.geurType.get(v.geur) || null,
      geurIntensity: v.geur ? (tiCodelists.geurSterkte.get(v.geurIntensity) ?? null) : null,
      grainSize:   this._gsizePayload(v),
      // De term, niet de TI-code -- net als bij NEN 6693. De vertaling naar codelijst 48 hoort
      // bij de export, zodat de database één vorm kent.
      consistentie: v.consistentie || null,
      // Alleen bij een reactie: een waarneming zonder reactie beschrijft niets.
      oilWaterObservation: (v.oilWater && v.oilWater !== 'Geen') ? (v.oilWaterObservation || null) : null,
      // NEN 5104-laag: geen mineraalarm/wortels-velden.
      mineraalarm: null,
      bzb: (v.bzbItems || []).filter(b => b.type).map(b => ({
        code:      tiCodelists.LaSpecialCompounds.get(b.type) || null,
        type:      b.type,
        gradation: tiCodelists.LaSpecialCompoundsGradiation.get(b.gradatie) || null,
        origin:    tiCodelists.LaSpecialCompoundsOrigin.get(b.herkomst) || null,
      })),
      opmerking: v.opmerking || null,
    };
  }

  _getShortcode5104(v) {
    if (!v) return '';
    const hoofd = v.gs_hoofd || v.hoofd;
    const h  = tiCodelists.bodemsoortHistorisch.get(hoofd) || '';
    const med = mediaanApplies(hoofd) ? (tiCodelists.mediaan.get(v.mediaan) || '') : '';
    const t1 = tiCodelists.toevoeging.get(v.gs_toev1 || v.toev1) || '';
    const g1 = tiCodelists.gradatie.get(v.gs_grad1 || v.grad1)   || '';
    const t2 = tiCodelists.toevoeging.get(v.gs_toev2 || v.toev2) || '';
    const g2 = tiCodelists.gradatie.get(v.gs_grad2 || v.grad2)   || '';
    const t3 = tiCodelists.toevoeging.get(v.gs_toev3 || v.toev3) || '';
    const g3 = tiCodelists.gradatie.get(v.gs_grad3 || v.grad3)   || '';
    return (h + med + t1 + g1 + t2 + g2 + t3 + g3).toUpperCase();
  }

  _getReverseMaps() {
    if (this._revMaps) return this._revMaps;
    const reverse = (map) => { const m = new Map(); for (const [k, v] of map) m.set(v, k); return m; };
    const medianByCode = new Map();
    for (const [name, codeVal] of tiCodelists.mediaan) { if (!medianByCode.has(codeVal)) medianByCode.set(codeVal, name); }
    this._revMaps = {
      soil:     reverse(tiCodelists.bodemsoortHistorisch),
      toev:     reverse(tiCodelists.toevoeging),
      grad:     reverse(tiCodelists.gradatie),
      color:    reverse(tiCodelists.kleur),
      shade:    reverse(tiCodelists.kleurSterkte),
      bzbGrad:  reverse(tiCodelists.LaSpecialCompoundsGradiation),
      bzbOrigin:reverse(tiCodelists.LaSpecialCompoundsOrigin),
      medianByCode,
    };
    return this._revMaps;
  }

  _getBzbShortcode5104(b) {
    if (!b?.type) return '';
    const typeCode = (tiCodelists.LaSpecialCompounds.get(b.type) || '').toUpperCase();
    if (!typeCode) return '';
    const gradCode   = b.gradatie ? (tiCodelists.LaSpecialCompoundsGradiation.get(b.gradatie) || '') : '';
    const originCode = b.herkomst ? (tiCodelists.LaSpecialCompoundsOrigin.get(b.herkomst)    || '') : '';
    return typeCode + gradCode + originCode;
  }

  _applyBzbShortcode5104(code, bi) {
    const raw = (code || '').trim().toUpperCase();
    if (!raw) {
      const bzbItems = this._inlineVal.bzbItems.map((it, i) => i === bi ? { ...it, type: '', gradatie: '', herkomst: '' } : it);
      this._inlineVal = { ...this._inlineVal, bzbItems };
      const inv = new Set(this._invalidBzbRows); inv.delete(bi); this._invalidBzbRows = inv;
      return;
    }
    if (raw.length < 2) { const inv = new Set(this._invalidBzbRows); inv.add(bi); this._invalidBzbRows = inv; return; }
    const typeCode = raw.slice(0, 2);
    let rest = raw.slice(2);
    const { bzbGrad, bzbOrigin } = this._getReverseMaps();
    let typeName = '';
    for (const [name, cd] of tiCodelists.LaSpecialCompounds) { if (cd.toUpperCase() === typeCode) { typeName = name; break; } }
    if (!typeName) { const inv = new Set(this._invalidBzbRows); inv.add(bi); this._invalidBzbRows = inv; return; }
    let herkomstName = '';
    if (rest.endsWith('AN') || rest.endsWith('GE')) {
      herkomstName = bzbOrigin.get(rest.slice(-2)) || '';
      if (!herkomstName) { const inv = new Set(this._invalidBzbRows); inv.add(bi); this._invalidBzbRows = inv; return; }
      rest = rest.slice(0, -2);
    }
    let gradatieName = '';
    if (rest) { gradatieName = bzbGrad.get(rest) || ''; if (!gradatieName) { const inv = new Set(this._invalidBzbRows); inv.add(bi); this._invalidBzbRows = inv; return; } }
    const inv = new Set(this._invalidBzbRows); inv.delete(bi); this._invalidBzbRows = inv;
    this._setBzbItem(bi, 'type', typeName);
    this._setBzbItem(bi, 'gradatie', gradatieName);
    this._setBzbItem(bi, 'herkomst', herkomstName);
  }

  _getColorShortcode5104(v) {
    if (!v) return '';
    const colorName = v.kleurMain || v.kleur || '';
    const subName   = v.kleurSub  || '';
    const shadeName = v.kleurShade || v.sterkte || '';
    const shade = tiCodelists.kleurSterkte.get(shadeName) || '';
    const color = tiCodelists.kleur.get(colorName) || '';
    const sub   = tiCodelists.kleur.get(subName) || '';
    return (shade + color + sub).toUpperCase();
  }

  _applyColorShortcode5104(code) {
    const c = (code || '').trim().toUpperCase();
    if (!c) { this._invalidColorShortcode = false; this._inlineVal = { ...this._inlineVal, kleurMain:'', kleurSub:'', kleurShade:'' }; return; }
    const SHADE_CODES = new Set(['LI','DR','NE','ZE','ZL']);
    const { color: colorRev, shade: shadeRev } = this._getReverseMaps();
    let shadeCode = '', mainCode = '', subCode = '';
    if (c.length === 2) { mainCode = c; }
    else if (c.length === 4) {
      if (SHADE_CODES.has(c.slice(0, 2))) { shadeCode = c.slice(0, 2); mainCode = c.slice(2, 4); }
      else { mainCode = c.slice(0, 2); subCode = c.slice(2, 4); }
    } else if (c.length === 6) { shadeCode = c.slice(0, 2); mainCode = c.slice(2, 4); subCode = c.slice(4, 6); }
    else { this._invalidColorShortcode = true; return; }
    const mainVal = colorRev.get(mainCode);
    const subVal  = subCode  ? colorRev.get(subCode)  : undefined;
    const shadeVal = shadeCode ? shadeRev.get(shadeCode) : undefined;
    if (!mainVal || (subCode && !subVal) || (shadeCode && !shadeVal)) { this._invalidColorShortcode = true; return; }
    this._invalidColorShortcode = false;
    this._inlineVal = { ...this._inlineVal, kleurMain: mainVal, kleurSub: subVal || '', kleurShade: shadeVal || '' };
  }

  _applyShortcode5104(code) {
    const c = (code || '').trim().toUpperCase();
    if (!c) { this._invalidShortcode = false; this._inlineVal = { ...this._inlineVal, gs_hoofd:'', mediaan:'', gs_toev1:'', gs_grad1:'', gs_toev2:'', gs_grad2:'', gs_toev3:'', gs_grad3:'' }; return; }
    const { soil: soilRev, toev: toevRev, grad: gradRev, medianByCode } = this._getReverseMaps();
    const h = soilRev.get(c[0]);
    if (!h) { this._invalidShortcode = true; return; }
    let rest = c.slice(1);
    let mediaan = '';
    if (mediaanApplies(h) && /^[1-6]/.test(rest)) {
      mediaan = medianByCode.get(rest[0]) || '';
      if (!mediaan) { this._invalidShortcode = true; return; }
      rest = rest.slice(1);
    }
    const pairs = [];
    while (rest.length) {
      const m = rest.match(/^([A-Z])([1-4])?/);
      if (!m) { this._invalidShortcode = true; return; }
      const t = toevRev.get(m[1]);
      const g = m[2] ? gradRev.get(m[2]) : '';
      if (!t || (m[2] && !g)) { this._invalidShortcode = true; return; }
      pairs.push({ t, g });
      if (pairs.length > 3) { this._invalidShortcode = true; return; }
      rest = rest.slice(m[0].length);
    }
    this._invalidShortcode = false;
    this._inlineVal = {
      ...this._inlineVal,
      gs_hoofd: h, mediaan,
      gs_toev1: pairs[0]?.t || '', gs_grad1: pairs[0]?.g || '',
      gs_toev2: pairs[1]?.t || '', gs_grad2: pairs[1]?.g || '',
      gs_toev3: pairs[2]?.t || '', gs_grad3: pairs[2]?.g || '',
      extraAdmix: pairs.length >= 3 ? 2 : pairs.length >= 2 ? 1 : (this._inlineVal.extraAdmix ?? 0),
    };
  }

  _renderLagenTab5104(mp, mpLagen, mpMonsters) {
    if (!mpLagen.length) {
      return html`
        <div style="padding:0.75rem 0.9rem; color:var(--mv-text-muted); font-size:0.85rem;">Geen lagen gevonden.</div>
        <div style="padding:0.5rem 0.9rem; border-top:1px solid var(--mv-border);">${this._renderAddLayerButton(mp, mpLagen)}</div>
      `;
    }
    const _hb = this._hlBoring(mp);
    const conflicts = mp.depthConflicts;
    const conflictByIdx = new Map(conflicts.map(c => [c.afterIdx, c]));
    const soilOpts = [...tiCodelists.bodemsoort.keys()];
    const toevOpts = [...tiCodelists.toevoeging.keys()];
    const gradOpts = [...tiCodelists.gradatie.keys()];
    const mediaanSeen = new Set();
    const mediaanOpts = [...tiCodelists.mediaan.keys()].filter(k => { const val = tiCodelists.mediaan.get(k); if (mediaanSeen.has(val)) return false; mediaanSeen.add(val); return true; });
    const kleurOpts = [...tiCodelists.kleur.keys()].sort((a, b) => a.localeCompare(b, 'nl'));
    const shadeOpts = [...tiCodelists.kleurSterkte.keys()];
    const bzbOpts   = [...tiCodelists.LaSpecialCompounds.keys()];
    const bzbGOpts  = [...tiCodelists.LaSpecialCompoundsGradiation.keys()];
    const origOpts  = [...tiCodelists.LaSpecialCompoundsOrigin.keys()];
    return html`
      <div>
        <div class="layer-header"><span>Diepte (cm)</span><span>Grondsoort</span><span>Kleur</span><span>Bijzonderheden</span></div>
        ${mpLagen.map((l, idx) => {
          // Read-only kent zijn eigen uitgeklapte rij: _inlineEdit hoort bij het bewerken en
          // blijft daar leeg.
          const isActive = this.readonly ? this._roLayer === l.id : this._inlineEdit?.layerId === l.id;
          const isLast   = idx === mpLagen.length - 1 && mpMonsters.length === 0;
          const iv       = this._inlineVal;
          const _toev1 = l.toevoeging1 ? this._codeToName(l.toevoeging1, tiCodelists.toevoeging) : '';
          const _grad1 = l.gradatie1   ? this._codeToName(l.gradatie1,   tiCodelists.gradatie)   : '';
          const _toev2 = l.toevoeging2 ? this._codeToName(l.toevoeging2, tiCodelists.toevoeging) : '';
          const _grad2 = l.gradatie2   ? this._codeToName(l.gradatie2,   tiCodelists.gradatie)   : '';
          const _toev3 = l.toevoeging3 ? this._codeToName(l.toevoeging3, tiCodelists.toevoeging) : '';
          const _grad3 = l.gradatie3   ? this._codeToName(l.gradatie3,   tiCodelists.gradatie)   : '';
          const soil = [
            l.soilLabel,
            _toev1 ? [_toev1, _grad1].filter(Boolean).join(' ') : null,
            _toev2 ? [_toev2, _grad2].filter(Boolean).join(' ') : null,
            _toev3 ? [_toev3, _grad3].filter(Boolean).join(' ') : null,
          ].filter(Boolean).join(', ');
          const extra = [l.bzbLabels.join(', '), l.opmerking || ''].filter(Boolean).join(' · ') || '-';
          const _lhl = _hb?.layers?.[idx];
          const HL = 'background:#f1416c1f;color:#f1416c;border-radius:4px;padding:0 4px;font-weight:600;';
          const hlCol = (col) => _lhl && (_lhl.extra || _lhl.cols?.has(col)) ? HL : '';
          return html`
            <div class="layer-row ${isActive ? 'is-active' : ''}"
              style="border-left:3px solid ${_lhl?.extra ? '#f1416c' : l.colorHex}; ${isLast && !isActive ? 'border-bottom:none;' : ''} align-items:center;"
              @click=${(e) => this._startInlineEdit(e, mp.guid, l.id)}>
              <span style="font-weight:700;font-family:monospace;color:var(--mv-text-sub);${hlCol('diepte')}">${l.upperDepth}–${l.lowerDepth}</span>
              <span style="font-weight:500;${hlCol('grondsoort')}">${soil}${mediaanApplies(l.soilType) && l.mediaanLabel ? html` <span style="font-weight:400; color:var(--mv-text-muted); font-size:0.85em;">(${l.mediaanLabel})</span>` : ''}</span>
              <span style="color:var(--mv-text-sub);${hlCol('kleur')}">${l.kleurLabel || '-'}</span>
              <span style="color:var(--mv-text-sub);display:flex;align-items:center;gap:4px;">
                <span style="flex:1;min-width:0;${hlCol('bijzonderheden')}">${extra}</span>
                ${this.readonly
                  // Read-only: een chevron in plaats van het potlood. Zonder enig teken lijkt de
                  // rij dood en komt niemand op het idee erop te klikken; een potlood zou juist
                  // bewerken beloven.
                  ? html`<span class="row-edit-hint" style="display:flex; transform:rotate(${isActive ? 180 : 0}deg);">${unsafeSVG(lucideIcon(ChevronDown, 11))}</span>`
                  : html`<span class="row-edit-hint">${unsafeSVG(lucideIcon(Edit2, 11))}</span>`}
              </span>
            </div>
            ${isActive ? (this.readonly
              ? this._renderLayerReadOnly(l, l.soilLabel, [[_toev1, _grad1], [_toev2, _grad2], [_toev3, _grad3]])
              : this._renderLayerEditForm5104(iv, idx, mp.guid, l, soilOpts, toevOpts, gradOpts, mediaanOpts, kleurOpts, shadeOpts, bzbOpts, bzbGOpts, origOpts)) : ''}
            ${conflictByIdx.has(idx) ? (() => {
              const c = conflictByIdx.get(idx);
              const isOverlap = c.type === 'overlap';
              return html`
                <div style="display:flex; align-items:center; gap:6px; padding:3px 10px; font-size:0.75rem; font-weight:600;
                  background:${isOverlap ? 'rgba(239,68,68,0.07)' : 'rgba(245,158,11,0.07)'};
                  color:${isOverlap ? '#DC2626' : '#D97706'};
                  border-left:3px solid ${isOverlap ? '#DC2626' : '#F59E0B'};">
                  ${unsafeSVG(lucideIcon(AlertTriangle, 11))}
                  <span style="flex:1;">${isOverlap ? `Overlapping: laag eindigt op ${c.a.lowerDepth} cm maar volgende begint op ${c.b.upperDepth} cm` : `Hiaat: laag eindigt op ${c.a.lowerDepth} cm maar volgende begint op ${c.b.upperDepth} cm`}</span>
                  <button @click=${e => this._fixDepthConflict(e, c)} style="background:${isOverlap ? '#DC2626' : '#D97706'}; color:#fff; border:none; border-radius:4px; padding:2px 8px; font-size:0.7rem; font-weight:700; cursor:pointer; white-space:nowrap;">Herstellen</button>
                </div>`;
            })() : ''}
          `;
        })}
      </div>
      ${_hb?.missingLayers ? html`
        <div style="display:flex; align-items:center; gap:6px; padding:5px 12px; font-size:0.78rem; font-weight:600; color:#f1416c; background:#f1416c14; border-top:1px solid var(--mv-border);">
          ${unsafeSVG(lucideIcon(AlertTriangle, 12))}<span>${_hb.missingLayers} laag/lagen minder dan verwacht.</span>
        </div>` : ''}
      <div style="padding:0.5rem 0.9rem; border-top:1px solid var(--mv-border);">${this._renderAddLayerButton(mp, mpLagen)}</div>
    `;
  }

  _renderLayerEditForm5104(iv, idx, boringGuid, l, soilOpts, toevOpts, gradOpts, mediaanOpts, kleurOpts, shadeOpts, bzbOpts, bzbGOpts, origOpts) {
    const codeVeld = this._grondsoortCode(iv, 'ZS1G1', 96, true);
    return html`
      <div class="layer-expand" @click=${e => e.stopPropagation()} @keydown=${e => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') this._saveInlineEdit(e); }}>
        ${this._renderDiepteRij(iv)}
        <div class="laagdeel">
        ${this._sectieKop('Grondsoort', '', this.grainSizeInput ? '' : codeVeld)}
        ${this._renderKorrelverdeling(iv)}
        <div class="grondsoort-blok ${this._gsizeKlasse(iv) ? 'vast' : ''}"
             @click=${() => { if (this._gsizeKlasse(iv)) this._korrelUitlegTonen(); }}>
          ${!this.grainSizeInput ? '' : html`
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
            <div style="display:flex; align-items:center; gap:8px;">
              <div class="expand-section-title" style="margin:0;">Grondsoort</div>
              ${this._gsizeKlasse(iv) ? '' : html`
                <span class="korrel-chip uit">Handmatig</span>
                <span class="korrel-uitleg">Typ een code of kies hieronder. Vul een percentage in om het automatisch te bepalen.</span>`}
            </div>
            ${codeVeld}
          </div>`}
          ${!this._gsizeKlasse(iv) || !this._korrelUitleg ? '' : html`
            <div class="korrel-let-op">
              <span>Deze velden volgen de korrelverdeling en worden bij elke wijziging
                    overschreven. Wis de percentages om handmatig te kiezen.</span>
              <button type="button" class="korrel-wissen" @click=${() => this._gsizeWissen()}>
                Percentages wissen
              </button>
            </div>`}
          ${(() => {
            // 'Vast' = door de korrelverdeling bepaald. Te lezen, niet te wijzigen: twee bronnen
            // voor dezelfde waarheid lopen vroeg of laat uit elkaar. `v` is wat er in beeld
            // hoort te staan -- de uitkomst, of de eigen keuze als er geen percentages zijn.
            const _klasse = this._gsizeKlasse(iv);
            const vast = !!_klasse;
            const v = this._gsizeView(iv, _klasse);
            const autoMerk = vast ? html` <span class="auto-merk">auto</span>` : '';
            const showMediaan = mediaanApplies(v.gs_hoofd);
            const extra = v.extraAdmix ?? 0;
            const two = 'grid-template-columns:1fr 1fr;';
            const admixRow = (label, toevKey, gradKey, toevVal, gradVal, onRemove) => html`
              <div class="expand-field-group admix-rij" style="margin-bottom:6px;">
                <div class="expand-field ${vast ? 'auto' : ''}"><label>${label}${autoMerk}</label>
                  <select ?disabled=${vast} @change=${e => this._setIV(toevKey, e.target.value)}><option value="">—</option>${toevOpts.map(o => html`<option value=${o} ?selected=${o === toevVal}>${o}</option>`)}</select>
                </div>
                <div class="expand-field ${vast ? 'auto' : ''}">
                  <label><span>${label === 'Bijmenging' ? 'Intensiteit' : `Intensiteit ${label.split(' ')[1]}`}${autoMerk}</span></label>
                  <select ?disabled=${vast} @change=${e => this._setIV(gradKey, e.target.value)}><option value="">—</option>${gradOpts.map(o => html`<option value=${o} ?selected=${o === gradVal}>${o}</option>`)}</select>
                </div>
                ${onRemove && !vast
                  ? html`<button type="button" class="bzb-del-btn" title="Bijmenging verwijderen"
                           @click=${onRemove}>${unsafeSVG(lucideIcon(X, 12))}</button>`
                  : html`<span></span>`}
              </div>`;
            return html`
              <div class="expand-field-group zonder-knop-kolom" style="${two}margin-bottom:6px;">
                <div class="expand-field ${vast ? 'auto' : ''}"><label>Hoofdbestanddeel${autoMerk}</label>
                  <select ?disabled=${vast} @change=${e => { const val = e.target.value; this._setIV('gs_hoofd', val); if (!mediaanApplies(val)) this._setIV('mediaan', ''); }}>
                    <option value="">— kies —</option>${(v.gs_hoofd && !soilOpts.includes(v.gs_hoofd) ? [...soilOpts, v.gs_hoofd] : soilOpts).map(o => html`<option value=${o} ?selected=${o === v.gs_hoofd}>${o}</option>`)}
                  </select>
                </div>
                ${showMediaan ? html`<div class="expand-field ${vast ? 'auto' : ''}"><label>Mediaan${autoMerk}</label><select ?disabled=${vast} @change=${e => this._setIV('mediaan', e.target.value)}><option value="">—</option>${mediaanOpts.map(o => html`<option value=${o} ?selected=${o === v.mediaan}>${o}</option>`)}</select></div>` : html`<div></div>`}
              </div>
              ${admixRow('Bijmenging', 'gs_toev1', 'gs_grad1', v.gs_toev1, v.gs_grad1, () => this._removeAdmix(1))}
              ${extra >= 1 ? admixRow('Bijmenging 2', 'gs_toev2', 'gs_grad2', v.gs_toev2, v.gs_grad2, vast ? null : () => this._removeAdmix(2)) : ''}
              ${extra >= 2 ? admixRow('Bijmenging 3', 'gs_toev3', 'gs_grad3', v.gs_toev3, v.gs_grad3, vast ? null : () => this._removeAdmix(3)) : ''}
              ${vast
                ? html`<div class="vast-tegel" style="margin-top:2px;">Bijmengingen worden bepaald door de korrelverdeling</div>`
                : extra < 2 ? html`<button class="bzb-add-btn" style="margin-top:2px;" @click=${() => this._setIV('extraAdmix', Math.min(2, (iv.extraAdmix ?? 0) + 1))}>+ Bijmenging toevoegen</button>` : ''}
            `;
          })()}
        </div>
        </div>
        <div>
          ${this._sectieKop('Kleur')}
          <div class="bzb-tegels">
            <div class="bzb-tegel">
              <div class="expand-field"><label>Hoofdkleur</label><select @change=${e => this._setIV('kleurMain', e.target.value)}><option value="">—</option>${kleurOpts.map(o => html`<option value=${o} ?selected=${o === iv.kleurMain}>${o}</option>`)}</select></div>
              <div class="expand-field"><label>Subkleur</label><select @change=${e => this._setIV('kleurSub', e.target.value)}><option value="">—</option>${kleurOpts.map(o => html`<option value=${o} ?selected=${o === iv.kleurSub}>${o}</option>`)}</select></div>
              <div class="expand-field"><label>Tint</label><select @change=${e => this._setIV('kleurShade', e.target.value)}><option value="">—</option>${shadeOpts.map(o => html`<option value=${o} ?selected=${o === iv.kleurShade}>${o}</option>`)}</select></div>
              <div class="expand-field">
                <label>Code</label>
                <input type="text" title="Kleurcode, bijvoorbeeld LIGR" class="bzb-code"
                  .value=${this._getColorShortcode(iv)}
                  @input=${e => this._applyColorShortcode(e.target.value)}
                  style="border-color:${this._invalidColorShortcode ? '#DC2626' : 'var(--mv-border)'};
                         color:${this._invalidColorShortcode ? '#DC2626' : 'var(--mv-primary)'};">
              </div>
              <span></span>
            </div>
          </div>
        </div>

        <div>
          ${this._sectieKop('Bijzondere bestanddelen')}
          <div class="bzb-tegels">
            ${!(iv.bzbItems || []).length ? html`
              <div class="bzb-leeg">Nog geen bijzondere bestanddelen.</div>` :
              (iv.bzbItems || []).map((b, bi) => this._renderBzbTegel(iv, b, bi, bzbOpts, bzbGOpts, origOpts, false))}
            <button class="bzb-add-btn"
              @click=${() => this._inlineVal = { ...iv, bzbItems: [...(iv.bzbItems||[]), { type:'', gradatie:'', herkomst:'' }] }}>
              + BZB toevoegen
            </button>
          </div>
        </div>

        ${this._renderWaarnemingen(iv, true)}
        <div class="expand-actions" style="align-items:center;">
          <button class="expand-danger-btn" @click=${e => this._removeLayer(e)}>${unsafeSVG(lucideIcon(Trash2, 13))} Verwijderen</button>
          <button class="expand-cancel-btn" style="margin-left:auto;" @click=${e => this._cancelInlineEdit(e)}>Sluiten</button>
        </div>
      </div>`;
  }

  _renderPotjesTab(boreholeJars, rawBoring, displayName) {
    return html`
      ${boreholeJars.length > 0 ? html`
        <div>
          <div class="layer-header" style="grid-template-columns:minmax(0,1fr) 110px;">
            <span>Naam / Barcode</span><span>Diepte (cm)</span>
          </div>
          ${boreholeJars.map((jar, jarIdx) => {
            const isEditing = jarIdx === boreholeJars.findIndex(j => this._jarSleutel(j) === this._jarEdit);
            return html`
              <div class="layer-row ${isEditing ? 'is-active' : ''}"
                style="grid-template-columns:minmax(0,1fr) 110px; align-items:center;"
                @click=${(e) => this._startJarEdit(e, jar, rawBoring)}>
                <span style="display:flex; flex-direction:column; gap:1px;">
                  <span style="font-weight:600; display:flex; align-items:center; gap:6px;">
                    ${unsafeSVG(lucideIcon(FileText, 12))} ${jar.name || jar.barcode || '—'}
                  </span>
                  ${jar.barcode && jar.name !== jar.barcode ? html`
                    <span style="font-family:monospace; font-size:0.75em; color:var(--mv-text-muted);">${jar.barcode}</span>
                  ` : ''}
                </span>
                <span style="font-family:monospace; font-weight:700; color:var(--mv-text-sub); display:flex; align-items:center; gap:4px;">
                  <span style="flex:1;">${jar.layerDepthFrom != null ? `${jar.layerDepthFrom}–${jar.layerDepthTo}` : '—'}</span>
                  <!-- Geen chevron bij een grondmonster: die rij klapt in read-only niet uit,
                     en een teken dat niets doet is erger dan geen teken. -->
                ${this.readonly ? '' : html`<span class="row-edit-hint">${unsafeSVG(lucideIcon(Edit2, 11))}</span>`}
                </span>
              </div>
              ${isEditing ? html`
                <div class="layer-expand" @click=${e => e.stopPropagation()}>
                  <div class="expand-field">
                    <label>Naam</label>
                    <input type="text" .value=${this._jarVal.name}
                      @input=${e => this._jarVal = { ...this._jarVal, name: e.target.value }}>
                  </div>
                  <div style="display:flex; align-items:flex-start; gap:10px;">
                    <div class="expand-field" style="flex:2;">
                      <label>Barcode</label>
                      <div style="display:flex; gap:6px;">
                        <input type="text" style="flex:1; min-width:0;" placeholder="typ of scan" .value=${this._jarVal.barcode}
                          @input=${e => this._jarVal = { ...this._jarVal, barcode: e.target.value }}>
                        <button type="button" class="scan-knop" title="Barcode scannen met de camera" aria-label="Barcode scannen met de camera"
                          @click=${() => { this._jarScan = true; }}>
                          ${unsafeSVG(lucideIcon(ScanLine, 16))}
                        </button>
                      </div>
                      ${this._barcodeAlGebruikt(this._jarVal.barcode, rawBoring) ? html`
                        <div style="color:var(--mv-danger,#dc2626); font-size:0.75rem; margin-top:3px;">
                          Deze barcode zit al op ${this._barcodeAlGebruikt(this._jarVal.barcode, rawBoring)}.
                        </div>` : ''}
                      <camera-venster .open=${this._jarScan} modus="barcode" titel="Barcode scannen"
                        .duplicaatVan=${(code) => this._barcodeAlGebruikt(code, rawBoring)}
                        @sluit=${() => { this._jarScan = false; }}
                        @barcode=${(ev) => { this._jarScan = false; this._jarVal = { ...this._jarVal, barcode: ev.detail.code }; }}></camera-venster>
                    </div>
                    <div class="expand-field" style="flex:1;">
                      <label>Geroerd</label>
                      <div style="display:flex; gap:6px;">
                        ${[true, false].map(opt => {
                          const active = this._jarVal.geroerd === opt;
                          const activeColor = opt ? 'var(--mv-primary)' : '#DC2626';
                          return html`
                            <button type="button"
                              @click=${() => this._jarVal = { ...this._jarVal, geroerd: opt }}
                              style="flex:1; padding:5px 8px; border-radius:5px; border:1px solid ${active ? activeColor : 'var(--mv-border)'}; background:${active ? activeColor : 'var(--mv-surface)'}; color:${active ? '#fff' : 'var(--mv-text)'}; font-weight:700; font-size:0.82rem; font-family:inherit; box-sizing:border-box; cursor:pointer;">
                              ${opt ? 'Ja' : 'Nee'}
                            </button>`;
                        })}
                      </div>
                    </div>
                  </div>
                  <div style="display:flex; align-items:center; gap:8px;">
                    <div class="expand-field" style="flex:1;">
                      <label>Van (cm)</label>
                      <input type="number" .value=${this._jarVal.from}
                        @input=${e => this._jarVal = { ...this._jarVal, from: e.target.value }}>
                    </div>
                    <span style="margin-top:18px; color:var(--mv-text-muted);">–</span>
                    <div class="expand-field" style="flex:1;">
                      <label>Tot (cm)</label>
                      <input type="number" .value=${this._jarVal.to}
                        @input=${e => this._jarVal = { ...this._jarVal, to: e.target.value }}>
                    </div>
                  </div>
                  <div class="expand-field">
                    <label>Verpakking</label>
                    <searchable-select
                      .options=${VERPAKKING_OPTIONS}
                      .value=${this._jarVal.packingType || ''}
                      placeholder="Kies verpakking…"
                      @change=${e => this._jarVal = { ...this._jarVal, packingType: e.detail.value || null }}></searchable-select>
                  </div>
                  <div class="expand-actions" style="align-items:center;">
                    <button class="expand-danger-btn" @click=${(e) => this._deleteJar(e, jar, rawBoring)}>
                      ${unsafeSVG(lucideIcon(Trash2, 13))} Verwijderen
                    </button>
                    <button class="expand-cancel-btn" style="margin-left:auto;" @click=${(e) => this._cancelJarEdit(e)}>Sluiten</button>
                  </div>
                </div>
              ` : ''}
            `;
          })}
        </div>
      ` : html`<div style="padding:0.75rem 0.9rem; color:var(--mv-text-muted); font-size:0.85rem;">Geen grondmonsters gevonden.</div>`}
      ${this.readonly ? '' : html`
      <div style="padding:0.5rem 0.9rem; border-top:1px solid var(--mv-border);">
        <button class="bzb-add-btn" style="width:100%;"
          @click=${(e) => this._addJar(e, displayName, rawBoring)}>
          ${unsafeSVG(lucideIcon(Plus, 12))} Grondmonster toevoegen
        </button>
      </div>`}
    `;
  }

  // ── Watermonster (redesign: monsterkaarten + sectiebalk + flessentabel + autosave) ──
  _wmCfg() {
    return {
      sections: [
        { key: 'kern', label: 'Algemeen', fields: ['name', 'date', 'time', 'sampler', 'matrix', 'remark'] },
        { key: 'grondwater', label: 'Grondwater', fields: ['gwsBeforePrepumpCm', 'gwsAfterPrepumpCm', 'gwsBeforeSamplingCm', 'prepumpVolumeL', 'recommendedPrepumpVolumeL', 'flushVolumeL', 'pumpType', 'flowRate', 'aerated', 'yield', 'gwsReferencePoint'] },
        { key: 'veldmeting', label: 'Veldmeting', fields: ['ec', 'ph', 'temperature', 'oxygen', 'redox', 'turbidityPrepump', 'turbiditySample'] },
        { key: 'zintuiglijk', label: 'Zintuiglijk', fields: ['colourShade', 'colourMain', 'colourSub', 'clarity', 'specialConstituents'] },
        { key: 'drijflaag', label: 'Drijflaag', fields: ['floatingLayer', 'floatingLayerThicknessMm', 'sinkingLayer', 'sinkingLayerThicknessMm'] },
      ],
      field: {
        name: { label: 'Monstercode', t: 'text', req: true }, date: { label: 'Datum', t: 'date', req: true }, time: { label: 'Tijd', t: 'time' }, sampler: { label: 'Monsternemer', t: 'text' }, matrix: { label: 'Matrix', t: 'sel', o: WATERMONSTER_MATRIX }, remark: { label: 'Opmerking', t: 'text', full: true },
        gwsBeforePrepumpCm: { label: 'GWS voor voorpompen (cm)', t: 'num' }, gwsAfterPrepumpCm: { label: 'GWS na voorpompen (cm)', t: 'num' }, gwsBeforeSamplingCm: { label: 'GWS voor bemonstering (cm)', t: 'num' }, prepumpVolumeL: { label: 'Voorgepompt volume (l)', t: 'num' }, recommendedPrepumpVolumeL: { label: 'Volume te voorpompen (l)', t: 'num' }, flushVolumeL: { label: 'Spoelvolume (l)', t: 'num' }, pumpType: { label: 'Pomptype', t: 'sel', o: WATERMONSTER_POMPTYPE, alias: WATERMONSTER_POMPTYPE_ALIASES }, flowRate: { label: 'Debiet (l/min)', t: 'num' }, aerated: { label: 'Belucht', t: 'yn' }, yield: { label: 'Opbrengst', t: 'sel', o: WATERMONSTER_OPBRENGST }, gwsReferencePoint: { label: 'Ref.vlak GWS', t: 'ref' },
        ec: { label: 'EC (µS/cm)', t: 'num' }, ph: { label: 'pH', t: 'num' }, temperature: { label: 'Temperatuur (°C)', t: 'num' }, oxygen: { label: 'Zuurstof (mg/l)', t: 'num' }, redox: { label: 'Redox (mV)', t: 'num' }, turbidityPrepump: { label: 'Troebelheid voorpompen (NTU)', t: 'num' }, turbiditySample: { label: 'Troebelheid monster (NTU)', t: 'num' },
        colourShade: { label: 'Kleursterkte', t: 'tint' }, colourMain: { label: 'Hoofdkleur', t: 'kleur' }, colourSub: { label: 'Bijkleur', t: 'kleur' }, clarity: { label: 'Transparantie', t: 'sel', o: WATERMONSTER_HELDERHEID }, specialConstituents: { label: 'Bijzondere bestanddelen', t: 'text', full: true },
        floatingLayer: { label: 'Drijflaag', t: 'yn' }, floatingLayerThicknessMm: { label: 'Dikte drijflaag (mm)', t: 'num' }, sinkingLayer: { label: 'Zaklaag', t: 'yn' }, sinkingLayerThicknessMm: { label: 'Dikte zaklaag (mm)', t: 'num' },
      },
    };
  }
  /**
   * De watermonsters van een boring, elk met een gegarandeerd id.
   *
   * Een watermonster wordt op id geselecteerd, bewerkt én verwijderd. Uit de veldapp komen
   * monsters zonder id de blob in; die tegel deed dan niets (het detail bleef leeg, want er
   * viel niets te selecteren) en waren er twee, dan trof een bewerking of verwijdering ze
   * allebei — `w.id === undefined` klopt immers voor elk id-loos monster.
   *
   * Ontbreekt het id, dan vullen we er hier één in. Per (boring, positie) onthouden we welk
   * id we verzonnen, zodat hij niet bij elke render verspringt; bij de eerstvolgende opslag
   * gaat het hele blob mee terug naar de server en is het monster blijvend geheeld.
   */
  _wmList(boringId) {
    const raw = (this.borings || []).find(b => b.id === boringId);
    return (raw?.watermonsters || []).map((wm, i) => {
      if (!wm || wm.id) return wm;
      const key = `${boringId}#${i}`;
      this._wmMintedIds[key] = this._wmMintedIds[key] || crypto.randomUUID();
      return { ...wm, id: this._wmMintedIds[key] };
    });
  }
  /** Actuele waarde: de niet-opgeslagen draft, anders het opgeslagen watermonster. */
  _wmValue(boringId, wmId) {
    const draft = this._wmFormVals?.[wmId];
    if (draft) return draft;
    return this._wmList(boringId).find(w => w.id === wmId) || null;
  }
  _wmEditField(boringId, wmId, field, value) {
    const cur = this._wmValue(boringId, wmId) || {};
    this._wmFormVals = { ...this._wmFormVals, [wmId]: { ...cur, [field]: value } };
    this._wmSaveSchedule(boringId, wmId);
  }
  _wmSaveSchedule(boringId, wmId) {
    this._wmSaveState = { ...this._wmSaveState, [boringId]: 'saving' };
    clearTimeout(this._wmSaveTimers[boringId]);
    this._wmSaveTimers[boringId] = setTimeout(() => this._wmSaveNow(boringId, wmId), 700);
  }
  _wmPersist(boringId, next) {
    this.borings = (this.borings || []).map(b => b.id === boringId ? { ...b, watermonsters: next } : b);
    this._wmSaveState = { ...this._wmSaveState, [boringId]: 'saving' };
    Promise.resolve(this._crud.updateBoring(boringId, { watermonsters: next }))
      .then(() => { this._wmSaveState = { ...this._wmSaveState, [boringId]: 'saved' }; })
      .catch(() => { this._wmSaveState = { ...this._wmSaveState, [boringId]: 'error' }; });
  }
  _wmSaveNow(boringId, wmId) {
    const draft = this._wmFormVals?.[wmId];
    if (!draft) { this._wmSaveState = { ...this._wmSaveState, [boringId]: 'saved' }; return; }
    const cur = this._wmList(boringId);
    const next = cur.some(w => w.id === wmId) ? cur.map(w => w.id === wmId ? draft : w) : [...cur, draft];
    this._wmPersist(boringId, next);
  }
  _wmAddMonster(boringId, peilbuizen) {
    if (this.readonly) return;
    const cur = this._wmList(boringId);
    const wmCount = cur.length;
    const pb = peilbuizen?.[0];
    const code = pb?.name ? `${pb.name}-${wmCount + 1}` : '';
    let today = ''; try { today = new Date().toISOString().slice(0, 10); } catch (_) {}
    // Tijd (lokaal, HH:MM) net als de datum automatisch voorvullen bij een nieuw watermonster.
    let now = ''; try { now = new Date().toTimeString().slice(0, 5); } catch (_) {}
    const id = crypto.randomUUID();
    // Sjabloon-defaults; leeg = geen default, dan blijft de bestaande fallback gelden.
    const t = this.templateDefaults?.watermonster || {};
    const jaNee = (v) => (v === 'ja' ? true : v === 'nee' ? false : null);
    const wm = {
      id, name: code, date: today, time: now,
      sampler: t.sampler || this._currentUserName || '',
      matrix: t.matrix || 'grondwater',
      pumpType: t.pumpType || null,
      aerated: jaNee(t.aerated),
      gwsReferencePoint: t.gwsReferencePoint || null,
      peilbuisId: pb?.id ?? null, bottles: [],
    };
    this._wmFormVals = { ...this._wmFormVals, [id]: wm };
    this._wmSel = { ...this._wmSel, [boringId]: id };
    this._wmSection = { ...this._wmSection, [boringId]: 'kern' };
    this._wmPersist(boringId, [...cur, wm]);
  }
  _wmSelect(boringId, wmId) { this._wmSel = { ...this._wmSel, [boringId]: wmId }; }
  _wmSetSection(boringId, key) { this._wmSection = { ...this._wmSection, [boringId]: key }; }
  async _wmDeleteMonster(boringId, wmId) {
    const ok = await confirmDialog({ title: 'Watermonster verwijderen', message: 'Weet je zeker dat je dit watermonster wilt verwijderen?', confirmLabel: 'Verwijderen', danger: true });
    if (!ok) return;
    const next = this._wmList(boringId).filter(w => w.id !== wmId);
    const fv = { ...this._wmFormVals }; delete fv[wmId]; this._wmFormVals = fv;
    const sel = { ...this._wmSel }; if (sel[boringId] === wmId) sel[boringId] = next[0]?.id ?? null; this._wmSel = sel;
    this._wmPersist(boringId, next);
  }
  _wmEditBottle(boringId, wmId, idx, field, value) {
    const cur = this._wmValue(boringId, wmId) || {};
    this._wmEditField(boringId, wmId, 'bottles', (cur.bottles || []).map((b, i) => i === idx ? { ...b, [field]: value } : b));
  }
  _wmAddBottle(boringId, wmId) {
    const cur = this._wmValue(boringId, wmId) || {};
    const t = this.templateDefaults?.watermonster || {};
    const filtered = t.filtered === 'ja' ? true : t.filtered === 'nee' ? false : null;
    this._wmEditField(boringId, wmId, 'bottles', [...(cur.bottles || []), {
      id: crypto.randomUUID(), barcode: '',
      filtered, filterType: t.filterType || '', bottleType: t.bottleType || '', remark: '',
    }]);
  }
  _wmDelBottle(boringId, wmId, idx) {
    const cur = this._wmValue(boringId, wmId) || {};
    this._wmEditField(boringId, wmId, 'bottles', (cur.bottles || []).filter((_, i) => i !== idx));
  }
  _wmMissing(wm) {
    return !(wm.name || '').trim() || !(wm.date || '').trim() || (wm.bottles || []).some(b => !(b.barcode || '').trim());
  }
  _wmFilled(wm, fields) { return fields.filter(f => { const x = wm[f]; return x !== '' && x != null; }).length; }

  _renderWatermonsterTab(boringId, peilbuizen) {
    const cfg = this._wmCfg();
    const items = this._wmList(boringId).map(wm => this._wmValue(boringId, wm.id) || wm);
    const selId = this._wmSel?.[boringId] ?? items[0]?.id ?? null;
    const selWm = selId ? (items.find(w => w.id === selId) || null) : null;
    const section = this._wmSection?.[boringId] || 'kern';
    return html`
      <div class="afw-tab" style="padding:0.7rem 0.9rem;">
        <div style="margin-bottom:10px;">
          <span style="font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-muted);">Watermonsters ${items.length}</span>
        </div>

        <div style="display:flex; gap:8px; flex-wrap:wrap; margin-bottom:12px;">
          ${items.map(wm => this._renderWmCard(boringId, wm, wm.id === selId, cfg))}
          ${this.readonly ? '' : html`<button title="Watermonster toevoegen" @click=${() => this._wmAddMonster(boringId, peilbuizen)} style="width:44px; min-height:60px; border:1.5px dashed var(--mv-border); border-radius:8px; background:var(--mv-surface); color:var(--mv-primary); cursor:pointer; display:inline-flex; align-items:center; justify-content:center;">${unsafeSVG(lucideIcon(Plus, 18))}</button>`}
        </div>

        ${selWm ? this._renderWmDetail(boringId, selWm, section, cfg) : html`<div style="color:var(--mv-text-muted); font-size:0.85rem; padding:0.5rem 0;">Nog geen watermonster. Klik op + om er een toe te voegen.</div>`}
      </div>`;
  }

  _renderWmCard(boringId, wm, selected, cfg) {
    const total = cfg.sections.reduce((s, sec) => s + sec.fields.length, 0);
    const filled = cfg.sections.reduce((s, sec) => s + this._wmFilled(wm, sec.fields), 0);
    const pct = total ? Math.round(filled / total * 100) : 0;
    const missing = this._wmMissing(wm);
    return html`
      <div @click=${() => this._wmSelect(boringId, wm.id)} style="width:190px; border:1.5px solid ${selected ? 'var(--mv-primary)' : 'var(--mv-border)'}; border-radius:8px; padding:8px 10px; cursor:pointer; background:${selected ? 'var(--mv-primary-tint,#e9f5e6)' : 'var(--mv-surface)'};">
        <div style="display:flex; align-items:center; gap:6px;">
          ${unsafeSVG(lucideIcon(Droplets, 13))}
          <span style="font-weight:700; font-size:0.85rem; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${wm.name || '— zonder code —'}</span>
          ${missing ? html`<span title="Onvolledig" style="color:#D97706; display:inline-flex;">${unsafeSVG(lucideIcon(AlertTriangle, 13))}</span>` : ''}
        </div>
        <div style="font-size:0.7rem; color:var(--mv-text-muted); margin:3px 0 6px;">${[wm.date, wm.matrix, `${(wm.bottles || []).length} flessen`].filter(Boolean).join(' · ')}</div>
        <div style="height:4px; border-radius:2px; background:var(--mv-border); overflow:hidden;"><div style="height:100%; width:${pct}%; background:var(--mv-primary);"></div></div>
      </div>`;
  }

  _renderWmDetail(boringId, wm, section, cfg) {
    const sec = cfg.sections.find(s => s.key === section) || cfg.sections[0];
    return html`
      <div style="border:1px solid var(--mv-border); border-radius:8px; overflow:hidden;">
        <div style="display:flex; gap:4px; flex-wrap:wrap; padding:8px; background:var(--mv-surface-alt); border-bottom:1px solid var(--mv-border);">
          ${cfg.sections.map(s => {
            const active = s.key === section;
            return html`<button @click=${() => this._wmSetSection(boringId, s.key)} style="padding:5px 10px; border-radius:6px; border:1px solid ${active ? 'var(--mv-primary)' : 'transparent'}; background:${active ? 'var(--mv-surface)' : 'transparent'}; color:${active ? 'var(--mv-primary)' : 'var(--mv-text-sub)'}; font-size:0.78rem; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:5px;">${s.label}<span style="font-size:0.66rem; color:var(--mv-text-muted);">${this._wmFilled(wm, s.fields)}/${s.fields.length}</span></button>`;
          })}
        </div>
        ${sec.key === 'drijflaag'
          ? this._renderWmDrijflaag(boringId, wm, cfg)
          : html`<div class="expand-field-group" style="grid-template-columns:1fr 1fr 1fr; padding:12px; min-height:110px;">
              ${sec.fields.map(f => this._renderWmField(boringId, wm, f, cfg.field[f]))}
            </div>`}
        ${this._renderWmBottles(boringId, wm)}
        ${this.readonly ? '' : html`<div style="padding:8px 12px; border-top:1px solid var(--mv-border); display:flex; justify-content:flex-end;"><button class="expand-danger-btn" @click=${() => this._wmDeleteMonster(boringId, wm.id)}>${unsafeSVG(lucideIcon(Trash2, 13))} Watermonster verwijderen</button></div>`}
      </div>`;
  }

  /** Drijflaag/zaklaag onder elkaar; het dikte-veld verschijnt pas als de boolean op 'Ja' staat. */
  _renderWmDrijflaag(boringId, wm, cfg) {
    const ynRow = (ynKey, thickKey) => {
      const v = wm[ynKey];
      const meta = cfg.field[ynKey];
      const toggle = (bool) => {
        const next = (v === bool) ? null : bool;
        this._wmEditField(boringId, wm.id, ynKey, next);
        if (next !== true && (wm[thickKey] ?? '') !== '') this._wmEditField(boringId, wm.id, thickKey, ''); // dikte leegmaken zodra niet 'Ja'
      };
      const btn = (bool, text, color) => html`<button type="button" @click=${() => toggle(bool)} style="flex:1; padding:5px 8px; border-radius:5px; border:1px solid ${v === bool ? color : 'var(--mv-border)'}; background:${v === bool ? color : 'var(--mv-surface)'}; color:${v === bool ? '#fff' : 'var(--mv-text)'}; font-weight:700; font-size:0.82rem; font-family:inherit; box-sizing:border-box; cursor:pointer;">${text}</button>`;
      return html`
        <div class="expand-field-group" style="grid-template-columns:1fr 1fr; margin:0; align-items:end;">
          <div class="expand-field"><label>${meta.label}</label><div style="display:flex; gap:6px;">${btn(true, 'Ja', 'var(--mv-primary)')}${btn(false, 'Nee', '#DC2626')}</div></div>
          ${v === true ? this._renderWmField(boringId, wm, thickKey, cfg.field[thickKey]) : html`<div></div>`}
        </div>`;
    };
    return html`<div style="padding:12px; display:flex; flex-direction:column; gap:12px; min-height:110px;">
      ${ynRow('floatingLayer', 'floatingLayerThicknessMm')}
      ${ynRow('sinkingLayer', 'sinkingLayerThicknessMm')}
    </div>`;
  }

  _renderWmField(boringId, wm, key, meta) {
    if (!meta) return '';
    const v = wm[key];
    const set = (val) => this._wmEditField(boringId, wm.id, key, val);
    const label = html`<label style="display:flex; align-items:center; gap:5px;">${meta.label}${meta.req ? html`<span style="color:var(--mv-danger,#f1416c);">*</span>` : ''}</label>`;
    const full = meta.full ? 'grid-column:1/-1' : '';
    if (meta.t === 'yn') {
      const btn = (bool, text, color) => { const active = v === bool; return html`<button type="button" @click=${() => set(active ? null : bool)} style="flex:1; padding:5px 8px; border-radius:5px; border:1px solid ${active ? color : 'var(--mv-border)'}; background:${active ? color : 'var(--mv-surface)'}; color:${active ? '#fff' : 'var(--mv-text)'}; font-weight:700; font-size:0.82rem; font-family:inherit; box-sizing:border-box; cursor:pointer;">${text}</button>`; };
      return html`<div class="expand-field" style="${full}">${label}<div style="display:flex; gap:6px;">${btn(true, 'Ja', 'var(--mv-primary)')}${btn(false, 'Nee', '#DC2626')}</div></div>`;
    }
    // meta.alias: een opgeslagen oude naam (bijv. 'peristaltische pomp') staat geselecteerd als de huidige keuze.
    if (meta.t === 'sel') { const cur = meta.alias?.get(v) ?? v; return html`<div class="expand-field" style="${full}">${label}<select @change=${e => set(e.target.value)}><option value="">— kies —</option>${meta.o.map(o => html`<option value=${o} ?selected=${o === cur}>${this._cap(o)}</option>`)}</select></div>`; }
    if (meta.t === 'ref') return html`<div class="expand-field" style="${full}">${label}<select @change=${e => set(e.target.value)}><option value="">— kies —</option>${[...PEILBUIS_REFERENTIEVLAK].map(([l, c]) => html`<option value=${c} ?selected=${c === v}>${l}</option>`)}</select></div>`;
    if (meta.t === 'tint') return html`<div class="expand-field" style="${full}">${label}<select @change=${e => set(e.target.value)}><option value="">—</option>${[...nen6693.KLEUR_TINT.keys()].map(o => html`<option value=${o} ?selected=${o === v}>${this._cap(o)}</option>`)}</select></div>`;
    if (meta.t === 'kleur') return html`<div class="expand-field" style="${full}">${label}<select @change=${e => set(e.target.value)}><option value="">—</option>${[...nen6693.KLEUR.keys()].sort((a, b) => a.localeCompare(b, 'nl')).map(o => html`<option value=${o} ?selected=${o === v}>${this._cap(o)}</option>`)}</select></div>`;
    const inputType = meta.t === 'num' ? 'number' : meta.t === 'date' ? 'date' : meta.t === 'time' ? 'time' : 'text';
    const bad = meta.req && !String(v ?? '').trim();
    return html`<div class="expand-field" style="${full}">${label}<input type="${inputType}" .value=${v ?? ''} style=${bad ? 'border-color:var(--mv-danger,#f1416c);' : ''} @input=${e => set(e.target.value)}></div>`;
  }

  // Gefiltreerd = boolean; filterType wordt geleegd zodra niet gefilterd.
  _wmSetFiltered(boringId, wmId, idx, val) {
    const cur = this._wmValue(boringId, wmId) || {};
    const bottles = (cur.bottles || []).map((b, i) => i === idx ? { ...b, filtered: val, filterType: val === true ? b.filterType : '' } : b);
    this._wmEditField(boringId, wmId, 'bottles', bottles);
  }
  _renderWmBottles(boringId, wm) {
    const bottles = wm.bottles || [];
    const cols = 'minmax(0,1.3fr) 1fr 120px minmax(0,1.2fr) 26px';
    const bset = (i, f, val) => this._wmEditBottle(boringId, wm.id, i, f, val);
    return html`
      <div style="border-top:1px solid var(--mv-border);">
        <div style="background:var(--mv-surface-alt,#F3F4F6); padding:8px 12px; border-bottom:1px solid var(--mv-border); font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--mv-text-muted);">Flessen ${bottles.length}</div>
        <div style="padding:12px;">
        ${bottles.length ? html`
          <div style="display:grid; grid-template-columns:${cols}; gap:6px 8px; align-items:center;">
            <span class="afw-label">Barcode</span><span class="afw-label">Flestype</span><span class="afw-label">Gefiltreerd</span><span class="afw-label">Filtertype</span><span></span>
            ${bottles.map((b, i) => {
              const ynBtn = (val, text, color) => { const active = b.filtered === val; return html`<button type="button" @click=${() => this._wmSetFiltered(boringId, wm.id, i, active ? null : val)} style="flex:1; padding:4px 6px; border-radius:5px; border:1px solid ${active ? color : 'var(--mv-border)'}; background:${active ? color : 'var(--mv-surface)'}; color:${active ? '#fff' : 'var(--mv-text)'}; font-weight:700; font-size:0.76rem; font-family:inherit; box-sizing:border-box; cursor:pointer;">${text}</button>`; };
              return html`
                <input type="text" placeholder="barcode" .value=${b.barcode ?? ''} style=${!(b.barcode || '').trim() ? 'border-color:var(--mv-danger,#f1416c);' : ''} @input=${e => bset(i, 'barcode', e.target.value)}>
                <select @change=${e => bset(i, 'bottleType', e.target.value)}><option value="">— kies —</option>${WATERBOTTLE_TYPE.map(o => html`<option value=${o} ?selected=${o === b.bottleType}>${this._cap(o)}</option>`)}</select>
                <div style="display:flex; gap:4px;">${ynBtn(true, 'Ja', 'var(--mv-primary)')}${ynBtn(false, 'Nee', '#DC2626')}</div>
                ${b.filtered === true
                  ? html`<select @change=${e => bset(i, 'filterType', e.target.value)}><option value="">— kies —</option>${WATERBOTTLE_FILTERTYPE.map(o => html`<option value=${o} ?selected=${o === b.filterType}>${o}</option>`)}</select>`
                  : html`<span style="font-size:0.78rem; color:var(--mv-text-muted);">n.v.t.</span>`}
                <button title="Verwijderen" @click=${() => this._wmDelBottle(boringId, wm.id, i)} style="border:none;background:none;cursor:pointer;color:var(--mv-danger,#f1416c);font-size:1rem;line-height:1;padding:0;">×</button>
              `;
            })}
          </div>` : html`<div style="font-size:0.82rem; color:var(--mv-text-muted);">Nog geen flessen.</div>`}
          ${this.readonly ? '' : html`<button class="bzb-add-btn" style="width:100%; margin-top:8px;" @click=${() => this._wmAddBottle(boringId, wm.id)}>${unsafeSVG(lucideIcon(Plus, 12))} Fles toevoegen</button>`}
        </div>
      </div>`;
  }


  // ── Peilbuis (redesign: kaartlayout Filterstelling/Buis/Bij plaatsing + autosave) ──
  _pbSeed(pb) {
    return {
      name: pb.name || '',
      material: pb.material ?? pb.material_type ?? '',
      innerDiameterMm: pb.innerDiameterMm ?? pb.inner_diameter_mm ?? '',
      diameterMm: pb.diameter ?? pb.diameter_mm ?? '',
      filterFromCm: pb.filterTopCm ?? pb.filter_from_cm ?? '',
      filterToCm: pb.filterBottomCm ?? pb.filter_to_cm ?? '',
      referencePointType: pb.referencePointType ?? pb.reference_point_type ?? '',
      filterSieveUsed: pb.filterSieveUsed ?? null,
      unwatered: pb.unwatered ?? null,
      rinsed: pb.rinsed ?? null,
      flowType: pb.flowType ?? pb.flow_type ?? '',
      ph: pb.ph ?? '', ec: pb.ec ?? '', ecWorkWater: pb.ecWorkWater ?? pb.ec_work_water ?? '',
      temperature: pb.temperature ?? '', waterUsageL: pb.waterUsageL ?? pb.water_usage_l ?? '', pumpedL: pb.pumpedL ?? pb.pumped_l ?? '',
      remark: pb.remark ?? '',
    };
  }
  _pbValue(pb) { return this._pbFormVals?.[pb.id] || this._pbSeed(pb); }
  _pbToPayload(v) {
    const num = (x) => (x !== '' && x != null) ? Number(x) : null;
    const yn = (x) => x === true ? 1 : x === false ? 0 : null;
    return {
      name: (v.name || '').trim() || null,
      materialType: v.material || null,
      innerDiameterMm: num(v.innerDiameterMm), diameterMm: num(v.diameterMm),
      filterFromCm: num(v.filterFromCm), filterToCm: num(v.filterToCm),
      referencePointType: v.referencePointType || null,
      filterSieveUsed: yn(v.filterSieveUsed), unwatered: yn(v.unwatered), rinsed: yn(v.rinsed),
      flowType: v.flowType || null,
      ph: num(v.ph), ec: num(v.ec), ecWorkWater: num(v.ecWorkWater), temperature: num(v.temperature),
      waterUsageL: num(v.waterUsageL), pumpedL: num(v.pumpedL), remark: v.remark || null,
    };
  }
  _pbEdit(boringId, pb, field, value) {
    const cur = this._pbValue(pb);
    this._pbFormVals = { ...this._pbFormVals, [pb.id]: { ...cur, [field]: value } };
    this._pbSaveSchedule(boringId, pb.id);
  }
  _pbSaveSchedule(boringId, pbId) {
    this._pbSaveState = { ...this._pbSaveState, [boringId]: 'saving' };
    clearTimeout(this._pbSaveTimers[boringId]);
    this._pbSaveTimers[boringId] = setTimeout(() => this._pbSaveNow(boringId, pbId), 700);
  }
  _pbSaveNow(boringId, pbId) {
    const draft = this._pbFormVals?.[pbId];
    if (!draft) { this._pbSaveState = { ...this._pbSaveState, [boringId]: 'saved' }; return; }
    const payload = this._pbToPayload(draft);
    // Werk de lokale boring-peilbuis bij (zodat de kop/badge kloppen) én persisteer.
    this.borings = (this.borings || []).map(b => b.id !== boringId ? b : { ...b, peilbuizen: (b.peilbuizen || []).map(p => p.id === pbId ? { ...p, name: payload.name, filterTopCm: payload.filterFromCm, filterBottomCm: payload.filterToCm, material: payload.materialType, diameter: payload.diameterMm, innerDiameterMm: payload.innerDiameterMm, referencePointType: payload.referencePointType, filterSieveUsed: draft.filterSieveUsed, unwatered: draft.unwatered, rinsed: draft.rinsed, flowType: payload.flowType, ph: payload.ph, ec: payload.ec, ecWorkWater: payload.ecWorkWater, temperature: payload.temperature, waterUsageL: payload.waterUsageL, pumpedL: payload.pumpedL, remark: payload.remark } : p) });
    this._pbSaveState = { ...this._pbSaveState, [boringId]: 'saving' };
    Promise.resolve(this._crud.updatePeilbuis(pbId, payload))
      .then(() => { this._pbSaveState = { ...this._pbSaveState, [boringId]: 'saved' }; })
      .catch(() => { this._pbSaveState = { ...this._pbSaveState, [boringId]: 'error' }; });
  }
  async _pbCreate(boringId, peilbuizen) {
    if (this.readonly) return;
    const raw = (this.borings || []).find(b => b.id === boringId);
    const n = (raw?.peilbuizen || []).length;
    const tpl = this.templateDefaults?.peilbuis || {};
    const num = (x) => (x !== '' && x != null) ? Number(x) : null;
    const payload = {
      name: raw?.name ? `${raw.name}-${n + 1}` : `PB${n + 1}`,
      materialType: tpl.material || null, referencePointType: tpl.referencePointType || null,
      diameterMm: num(tpl.diameterMm), innerDiameterMm: num(tpl.innerDiameterMm),
      filterFromCm: num(tpl.filterFromCm), filterToCm: num(tpl.filterToCm),
      filterSieveUsed: tpl.filterSieveUsed === 'ja' ? 1 : tpl.filterSieveUsed === 'nee' ? 0 : null,
      unwatered: tpl.unwatered === 'ja' ? 1 : tpl.unwatered === 'nee' ? 0 : null,
    };
    this._pbSaveState = { ...this._pbSaveState, [boringId]: 'saving' };
    try {
      const created = await this._crud.createBoringPeilbuis(boringId, payload);
      this.borings = (this.borings || []).map(b => b.id === boringId ? { ...b, peilbuizen: [...(b.peilbuizen || []), created] } : b);
      this._pbSaveState = { ...this._pbSaveState, [boringId]: 'saved' };
    } catch (e) {
      this._pbSaveState = { ...this._pbSaveState, [boringId]: 'error' };
      alertDialog({ title: 'Fout', message: `Peilbuis aanmaken mislukt: ${e.message}` });
    }
  }
  async _pbDelete(boringId, pbId) {
    const ok = await confirmDialog({ title: 'Peilbuis verwijderen', message: 'Weet je zeker dat je deze peilbuis wilt verwijderen?', confirmLabel: 'Verwijderen', danger: true });
    if (!ok) return;
    const fv = { ...this._pbFormVals }; delete fv[pbId]; this._pbFormVals = fv;
    this.borings = (this.borings || []).map(b => b.id !== boringId ? b : { ...b, peilbuizen: (b.peilbuizen || []).filter(p => p.id !== pbId) });
    Promise.resolve(this._crud.deletePeilbuis(pbId)).catch(() => {});
  }

  _renderPeilbuisTab(boringId, peilbuizen) {
    const raw = (this.borings || []).find(b => b.id === boringId);
    const pb = (raw?.peilbuizen || [])[0] || null;

    // Lege tab → zelfde stijl als 'Grondmonsters'/'Lagen': melding + dashed toevoeg-knop.
    if (!pb) {
      if (this.readonly) return html`<div style="padding:0.75rem 0.9rem; color:var(--mv-text-muted); font-size:0.85rem;">Geen peilbuis gevonden.</div>`;
      return html`
        <div style="padding:0.75rem 0.9rem; color:var(--mv-text-muted); font-size:0.85rem;">Geen peilbuis gevonden.</div>
        <div style="padding:0.5rem 0.9rem; border-top:1px solid var(--mv-border);">
          <button class="bzb-add-btn" style="width:100%;" @click=${() => this._pbCreate(boringId, peilbuizen)}>
            ${unsafeSVG(lucideIcon(Plus, 12))} Peilbuis toevoegen
          </button>
        </div>`;
    }

    const ro = !!this.readonly;
    const v = this._pbValue(pb);
    const set = (f, val) => this._pbEdit(boringId, pb, f, val);
    const card = (inner) => html`<div style="border:1px solid var(--mv-border); border-radius:10px; padding:14px 16px; background:var(--mv-surface-alt, #F9FAFB); margin-bottom:12px;">${inner}</div>`;
    const cardHead = (title, sub) => html`<div style="display:flex; align-items:baseline; gap:8px; margin-bottom:10px;"><span style="font-weight:700; font-size:0.88rem;">${title}</span>${sub ? html`<span style="font-size:0.72rem; color:var(--mv-text-muted);">${sub}</span>` : ''}</div>`;
    const num = (label, key, extra) => html`<div class="expand-field">${label}<input type="number" ?disabled=${ro} .value=${v[key] ?? ''} @input=${e => set(key, e.target.value)}>${extra || ''}</div>`;
    const lbl = (t) => html`<label>${t}</label>`;
    const sel = (label, key, opts) => html`<div class="expand-field">${lbl(label)}<select ?disabled=${ro} @change=${e => set(key, e.target.value)}><option value="">— kies —</option>${opts.map(([val, txt]) => html`<option value=${val} ?selected=${val === v[key]}>${txt}</option>`)}</select></div>`;
    const yn = (label, key) => {
      const btn = (bool, text, color) => { const active = v[key] === bool; return html`<button type="button" ?disabled=${ro} @click=${() => set(key, active ? null : bool)} style="flex:1; padding:5px 8px; border-radius:5px; border:1px solid ${active ? color : 'var(--mv-border)'}; background:${active ? color : 'var(--mv-surface)'}; color:${active ? '#fff' : 'var(--mv-text)'}; font-weight:700; font-size:0.8rem; font-family:inherit; box-sizing:border-box; cursor:pointer;">${text}</button>`; };
      return html`<div class="expand-field">${lbl(label)}<div style="display:flex; gap:6px;">${btn(true, 'Ja', 'var(--mv-primary)')}${btn(false, 'Nee', '#DC2626')}</div></div>`;
    };
    const seg = (label, key, opts) => {
      const btn = ([val, text]) => { const active = v[key] === val; return html`<button type="button" ?disabled=${ro} @click=${() => set(key, active ? '' : val)} style="flex:1; padding:5px 6px; border-radius:5px; border:1px solid ${active ? 'var(--mv-primary)' : 'var(--mv-border)'}; background:${active ? 'var(--mv-primary)' : 'var(--mv-surface)'}; color:${active ? '#fff' : 'var(--mv-text)'}; font-weight:700; font-size:0.78rem; font-family:inherit; box-sizing:border-box; cursor:pointer;">${text}</button>`; };
      return html`<div class="expand-field">${lbl(label)}<div style="display:flex; gap:4px;">${opts.map(btn)}</div></div>`;
    };

    const MAT = [...PEILBUIS_MATERIAAL].map(([l, c]) => [c, l]);
    const REF = [...PEILBUIS_REFERENTIEVLAK].map(([l, c]) => [c, l]);
    const DOORLOOP = ['Snel', 'Matig', 'Traag', 'Geen'].map(l => [PEILBUIS_DOORLOOP.get(l), l]);

    // Filterstelling-subtitel + filtergrind-koppeling (leest de afwerking-vulling).
    const ff = v.filterFromCm !== '' && v.filterFromCm != null ? Number(v.filterFromCm) : null;
    const ft = v.filterToCm !== '' && v.filterToCm != null ? Number(v.filterToCm) : null;
    const filterSub = (ff != null && ft != null) ? `${ff} tot ${ft} cm onder maaiveld` : '';
    const fg = (raw?.afwerking?.fillings || []).filter(f => f.materiaal === 'VUL_FG' && f.van != null && f.van !== '' && f.tot != null && f.tot !== '');
    let fgHint = null;
    if (fg.length && ff != null && ft != null) {
      const fgTop = Math.min(...fg.map(f => Number(f.van)));
      const fgBot = Math.max(...fg.map(f => Number(f.tot)));
      const aligns = fgTop <= ff && fgBot >= ft;
      fgHint = { text: `Filtergrind loopt van ${fgTop} tot ${fgBot} cm · ${aligns ? 'sluit aan' : 'sluit niet aan op het filter'}`, ok: aligns };
    }
    // pH-validatie 0–14.
    const phNum = v.ph !== '' && v.ph != null ? Number(v.ph) : null;
    const phBad = phNum != null && (phNum < 0 || phNum > 14);

    return html`
      <div class="afw-tab" @click=${e => e.stopPropagation()} style="padding:0.7rem 0.9rem;">
        <div style="display:flex; align-items:center; gap:8px; margin-bottom:12px;">
          ${unsafeSVG(lucideIcon(TestTube, 15))}
          <input type="text" ?disabled=${ro} .value=${v.name ?? ''} placeholder="Naam" @input=${e => set('name', e.target.value)} style="font-weight:700; font-size:0.95rem; border:none; background:transparent; color:var(--mv-text); padding:2px 4px; max-width:200px; outline:none;">
          <span style="font-size:0.78rem; color:var(--mv-text-muted);">in boring ${raw?.name || ''}</span>
          ${ro ? '' : html`<button title="Peilbuis verwijderen" @click=${() => this._pbDelete(boringId, pb.id)} style="margin-left:auto; border:none; background:none; cursor:pointer; color:var(--mv-danger,#f1416c); padding:2px;">${unsafeSVG(lucideIcon(Trash2, 15))}</button>`}
        </div>

        ${card(html`
          ${cardHead('Filterstelling', filterSub)}
          <div class="expand-field-group" style="grid-template-columns:1fr 1fr 1fr;">
            ${num(lbl('Bovenkant filter (cm)'), 'filterFromCm')}
            ${num(lbl('Onderkant filter (cm)'), 'filterToCm')}
            ${sel('Referentievlak', 'referencePointType', REF)}
          </div>
          ${fgHint ? html`<div style="display:flex; align-items:center; gap:6px; margin-top:10px; font-size:0.76rem; color:${fgHint.ok ? 'var(--mv-primary)' : '#B45309'};">${unsafeSVG(lucideIcon(Info, 13))} ${fgHint.text}</div>` : ''}
        `)}

        ${card(html`
          ${cardHead('Buis')}
          <div class="expand-field-group" style="grid-template-columns:1fr 1fr 1fr 1fr;">
            ${sel('Materiaal', 'material', MAT)}
            ${num(lbl('Binnendiameter (mm)'), 'innerDiameterMm')}
            ${num(lbl('Buitendiameter (mm)'), 'diameterMm')}
            ${yn('Filterkous', 'filterSieveUsed')}
          </div>
        `)}

        ${card(html`
          ${cardHead('Bij plaatsing')}
          <div class="expand-field-group" style="grid-template-columns:1fr 1fr 1fr;">
            ${yn('Leeggezogen', 'unwatered')}
            ${yn('Gespoeld', 'rinsed')}
            ${seg('Doorloop', 'flowType', DOORLOOP)}
            ${num(lbl('pH'), 'ph', phBad ? html`<span style="font-size:0.72rem; color:var(--mv-danger,#f1416c);">pH loopt van 0 tot 14</span>` : '')}
            ${num(lbl('EC bij plaatsing (µS/cm)'), 'ec')}
            ${num(lbl('EC werkwater (µS/cm)'), 'ecWorkWater')}
            ${num(lbl('Temperatuur (°C)'), 'temperature')}
            ${num(lbl('Werkwater (l)'), 'waterUsageL')}
            ${num(lbl('Afgepompt (l)'), 'pumpedL')}
            <div class="expand-field" style="grid-column:1/-1">${lbl('Opmerking')}<input type="text" ?disabled=${ro} .value=${v.remark ?? ''} @input=${e => set('remark', e.target.value)}></div>
          </div>
        `)}
      </div>`;
  }

}

customElements.define('recording-row-detail', RecordingRowDetail);
